import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  ArrowLeft, Edit, MoreHorizontal, Phone, MapPin, Mail, Calendar, Clock,
  Users, FileText, FileSignature, CreditCard, ClipboardList, Sparkles,
  MessageCircle, Download, Plus, X, ChefHat, UserCog, Truck, Star, Package,
  CheckCircle2, Circle, Bell, Save, Trash2, Brain, Loader2, Image as ImageIcon,
  PartyPopper, Building2, LayoutGrid, Eye
} from "lucide-react";
import SelectorModal from "../../components/SelectorModal";
import FlujoOperativoPanel from "../../components/FlujoOperativoPanel";
import { useBusinessUnits } from "../../hooks/useBusinessUnits";
import { isModuloVisible } from "../../hooks/useModulosVisibles";
import { useAuth } from "../../App";

// ====== ESTATUS META ======
const ESTATUS_META = {
  nuevo: { label: "Nuevo", cls: "eo-pill-info" },
  cotizado: { label: "Cotizado", cls: "eo-pill-info" },
  contratado: { label: "Contratado", cls: "eo-pill-accent" },
  confirmado: { label: "Confirmado", cls: "eo-pill-success" },
  en_evento: { label: "En evento", cls: "eo-pill-primary" },
  finalizado: { label: "Finalizado", cls: "eo-pill-muted" },
  cancelado: { label: "Cancelado", cls: "eo-pill-danger" },
};

// Iter98 · Expediente consolidado a 8 secciones (sin crear módulos nuevos).
// - "servicios_menu" combina lo que antes eran tabs "servicios" y "menu".
// - "personal_prov" combina "personal" y "proveedores".
// - "itinerario_od" combina "itinerario" y "orden_dia".
// - "modo_evento" navega directo al Modo Evento (no es un tab con contenido).
// Iter104 · 5 tabs + 1 nav externo (Día del evento) — flujo real del negocio:
// Contratación → Planeación → Operación → Finanzas → Documentos → Día del evento.
// Los sub-tabs internos (Servicios/Menú, Montaje, Personal/Proveedores, Itinerario)
// se conservan como `key` internos y se agrupan bajo estos 5 padres.
// Iter140 · Expediente reorganizado en 9 pestañas para una vista central única.
// Mapeo a keys existentes (no se crean módulos nuevos):
//   Resumen      → resumen
//   Comercial    → servicios_menu (agrupa servicios/menú + montaje)
//   Pagos       → pagos (nuevo tab liviano, reutiliza pagos ya cargados)
//   Operación    → orden_maestra (agrupa OM + Órdenes de Trabajo + Itinerario)
//   Producción   → produccion
//   Personal     → personal (separado de personal_prov)
//   Proveedores  → proveedores (separado de personal_prov)
//   Documentos   → documentos
//   Finanzas     → finanzas
const TABS = [
  { key: "resumen", label: "Resumen" },
  { key: "servicios_menu", label: "Comercial", group: ["servicios_menu", "montaje"] },
  { key: "pagos", label: "Pagos", secundaria: true },
  { key: "orden_maestra", label: "Operación", group: ["orden_maestra", "ordenes_trabajo", "itinerario_od"], secundaria: true },
  { key: "produccion", label: "Producción" },
  { key: "cocina_chef", label: "Cocina / Chef", secundaria: true },
  { key: "personal", label: "Personal", secundaria: true },
  { key: "proveedores", label: "Proveedores", secundaria: true },
  { key: "documentos", label: "Documentos", secundaria: true },
  { key: "finanzas", label: "Finanzas", secundaria: true },
  { key: "modo_evento", label: "Día del evento", esNav: true, secundaria: true },
];

// Sub-tabs internos para los grupos consolidados
const SUBTABS_PLANEACION = [
  { key: "servicios_menu", label: "Servicios y menú" },
  { key: "montaje", label: "Montaje y decoración" },
];
const SUBTABS_OPERACION = [
  { key: "orden_maestra", label: "Orden Maestra" },
  { key: "ordenes_trabajo", label: "Órdenes de Trabajo" },
  { key: "itinerario_od", label: "Itinerario y orden del día" },
];

// Iter98 · Acciones rápidas simplificadas — todo lo que era generación de
// documentos vive ahora en la sección "Documentos". Se conservan las 3
// acciones no-documentales: Registrar Pago, Modo Evento y WhatsApp.
const ACCIONES_RAPIDAS = [
  { key: "pago", label: "Registrar Pago", icon: CreditCard, ruta: "/panel/pagos" },
  { key: "modo_evento", label: "Modo Evento", icon: Sparkles, ruta: "/panel/modo-evento" },
  { key: "whatsapp", label: "Enviar WhatsApp", icon: MessageCircle, whatsapp: true },
];

const CHECKLIST_DEFAULT = [
  { id: "1", label: "Confirmar montaje", hecho: false },
  { id: "2", label: "Revisar menú", hecho: false },
  { id: "3", label: "Confirmar personal", hecho: false },
  { id: "4", label: "Confirmar proveedores", hecho: false },
  { id: "5", label: "Lista de compras", hecho: false },
  { id: "6", label: "Pago final", hecho: false },
  { id: "7", label: "Llamada previa al cliente", hecho: false },
  { id: "8", label: "Logística confirmada", hecho: false },
  { id: "9", label: "Decoración aprobada", hecho: false },
  { id: "10", label: "Equipo técnico revisado", hecho: false },
  { id: "11", label: "Itinerario impreso", hecho: false },
  { id: "12", label: "Cliente notificado", hecho: false },
];

const ExpedienteEvento = () => {
  const { id: idParam, eventoId } = useParams();
  const id = idParam || eventoId;
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [evento, setEvento] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [pagos, setPagos] = useState([]);
  // Iter125 · P1 Fase 1 · Expediente único: datos heredados del backend
  const [contrato, setContrato] = useState(null);
  const [cotizacion, setCotizacion] = useState(null);
  const [itemsContratados, setItemsContratados] = useState([]);
  const [resumenFinanciero, setResumenFinanciero] = useState(null);
  const [personalCatalogo, setPersonalCatalogo] = useState([]);
  const [proveedoresCatalogo, setProveedoresCatalogo] = useState([]);
  // Iter125 · P1 Fase 5 · Equipo del evento: tareas OM por responsable + edición asignación
  const [ordenMaestra, setOrdenMaestra] = useState([]);
  const [equipoEditPersonal, setEquipoEditPersonal] = useState(null);   // {idx, ...draft}
  const [equipoEditProveedor, setEquipoEditProveedor] = useState(null); // {idx, ...draft}
  const [equipoTareasPara, setEquipoTareasPara] = useState(null);       // {nombre, items}
  // Iter125 · P1 Fase 8 · Finanzas: costos reales del expediente
  const [comprasEvento, setComprasEvento] = useState([]);
  const [egresosEvento, setEgresosEvento] = useState([]);
  const [produccionEvento, setProduccionEvento] = useState([]);
  const [finalizando, setFinalizando] = useState(false);
  const [serviciosCatalogo, setServiciosCatalogo] = useState([]);
  const [menusCatalogo, setMenusCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [tab, setTab] = useState(params.get("tab") || "resumen");
  const [unidadVista, setUnidadVista] = useState(params.get("vista") || "general"); // "general" | slug
  const [editando, setEditando] = useState(false);
  const [materializando, setMaterializando] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  // Iter124w · P1 · Estado local para la vista de Operación del evento.
  const [operacion, setOperacion] = useState(null);
  const [operacionLoading, setOperacionLoading] = useState(false);
  const fetchOperacion = useCallback(async () => {
    if (!id) return;
    setOperacionLoading(true);
    try {
      const { data } = await axios.get(`${API}/eventos/${id}/operacion`);
      setOperacion(data);
    } catch (e) {
      // silencioso — puede no aplicar si el usuario no tiene permisos operativos
    } finally {
      setOperacionLoading(false);
    }
  }, [id]);
  useEffect(() => {
    if (tab === "personal_prov" && !operacion) fetchOperacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id]);

  // Iter140 · Fix P0 · El Cronograma (ItinerarioEditor) lee `ordenMaestra` del
  // estado del padre, cargado solo en fetchData inicial. Cuando el usuario crea
  // o edita items desde OrdenMaestraTab, el padre quedaba con una copia STALE
  // → el Cronograma no veía la actividad con hora. Refrescamos al entrar a
  // itinerario_od (sub-tab Cronograma / Itinerario).
  const refreshOrdenMaestra = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/eventos/${id}/orden-maestra`);
      setOrdenMaestra(data?.items || []);
    } catch { /* silencioso */ }
  }, [id]);
  useEffect(() => {
    if (tab === "itinerario_od" || tab === "orden_maestra") refreshOrdenMaestra();
  }, [tab, refreshOrdenMaestra]);
  const [draft, setDraft] = useState({});
  // Iter117 · BUG-01 · Refs para persistencia móvil del menú (declarados temprano
  // para que handleGuardarEdit los referencie sin TDZ)
  const menuSeccionesRef = useRef([]);
  const menuSaveTimerRef = useRef(null);
  const dirtyMenuRef = useRef(false);
  // Iter132e · Fix pérdida de itinerario al pulsar "Guardar cambios" del expediente.
  // Igual que menú: mantenemos refs actualizados por el ItinerarioEditor para que
  // handleGuardarEdit pueda incluir los cambios pendientes en el PUT atómico y no
  // haya carrera contra el onBlur async del textarea.
  const itinerarioRef = useRef([]);
  const dirtyItinerarioRef = useRef(false);

  // Unidades de negocio dinámicas
  const { unidades: unidadesDisponibles, puedeVerVistaGeneral, esAdmin, unidadesAsignadas } = useBusinessUnits();
  const { user } = useAuth();

  // Modales
  const [showPersonal, setShowPersonal] = useState(false);
  const [showProveedores, setShowProveedores] = useState(false);
  const [showServicios, setShowServicios] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  // Vincular cliente al evento
  const [showVincularCliente, setShowVincularCliente] = useState(false);
  const [clientesCatalogo, setClientesCatalogo] = useState([]);

  const abrirVincularCliente = async () => {
    try {
      const { data } = await axios.get(`${API}/clientes`);
      setClientesCatalogo(Array.isArray(data) ? data : data?.clientes || []);
    } catch { /* ignore */ }
    setShowVincularCliente(true);
  };
  const vincularCliente = async (ids) => {
    if (!ids?.length) { toast.error("Selecciona un cliente"); return; }
    try {
      await axios.put(`${API}/eventos/${id}`, { cliente_id: ids[0] });
      toast.success("Cliente vinculado al evento");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo vincular el cliente"); }
  };
  // Iter105e · Modal para agregar pago rápido desde el expediente
  const [showPagoQuick, setShowPagoQuick] = useState(false);
  // Iter145 · Menú "Más" que agrupa pestañas secundarias del expediente.
  const [mostrarMenuMas, setMostrarMenuMas] = useState(false);
  // Iter150 · Posición fija del dropdown para escapar del overflow-x-auto del contenedor de tabs.
  const btnMasRef = useRef(null);
  const [posMenuMas, setPosMenuMas] = useState(null);
  const abrirMenuMas = () => {
    if (!mostrarMenuMas && btnMasRef.current) {
      const r = btnMasRef.current.getBoundingClientRect();
      setPosMenuMas({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    }
    setMostrarMenuMas(v => !v);
  };
  const [pagoQuick, setPagoQuick] = useState({ monto: "", tipo: "anticipo", metodo: "transferencia", fecha: new Date().toISOString().split("T")[0], notas: "" });

  // Iter124f · P0 · Cancelar / eliminar un pago del historial del evento
  const refrescarEvento = useCallback(async () => {
    try {
      const ev = await axios.get(`${API}/eventos/${id}`);
      setEvento(ev.data.evento);
      setPagos(ev.data.pagos || []);
      setContrato(ev.data.contrato || null);
      setCotizacion(ev.data.cotizacion || null);
      setItemsContratados(Array.isArray(ev.data.items_contratados) ? ev.data.items_contratados : []);
      setResumenFinanciero(ev.data.resumen_financiero || null);
    } catch (e) { /* silencioso */ }
  }, [id]);

  const cancelarPago = async (pago) => {
    const motivo = window.prompt(`¿Motivo para cancelar el pago de $${(pago.monto_pagado || 0).toLocaleString("es-MX")}?`, "Duplicado");
    if (!motivo) return;
    try {
      await axios.post(`${API}/pagos/${pago.pago_id}/cancelar`, { motivo });
      toast.success("Pago cancelado");
      await refrescarEvento();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cancelar"); }
  };
  const eliminarPago = async (pago) => {
    if (!window.confirm(`¿ELIMINAR definitivamente el pago de $${(pago.monto_pagado || 0).toLocaleString("es-MX")}? Esta acción NO se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/pagos/${pago.pago_id}`);
      toast.success("Pago eliminado");
      await refrescarEvento();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  // Iter132 · Bug pagos · Ver / Editar pago desde Finanzas del evento
  const [pagoVer, setPagoVer] = useState(null);
  const [pagoEditar, setPagoEditar] = useState(null);
  const [pagoEditarDraft, setPagoEditarDraft] = useState(null);
  const [guardandoPago, setGuardandoPago] = useState(false);

  const abrirEditarPago = (pago) => {
    if (pago?.cancelado) { toast.info("Este pago está cancelado y no se puede editar."); return; }
    setPagoEditar(pago);
    setPagoEditarDraft({
      monto: pago.monto_pagado || pago.monto || 0,
      tipo: pago.tipo || "anticipo",
      metodo: pago.metodo_pago || pago.metodo || "transferencia",
      fecha_pago: pago.fecha_pago || (pago.created_at || "").substring(0, 10) || "",
      notas: pago.notas || "",
    });
  };

  const guardarEdicionPago = async () => {
    if (!pagoEditar || !pagoEditarDraft) return;
    const monto = parseFloat(pagoEditarDraft.monto);
    if (!(monto > 0)) { toast.error("Ingresa un monto válido"); return; }
    setGuardandoPago(true);
    try {
      await axios.put(`${API}/pagos/${pagoEditar.pago_id}`, {
        monto,
        monto_total: monto,
        monto_pagado: monto,
        tipo: pagoEditarDraft.tipo,
        metodo: pagoEditarDraft.metodo,
        metodo_pago: pagoEditarDraft.metodo,
        fecha_pago: pagoEditarDraft.fecha_pago || null,
        notas: pagoEditarDraft.notas || "",
      });
      toast.success("Pago actualizado");
      setPagoEditar(null);
      setPagoEditarDraft(null);
      await refrescarEvento();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el pago");
    } finally { setGuardandoPago(false); }
  };

  const registrarPagoQuick = async () => {
    if (!pagoQuick.monto || parseFloat(pagoQuick.monto) <= 0) { toast.error("Ingresa un monto válido"); return; }
    if (!evento?.cliente_id) { toast.error("Este evento no tiene cliente vinculado"); return; }
    try {
      await axios.post(`${API}/pagos`, {
        evento_id: evento.evento_id,
        cliente_id: evento.cliente_id,
        monto: parseFloat(pagoQuick.monto),
        monto_total: parseFloat(pagoQuick.monto),
        monto_pagado: parseFloat(pagoQuick.monto),
        tipo: pagoQuick.tipo,
        metodo: pagoQuick.metodo,
        fecha: pagoQuick.fecha,
        notas: pagoQuick.notas,
        estatus: "validado",
      });
      toast.success("Pago registrado y vinculado al evento");
      setShowPagoQuick(false);
      setPagoQuick({ monto: "", tipo: "anticipo", metodo: "transferencia", fecha: new Date().toISOString().split("T")[0], notas: "" });
      // refresh
      const ev = await axios.get(`${API}/eventos/${id}`);
      setEvento(ev.data.evento);
      setCliente(ev.data.cliente);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al registrar pago"); }
  };

  // IA
  const [iaResult, setIaResult] = useState(null);
  const [iaLoading, setIaLoading] = useState(null);
  const [showIaModal, setShowIaModal] = useState(false);
  // Estado financiero + confirmar
  const [estadoFin, setEstadoFin] = useState(null);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [confirmarData, setConfirmarData] = useState({ monto: 0, metodo: "transferencia", referencia: "" });
  const [confirmando, setConfirmando] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, perRes, provRes, sRes, mRes, pagRes, omRes, compRes, egrRes, prodRes] = await Promise.all([
        axios.get(`${API}/eventos/${id}`),
        axios.get(`${API}/personal`).catch(() => ({ data: [] })),
        axios.get(`${API}/proveedores`).catch(() => ({ data: [] })),
        axios.get(`${API}/servicios-rapidos`).catch(() => ({ data: [] })),
        axios.get(`${API}/menus`).catch(() => ({ data: [] })),
        axios.get(`${API}/pagos`).catch(() => ({ data: [] })),
        axios.get(`${API}/eventos/${id}/orden-maestra`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/compras?evento_id=${id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/egresos?evento_id=${id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/eventos/${id}/produccion-items`).catch(() => ({ data: { items: [] } })),
      ]);
      const ev = eRes.data.evento || eRes.data;
      setEvento(ev);
      setDraft(ev);
      setPersonalCatalogo(perRes.data || []);
      setProveedoresCatalogo(provRes.data || []);
      setServiciosCatalogo(sRes.data || []);
      setMenusCatalogo(mRes.data || []);
      // Iter125 · P1 Fase 1 · Herencia desde el expediente único.
      // Los pagos del evento vienen ya filtrados por evento_id + tenant desde
      // /api/eventos/{id}. Antes se filtraba /api/pagos por cliente_id lo que
      // traía pagos de OTROS eventos del mismo cliente e inflaba el "Pagado".
      const pagosHeredados = Array.isArray(eRes.data.pagos) ? eRes.data.pagos : null;
      setPagos(
        pagosHeredados ??
          (pagRes.data || []).filter(p => p.evento_id === id)
      );
      setContrato(eRes.data.contrato || null);
      setCotizacion(eRes.data.cotizacion || null);
      setItemsContratados(Array.isArray(eRes.data.items_contratados) ? eRes.data.items_contratados : []);
      setResumenFinanciero(eRes.data.resumen_financiero || null);
      setOrdenMaestra(omRes.data?.items || []);
      setComprasEvento(compRes.data || []);
      setEgresosEvento(egrRes.data || []);
      setProduccionEvento(prodRes.data?.items || []);
      if (ev.cliente_id) {
        try {
          const cRes = await axios.get(`${API}/clientes/${ev.cliente_id}`);
          setCliente(cRes.data.cliente || cRes.data);
        } catch { /* ignore */ }
      }
    } catch (e) {
      if (e?.response?.status === 404) { setNoEncontrado(true); }
      else toast.error("Error al cargar el expediente");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Cargar estado financiero
  useEffect(() => {
    if (!evento) return;
    axios.get(`${API}/eventos/${id}/estado-financiero`).then(({ data }) => setEstadoFin(data)).catch(() => {});
  }, [evento, id, pagos]);

  // Default monto anticipo al abrir modal
  useEffect(() => {
    if (showConfirmar && evento) {
      const anticipoSug = evento.anticipo_sugerido || Math.max(5000, (evento.total || 0) * 0.5);
      setConfirmarData(d => ({ ...d, monto: anticipoSug }));
    }
  }, [showConfirmar, evento]);

  const handleConfirmarEvento = async () => {
    if (confirmarData.monto <= 0) { toast.error("Ingresa un monto válido"); return; }
    setConfirmando(true);
    try {
      const { data } = await axios.post(`${API}/eventos/${id}/confirmar`, {
        monto_anticipo: confirmarData.monto,
        metodo_pago: confirmarData.metodo,
        referencia: confirmarData.referencia,
        generar_produccion: true,
      });
      toast.success(`Evento ${data.evento_estatus} · Anticipo $${data.total_pagado.toLocaleString("es-MX")} registrado${data.produccion ? " · Producción generada" : ""}`);
      setShowConfirmar(false);
      fetchData();
    } catch (e) { toast.error("Error: " + (e?.response?.data?.detail || e.message)); }
    finally { setConfirmando(false); }
  };

  const guardarCampo = useCallback(async (campo, valor) => {
    try {
      await axios.put(`${API}/eventos/${id}`, { [campo]: valor });
      setEvento(e => ({ ...e, [campo]: valor }));
    } catch { toast.error("Error al guardar"); }
  }, [id]);

  const handleGuardarEdit = async () => {
    try {
      // Iter117 · BUG-01 · Forzar commit de menú pendiente antes de recargar (evita
      // que fetchData pise cambios locales del usuario en móvil donde onBlur falla).
      if (menuSaveTimerRef.current) {
        clearTimeout(menuSaveTimerRef.current);
        menuSaveTimerRef.current = null;
      }
      const payload = { ...draft };
      if (dirtyMenuRef.current) {
        payload.menu_asignado = { ...(evento?.menu_asignado || {}), secciones: menuSeccionesRef.current };
        dirtyMenuRef.current = false;
      }
      // Iter132e · Incluye itinerario pendiente en el payload atómico (evita carrera con onBlur).
      // Iter140b · Fix P0 · Excluir SIEMPRE `itinerario` de `draft` antes de decidir:
      // el ItinerarioEditor persiste sus cambios por su cuenta vía guardarCampo("itinerario", …).
      // Cuando el useEffect del editor limpia dirtyItinerarioRef al llegar el nuevo prop,
      // `handleGuardarEdit` NO debe reinyectar el itinerario viejo capturado por setDraft(ev)
      // al entrar en modo edición — eso sobrescribía los cambios recién guardados con el
      // valor anterior ("se muestra como guardada pero luego vuelve el valor anterior").
      delete payload.itinerario;
      // Iter155 · Fix P0 · Misma lógica para `proveedores_asignados`: se persiste por
      // su cuenta desde el modal de "Editar asignación" vía guardarCampo. Si el
      // draft (copia obtenida al entrar en edición) queda con el array viejo,
      // handleGuardarEdit pisaba la edición fresca con la versión previa.
      delete payload.proveedores_asignados;
      // Iter161 · Fix P0 · Misma lógica para `personal_asignado`: se persiste
      // por su cuenta desde el modal de "Agregar personal" vía guardarCampo.
      // El draft capturado al entrar en modo edición tenía la lista vieja
      // (frecuentemente vacía) y `handleGuardarEdit` pisaba las asignaciones
      // recién agregadas → toast "Cambios guardados" seguido de "Sin personal
      // asignado" al recargar.
      delete payload.personal_asignado;
      if (dirtyItinerarioRef.current) {
        payload.itinerario = itinerarioRef.current;
        dirtyItinerarioRef.current = false;
      }
      await axios.put(`${API}/eventos/${id}`, payload);
      toast.success("Cambios guardados");
      setEditando(false);
      fetchData();
    } catch { toast.error("Error al guardar"); }
  };

  // Iter110 · EL EVENTO ES LA FUENTE MAESTRA · genera automáticamente producción,
  // lista de compras, gastos de personal/proveedores e itinerario desde el evento.
  const handleMaterializarOperacion = async () => {
    if (materializando) return;
    if (!window.confirm("¿Materializar la operación de este evento?\n\nSe generarán automáticamente:\n· Producción (basada en el menú)\n· Lista de compras (basada en las recetas)\n· Gastos de personal y proveedores asignados\n· Itinerario base\n\nEs idempotente: puedes ejecutarlo múltiples veces sin duplicar.")) return;
    setMaterializando(true);
    try {
      const { data } = await axios.post(`${API}/eventos/${id}/materializar-operacion`, { forzar: false });
      const partes = [];
      if (data.produccion_creada) partes.push("producción creada");
      else if (data.produccion_id) partes.push("producción existente actualizada");
      if (data.compras_generadas > 0) partes.push(`${data.compras_generadas} compras generadas`);
      if (data.gastos_generados > 0) partes.push(`${data.gastos_generados} gastos generados`);
      if (data.itinerario_creado) partes.push("itinerario creado");
      toast.success(`Operación materializada · ${partes.join(" · ") || "todo estaba al día"}`);
      if (data.advertencias?.length) {
        data.advertencias.slice(0, 3).forEach(a => toast.warning(a));
      }
      fetchData();
      fetchOperacion();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al materializar operación");
    } finally {
      setMaterializando(false);
    }
  };

  // Iter112 · Cerrar / Reabrir evento
  const handleCerrarEvento = async () => {
    if (cerrando) return;
    if (!window.confirm("¿Cerrar este evento?\n\nSe consolidará la utilidad final (ingresos − egresos) y se bloquearán futuras ediciones hasta que lo reabras.")) return;
    setCerrando(true);
    try {
      const { data } = await axios.post(`${API}/eventos/${id}/cerrar`);
      toast.success(`Evento cerrado · utilidad $${(data.utilidad || 0).toLocaleString("es-MX", {maximumFractionDigits: 0})} · margen ${data.margen || 0}%`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cerrar evento");
    } finally {
      setCerrando(false);
    }
  };

  const handleReabrirEvento = async () => {
    if (!window.confirm("¿Reabrir este evento? Podrás editarlo nuevamente.")) return;
    try {
      await axios.post(`${API}/eventos/${id}/reabrir`);
      toast.success("Evento reabierto");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al reabrir");
    }
  };

  // Iter124t · P0 · Cancelar evento (soft) — preserva expediente, pagos, contrato.
  const handleCancelarEvento = async () => {
    if (cancelando) return;
    if (!window.confirm("¿Cancelar este evento?\n\nEl expediente, contrato y pagos se conservan. El evento quedará marcado como Cancelado y se retirará de la agenda operativa.")) return;
    setCancelando(true);
    try {
      const { data } = await axios.delete(`${API}/eventos/${id}`);
      if (data?.cancelado) {
        toast.success("Evento cancelado · el expediente se preservó");
        fetchData();
      } else {
        toast.info("Solicitud procesada");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cancelar");
    } finally {
      setCancelando(false);
    }
  };

  // ===== Helpers
  const totalInv = useMemo(() => (parseInt(evento?.adultos) || 0) + (parseInt(evento?.ninos) || 0), [evento]);
  const totalPagado = useMemo(() => pagos.filter(p => !p.cancelado).reduce((s, p) => s + (parseFloat(p.monto_pagado) || parseFloat(p.monto) || 0), 0), [pagos]);
  const saldo = useMemo(() => Math.max((evento?.total || 0) - totalPagado, 0), [evento, totalPagado]);
  const pctPago = useMemo(() => evento?.total ? Math.round((totalPagado / evento.total) * 100) : 0, [evento, totalPagado]);

  // ===== Unidades del evento + Vistas disponibles al usuario
  const unidadesEvento = useMemo(() => {
    const arr = evento?.unidades;
    return Array.isArray(arr) && arr.length ? arr.map(s => String(s).toLowerCase()) : [];
  }, [evento]);

  // Filtrar unidades: solo mostrar las que están asociadas al evento (si el evento tiene unidades) y las que el usuario puede ver.
  const unidadesVisibles = useMemo(() => {
    const base = unidadesDisponibles.filter(u => u.activa !== false);
    // Filtrar por permisos del usuario (además del RBAC ya aplicado en backend)
    let filtradas = base;
    if (!esAdmin && unidadesAsignadas.length) {
      const asignadasSet = new Set(unidadesAsignadas.map(s => String(s).toLowerCase()));
      filtradas = filtradas.filter(u => asignadasSet.has(u.slug));
    }
    // Además: si el evento tiene unidades explícitas, priorizar solo esas + siempre mostrar admin las demás
    if (unidadesEvento.length && !esAdmin) {
      filtradas = filtradas.filter(u => unidadesEvento.includes(u.slug));
    }
    return filtradas;
  }, [unidadesDisponibles, esAdmin, unidadesAsignadas, unidadesEvento]);

  // La vista activa: si no puede ver "general", cae a la primera unidad disponible
  useEffect(() => {
    if (!unidadesDisponibles.length) return;
    if (unidadVista === "general" && !puedeVerVistaGeneral) {
      const primera = unidadesVisibles[0]?.slug;
      if (primera) setUnidadVista(primera);
    } else if (unidadVista !== "general") {
      const existe = unidadesVisibles.some(u => u.slug === unidadVista);
      if (!existe) {
        setUnidadVista(puedeVerVistaGeneral ? "general" : (unidadesVisibles[0]?.slug || "general"));
      }
    }
  }, [unidadVista, puedeVerVistaGeneral, unidadesVisibles, unidadesDisponibles]);

  // Secciones permitidas por la vista activa
  const seccionesActivas = useMemo(() => {
    if (unidadVista === "general") return TABS.map(t => t.key);
    const u = unidadesDisponibles.find(x => x.slug === unidadVista);
    return u?.secciones_permitidas || TABS.map(t => t.key);
  }, [unidadVista, unidadesDisponibles]);

  const tabsFiltrados = useMemo(
    () => TABS.filter(t => seccionesActivas.includes(t.key)).filter(t => isModuloVisible(t.key, user)),
    [seccionesActivas, user]
  );

  // Si el tab actual no está en los tabs filtrados, forzar el primero disponible
  // Iter104 · considerar también los sub-tabs de grupos consolidados
  // Iter104c · "datos" es un tab-escondido accesible desde el botón Editar de Contratación
  const _keyEnTabs = (k) => k === "datos" || k === "notas" || tabsFiltrados.some(t => t.key === k || (t.group && t.group.includes(k)));
  // Recuerda el último tab visitado por cada vista para restaurar contexto al alternar
  const [_tabsPorVista, setTabsPorVista] = useState({});
  useEffect(() => {
    if (!tabsFiltrados.length) return;
    if (!_keyEnTabs(tab)) {
      const recordado = _tabsPorVista[unidadVista];
      const siguiente = (recordado && _keyEnTabs(recordado))
        ? recordado
        : tabsFiltrados[0].key;
      setTab(siguiente);
    } else {
      // Guardar el tab activo para esta vista
      setTabsPorVista(prev => (prev[unidadVista] === tab ? prev : { ...prev, [unidadVista]: tab }));
    }
  }, [tabsFiltrados, tab, unidadVista]);

  const handleWhatsapp = () => {
    if (!cliente?.whatsapp) { toast.error("Sin WhatsApp del cliente"); return; }
    const tel = cliente.whatsapp.replace(/\D/g, "");
    const t = tel.length === 10 ? `52${tel}` : tel;
    const msg = encodeURIComponent(`Hola ${cliente.nombre || ""} 👋, te escribo del equipo de ${evento?.nombre || "tu evento"}.`);
    window.open(`https://wa.me/${t}?text=${msg}`, "_blank", "noopener,noreferrer");
  };

  // === Personal asignado
  const personalAsignados = evento?.personal_asignado || [];
  const personalIdsAsignados = personalAsignados.map(p => p.personal_id);
  const onConfirmarPersonal = async (ids) => {
    const lista = ids.map(pid => {
      const ya = personalAsignados.find(p => p.personal_id === pid);
      if (ya) return ya;
      const cat = personalCatalogo.find(p => p.personal_id === pid);
      // Iter132 · El costo del evento vive SOLO en la relación. Se pre-carga como
      // sugerencia desde la tarifa maestra (pago_monto / pago_hora legacy), pero a
      // partir de aquí es independiente: editarlo NO modifica la ficha maestra.
      const costoSugerido = (cat?.pago_monto != null && cat?.pago_monto !== "")
        ? Number(cat.pago_monto)
        : (cat?.pago_hora != null && cat?.pago_hora !== "" ? Number(cat.pago_hora) : 0);
      return {
        personal_id: pid,
        nombre: cat?.nombre,
        rol: cat?.rol,
        whatsapp: cat?.whatsapp,
        costo: Number.isFinite(costoSugerido) ? costoSugerido : 0,
        estatus: "asignado",
      };
    });
    await guardarCampo("personal_asignado", lista);
    toast.success(`${lista.length} personas asignadas`);
  };
  const removerPersonal = async (pid) => {
    const lista = personalAsignados.filter(p => p.personal_id !== pid);
    await guardarCampo("personal_asignado", lista);
  };

  // === Proveedores asignados (retrocompatible: array de ids o de objetos)
  const _provRaw = evento?.proveedores_asignados || [];
  const proveedoresIdsAsignados = _provRaw.map(x => (typeof x === "string" ? x : x?.proveedor_id)).filter(Boolean);
  const proveedoresDetalleMap = useMemo(() => {
    const m = {};
    _provRaw.forEach(x => { if (x && typeof x === "object" && x.proveedor_id) m[x.proveedor_id] = x; });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento?.proveedores_asignados]);
  const proveedoresAsignados = useMemo(() =>
    proveedoresCatalogo
      .filter(p => proveedoresIdsAsignados.includes(p.proveedor_id))
      .map(p => ({ ...p, ...(proveedoresDetalleMap[p.proveedor_id] || {}) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proveedoresCatalogo, proveedoresIdsAsignados, proveedoresDetalleMap]
  );
  const onConfirmarProveedores = async (ids) => {
    // Preservar detalle previo cuando exista; nuevos entran como objetos vacíos.
    const nuevos = ids.map(pid => proveedoresDetalleMap[pid] || { proveedor_id: pid, estatus: "asignado" });
    await guardarCampo("proveedores_asignados", nuevos);
    toast.success(`${ids.length} proveedores asignados`);
  };
  const removerProveedor = async (pid) => {
    const nuevos = _provRaw
      .map(x => (typeof x === "string" ? { proveedor_id: x } : x))
      .filter(x => x.proveedor_id !== pid);
    await guardarCampo("proveedores_asignados", nuevos);
  };
  // Iter125 · P1 Fase 5 · edición de relación (sin tocar el registro maestro)
  const updateProveedorRelacion = async (pid, patch) => {
    const nuevos = _provRaw
      .map(x => (typeof x === "string" ? { proveedor_id: x } : x))
      .map(x => (x.proveedor_id === pid ? { ...x, ...patch } : x));
    await guardarCampo("proveedores_asignados", nuevos);
  };
  const updatePersonalRelacion = async (pid, patch) => {
    const lista = (evento?.personal_asignado || []).map(p =>
      p.personal_id === pid ? { ...p, ...patch } : p
    );
    await guardarCampo("personal_asignado", lista);
  };

  // Iter132 · Pagos a proveedor por evento — reutiliza módulo Egresos existente.
  // Un egreso {tipo:"proveedor", beneficiario_id:proveedor_id, evento_id} = 1 pago real.
  // Nunca se sobrescribe: cada anticipo/parcial/liquidación es un egreso adicional.
  const refrescarEgresos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/egresos?evento_id=${id}`);
      setEgresosEvento(r.data || []);
    } catch { /* ignore */ }
  }, [id]);
  const egresosDeProveedor = (pid) => (egresosEvento || []).filter(e =>
    e.tipo === "proveedor" && e.beneficiario_id === pid && e.estatus !== "cancelado"
  );
  const [pagoProveedor, setPagoProveedor] = useState(null);
  const [pagoProvDraft, setPagoProvDraft] = useState(null);
  const [guardandoPagoProv, setGuardandoPagoProv] = useState(false);

  const abrirRegistrarPagoProveedor = (prov) => {
    const pagosPrev = egresosDeProveedor(prov.proveedor_id);
    const pagado = pagosPrev.reduce((s, e) => s + (Number(e.monto) || 0), 0);
    const saldo = Math.max(Number(prov.costo || 0) - pagado, 0);
    const tipoPagoInicial = pagosPrev.length === 0 ? "anticipo" : (saldo > 0 ? "parcial" : "adicional");
    setPagoProveedor(prov);
    setPagoProvDraft({
      monto: saldo > 0 ? saldo.toString() : "",
      fecha_pago: new Date().toISOString().split("T")[0],
      metodo_pago: "transferencia",
      tipo_pago: tipoPagoInicial,
      concepto: "",
    });
  };

  const registrarPagoProveedor = async () => {
    if (!pagoProveedor || !pagoProvDraft) return;
    const monto = parseFloat(pagoProvDraft.monto);
    if (!(monto > 0)) { toast.error("Ingresa un monto válido"); return; }
    const etiquetaTipo = ({ anticipo: "Anticipo", parcial: "Pago parcial", liquidacion: "Liquidación", adicional: "Adicional" })[pagoProvDraft.tipo_pago] || "Pago";
    setGuardandoPagoProv(true);
    try {
      await axios.post(`${API}/egresos`, {
        tipo: "proveedor",
        beneficiario_id: pagoProveedor.proveedor_id,
        beneficiario_nombre: pagoProveedor.nombre || "Proveedor",
        concepto: pagoProvDraft.concepto?.trim() || `${etiquetaTipo} — ${pagoProveedor.servicio || pagoProveedor.categoria || "servicio"}`,
        monto,
        categoria: pagoProveedor.categoria || "general",
        metodo_pago: pagoProvDraft.metodo_pago || "transferencia",
        estatus: "pagado",
        fecha_pago: pagoProvDraft.fecha_pago,
        evento_id: id,
      });
      toast.success(`${etiquetaTipo} registrado`);
      setPagoProveedor(null);
      setPagoProvDraft(null);
      await refrescarEgresos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el pago");
    } finally { setGuardandoPagoProv(false); }
  };
  // Mapa responsable normalizado → tareas de Orden Maestra
  const tareasPorResponsable = useMemo(() => {
    const norm = s => (s || "").trim().toLowerCase();
    const m = {};
    (ordenMaestra || []).forEach(it => {
      const key = norm(it.responsable);
      if (!key) return;
      (m[key] = m[key] || []).push(it);
    });
    return m;
  }, [ordenMaestra]);

  // === Servicios asignados (objects)
  // Iter156 · `servicios_asignados` es EXCLUSIVAMENTE la lista manual del evento
  // (Extras y servicios del evento). NO se hereda desde la cotización — los
  // conceptos cotizados viven en `servicios_incluidos` y se muestran en
  // "Servicios contratados / Cotización origen". Sin fallback: eliminar un
  // extra ahora persiste realmente (antes reaparecía por el fallback).
  const serviciosAsignados = evento?.servicios_asignados || [];
  const serviciosIdsAsignados = serviciosAsignados.map(s => s.servicio_id);
  const onConfirmarServicios = async (ids) => {
    const lista = ids.map(sid => {
      const ya = serviciosAsignados.find(s => s.servicio_id === sid);
      if (ya) return ya;
      const cat = serviciosCatalogo.find(c => (c.servicio_id || c.id) === sid);
      return {
        servicio_id: sid,
        nombre: cat?.nombre || cat?.titulo,
        cantidad: 1,
        precio: cat?.precio || 0,
        costo: cat?.costo || 0,
        notas: "",
        confirmado: false,
      };
    });
    await guardarCampo("servicios_asignados", lista);
    toast.success(`${lista.length} servicios agregados`);
  };
  const removerServicio = async (sid) => {
    await guardarCampo("servicios_asignados", serviciosAsignados.filter(s => s.servicio_id !== sid));
  };
  const toggleServicioConfirmado = async (sid) => {
    const lista = serviciosAsignados.map(s => s.servicio_id === sid ? { ...s, confirmado: !s.confirmado } : s);
    await guardarCampo("servicios_asignados", lista);
  };
  const totalServicios = useMemo(() => serviciosAsignados.reduce((s, x) => s + (x.precio || 0) * (x.cantidad || 1), 0), [serviciosAsignados]);

  // === Menú asignado
  const menuAsignado = evento?.menu_asignado || {};
  // Secciones adicionales del menú (botanas, postres, snacks, carritos, etc.)
  const menuSeccionesBackend = useMemo(() => {
    const arr = evento?.menu_asignado?.secciones;
    if (Array.isArray(arr)) return arr;
    // Migración legacy: convertir botanas/plato_fuerte/postres/bebidas en secciones
    const m = evento?.menu_asignado || {};
    const out = [];
    if (m.botanas) out.push({ _rid: "leg_bot", tipo: "Botanas", nombre: "Botanas", descripcion: m.botanas, precio_extra: 0 });
    if (m.plato_fuerte) out.push({ _rid: "leg_pf", tipo: "Plato fuerte", nombre: "Plato fuerte", descripcion: m.plato_fuerte, precio_extra: 0 });
    if (m.bebidas) out.push({ _rid: "leg_beb", tipo: "Bebidas", nombre: "Bebidas", descripcion: m.bebidas, precio_extra: 0 });
    if (m.postres) out.push({ _rid: "leg_post", tipo: "Postres", nombre: "Postres", descripcion: m.postres, precio_extra: 0 });
    return out;
  }, [evento?.menu_asignado]);
  // Local state: permite editar sin lag y sin race condition por PUT en cada keystroke
  const [menuSecciones, setMenuSecciones] = useState(menuSeccionesBackend);
  // Ref con la última versión para que un save global pueda leerlo sin race
  useEffect(() => { menuSeccionesRef.current = menuSecciones; }, [menuSecciones]);
  // Iter117 · BUG-01 · Fix persistencia menú:
  // Solo sincronizar el state local con backend si NO hay cambios locales sin guardar
  // (para no pisar edición del usuario cuando fetchData refresca el evento).
  useEffect(() => {
    if (dirtyMenuRef.current) return; // hay edición pendiente · no pisar
    setMenuSecciones(menuSeccionesBackend);
  }, [menuSeccionesBackend]);

  const saveMenuSecciones = async (nuevas) => {
    setMenuSecciones(nuevas); // optimistic
    menuSeccionesRef.current = nuevas;
    dirtyMenuRef.current = false; // se está guardando el estado final
    await guardarCampo("menu_asignado", { ...(evento?.menu_asignado || {}), secciones: nuevas });
  };
  const agregarSeccion = async (tipo = "Otra") => {
    const nueva = { _rid: `s_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, tipo, nombre: tipo, descripcion: "", precio_extra: 0 };
    await saveMenuSecciones([...menuSecciones, nueva]);
    toast.success(`Sección "${tipo}" agregada`);
  };
  // Iter117 · BUG-01 · autosave con debounce para funcionar en móvil (onBlur poco fiable)
  const scheduleAutosaveMenu = useCallback(() => {
    dirtyMenuRef.current = true;
    if (menuSaveTimerRef.current) clearTimeout(menuSaveTimerRef.current);
    menuSaveTimerRef.current = setTimeout(async () => {
      const snapshot = menuSeccionesRef.current;
      dirtyMenuRef.current = false;
      try {
        await axios.put(`${API}/eventos/${id}`, { menu_asignado: { ...(evento?.menu_asignado || {}), secciones: snapshot } });
        setEvento(e => e ? { ...e, menu_asignado: { ...(e.menu_asignado || {}), secciones: snapshot } } : e);
      } catch { toast.error("Error al guardar menú"); }
    }, 600);
  }, [id, evento?.menu_asignado]);
  const actualizarSeccionLocal = (rid, campo, valor) => {
    setMenuSecciones(prev => prev.map(s => s._rid === rid ? { ...s, [campo]: campo === "precio_extra" ? (parseFloat(valor) || 0) : valor } : s));
    scheduleAutosaveMenu();
  };
  // Guarda inmediato (usado en onBlur y al presionar Guardar global)
  const guardarSeccionesActuales = async () => {
    if (menuSaveTimerRef.current) { clearTimeout(menuSaveTimerRef.current); menuSaveTimerRef.current = null; }
    dirtyMenuRef.current = false;
    await guardarCampo("menu_asignado", { ...(evento?.menu_asignado || {}), secciones: menuSeccionesRef.current });
  };
  const eliminarSeccion = async (rid) => {
    const nuevas = menuSecciones.filter(s => s._rid !== rid);
    await saveMenuSecciones(nuevas);
    toast.success("Sección eliminada");
  };

  // === Checklist
  const checklist = evento?.checklist?.length ? evento.checklist : CHECKLIST_DEFAULT;
  const checklistDone = checklist.filter(c => c.hecho).length;
  const toggleChecklist = async (cid) => {
    const lista = (evento?.checklist?.length ? evento.checklist : CHECKLIST_DEFAULT).map(c => c.id === cid ? { ...c, hecho: !c.hecho } : c);
    await guardarCampo("checklist", lista);
  };

  // === Notas
  const notas = evento?.notas_evento || [];
  const [nuevaNota, setNuevaNota] = useState("");
  const agregarNota = async () => {
    if (!nuevaNota.trim()) return;
    const nota = { texto: nuevaNota.trim(), fecha: new Date().toISOString(), autor: "Tú" };
    await guardarCampo("notas_evento", [...notas, nota]);
    setNuevaNota("");
    toast.success("Nota agregada");
  };
  const eliminarNota = async (idx) => {
    await guardarCampo("notas_evento", notas.filter((_, i) => i !== idx));
  };

  // === IA
  const [iaError, setIaError] = useState(null);
  const ejecutarIA = async (tipo) => {
    setIaLoading(tipo);
    setShowIaModal(true);
    setIaResult(null);
    setIaError(null);
    try {
      const { data } = await axios.post(`${API}/eventos/${id}/generar-documento`, { tipo });
      setIaResult({ tipo, texto: data.texto });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Error desconocido";
      const isBudget = /budget|presupuesto/i.test(detail);
      setIaError({
        tipo,
        msg: isBudget
          ? "El servicio de generación de documentos está sin saldo. Contacta a soporte para recargar."
          : detail,
        isBudget,
      });
      toast.error(isBudget ? "Servicio sin saldo" : "Error al generar documento");
    } finally { setIaLoading(null); }
  };

  // === Próxima actividad · Iter147 · lee la MISMA fuente unificada que el
  // Cronograma de Resumen: db.orden_maestra (items con hora) + evento.itinerario.
  // No se copia ni duplica: solo se combinan referencias en memoria.
  const itinerario = evento?.itinerario || [];
  const proximaActividad = useMemo(() => {
    const filasOM = (ordenMaestra || [])
      .filter(o => (o.hora || "").trim() && (o.actividad || "").trim())
      .map(o => ({ hora: o.hora, actividad: o.actividad, responsable: o.responsable || "", origen: "orden_maestra" }));
    const filasIti = (itinerario || [])
      .filter(i => (i.hora || "").trim() && (i.actividad || "").trim())
      .map(i => ({ hora: i.hora, actividad: i.actividad, responsable: i.responsable || "", origen: "manual" }));
    const todas = [...filasOM, ...filasIti].sort((a, b) => (a.hora || "99:99").localeCompare(b.hora || "99:99"));
    if (!todas.length) return null;
    const ahora = new Date();
    const hoy = ahora.toISOString().slice(0, 10);
    if (evento?.fecha !== hoy) return todas[0]; // si no es hoy, la primera cronológica
    const ahoraHM = `${String(ahora.getHours()).padStart(2, "0")}:${String(ahora.getMinutes()).padStart(2, "0")}`;
    return todas.find(i => (i.hora || "") >= ahoraHM) || todas[todas.length - 1];
  }, [itinerario, ordenMaestra, evento]);

  if (noEncontrado || (!loading && !evento)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3" data-testid="expediente-no-encontrado">
        <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>Este evento ya no existe o fue eliminado.</p>
        <button onClick={() => navigate("/panel/eventos")} className="eo-btn eo-btn-primary text-xs" data-testid="btn-volver-eventos">Volver a Eventos</button>
      </div>
    );
  }
  if (loading || !evento) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: "var(--brand-primary)", borderTopColor: "transparent" }} /></div>;
  }

  const meta = ESTATUS_META[evento.estatus] || ESTATUS_META.nuevo;
  const fechaLarga = evento.fecha ? new Date(evento.fecha + "T00:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }) : "—";

  return (
    <div className="max-w-[1400px] mx-auto pb-32 md:pb-12">
      {/* Iter117 · BUG-01 · Barra flotante inferior en móvil cuando está editando · Guardar siempre visible */}
      {editando && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t shadow-lg px-4 py-3 flex items-center gap-2 safe-area-inset-bottom" style={{ borderColor: "var(--brand-border, #e5e7eb)" }} data-testid="mobile-guardar-bar">
          <button
            onClick={() => { setEditando(false); setDraft({}); }}
            className="eo-btn eo-btn-outline flex-1"
            data-testid="btn-cancelar-mobile"
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardarEdit}
            className="eo-btn eo-btn-primary flex-1"
            data-testid="btn-guardar-mobile"
          >
            <Save className="w-4 h-4" /> Guardar
          </button>
        </div>
      )}
      {/* ====== TOP BAR ====== */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <button onClick={() => navigate("/panel/agenda")} className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--brand-primary)" }} data-testid="btn-volver-agenda">
          <ArrowLeft className="w-4 h-4" />
          <span className="uppercase tracking-[0.18em] text-xs">Expediente del Evento</span>
        </button>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-start sm:justify-end">
          {editando ? (
            <button onClick={handleGuardarEdit} className="eo-btn eo-btn-primary" data-testid="btn-guardar"><Save className="w-4 h-4" /> Guardar</button>
          ) : (
            <button onClick={() => setEditando(true)} className="eo-btn eo-btn-outline" data-testid="btn-editar"><Edit className="w-4 h-4" /> <span className="hidden xs:inline">Editar</span></button>
          )}
          <button onClick={handleMaterializarOperacion} disabled={materializando} className="eo-btn eo-btn-outline" data-testid="btn-materializar-operacion" title="Genera automáticamente producción, lista de compras, gastos e itinerario a partir del menú, personal y proveedores">
            <Sparkles className="w-4 h-4" /> <span className="hidden xs:inline">{materializando ? "Generando…" : "Materializar operación"}</span><span className="xs:hidden">{materializando ? "…" : "Materializar"}</span>
          </button>
          {evento?.cerrado ? (
            <button onClick={handleReabrirEvento} className="eo-btn eo-btn-outline" data-testid="btn-reabrir-evento" title="Reabrir evento cerrado">
              <Edit className="w-4 h-4" /> <span className="hidden xs:inline">Reabrir</span>
            </button>
          ) : (
            <button onClick={handleCerrarEvento} disabled={cerrando} className="eo-btn eo-btn-outline" data-testid="btn-cerrar-evento" title="Cerrar evento · consolida utilidad y bloquea ediciones">
              <Save className="w-4 h-4" /> <span className="hidden xs:inline">{cerrando ? "Cerrando…" : "Cerrar evento"}</span><span className="xs:hidden">Cerrar</span>
            </button>
          )}
          <button
            onClick={handleCancelarEvento}
            disabled={cancelando || evento?.estatus === 'cancelado' || evento?.cancelado}
            className="eo-btn eo-btn-outline text-red-600 border-red-300"
            title="Cancelar el evento preservando expediente y pagos"
            data-testid="btn-cancelar-evento"
          >
            <X className="w-4 h-4" /> <span className="hidden xs:inline">{cancelando ? 'Cancelando…' : (evento?.cancelado ? 'Cancelado' : 'Cancelar')}</span><span className="xs:hidden">Cancelar</span>
          </button>
          <button onClick={() => setTab("documentos")} className="eo-btn eo-btn-outline" data-testid="btn-mas-acciones"><MoreHorizontal className="w-4 h-4" /> <span className="hidden xs:inline">Más acciones</span></button>
        </div>
      </div>

      {/* ====== HERO ====== · Iter145 · Compactado */}
      <div className="eo-card overflow-hidden mb-3 eo-animate-in">
        <div className="px-4 py-3 md:px-5 md:py-4 flex items-start gap-3 flex-wrap" style={{ background: `linear-gradient(135deg, var(--brand-primary-soft), var(--brand-accent-soft))` }}>
          {/* Icono */}
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(196,149,87,0.3)" }}>
            <Sparkles className="w-6 h-6" style={{ color: "var(--brand-accent)" }} />
          </div>
          {/* Datos */}
          <div className="flex-1 min-w-[220px]">
            {editando ? (
              <input value={draft.nombre || ""} onChange={(e) => setDraft({ ...draft, nombre: e.target.value })} className="font-display text-xl md:text-2xl leading-tight bg-transparent border-0 focus:outline-none w-full" style={{ color: "var(--brand-text)" }} />
            ) : (
              <h1 className="font-display text-xl md:text-2xl leading-tight" style={{ color: "var(--brand-text)" }}>{evento.nombre || "Evento sin nombre"}</h1>
            )}
            <div className="flex items-center gap-2 mt-1 text-xs flex-wrap" style={{ color: "var(--brand-text-muted)" }}>
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fechaLarga}</span>
              {(evento.hora_inicio || evento.hora_fin) && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {evento.hora_inicio || "—"} - {evento.hora_fin || "—"}</span>}
              {evento.ubicacion && (() => {
                const isUrl = /^https?:\/\//i.test(String(evento.ubicacion).trim());
                const lugar = evento.lugar_nombre || evento.nombre_lugar;
                return (
                  <span className="flex items-center gap-1 min-w-0 max-w-full">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {isUrl ? (
                      <a href={evento.ubicacion} target="_blank" rel="noopener noreferrer" className="truncate underline-offset-2 hover:underline" style={{ color: "var(--brand-primary-dark)", maxWidth: "180px" }}>
                        {lugar || "Abrir ubicación"}
                      </a>
                    ) : (
                      <span className="truncate" style={{ maxWidth: "180px" }}>{lugar || evento.ubicacion}</span>
                    )}
                  </span>
                );
              })()}
              {cliente?.nombre && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {cliente.nombre}</span>}
              <span>·</span>
              <span>{totalInv} invitados</span>
            </div>
          </div>
          {/* Totales · Iter145 · Compactados */}
          <div className="flex items-start gap-3 md:gap-4">
            <Cifra label="Total" value={`$${(evento.total || 0).toLocaleString("es-MX")}`} />
            <Cifra label="Pagado" value={`$${totalPagado.toLocaleString("es-MX")}`} color="var(--brand-success)" />
            <Cifra label="Saldo" value={`$${saldo.toLocaleString("es-MX")}`} color={saldo > 0 ? "var(--brand-primary-dark)" : "var(--brand-success)"} />
            <span className={`eo-pill ${meta.cls}`}><CheckCircle2 className="w-3 h-3" /> {meta.label}</span>
          </div>
        </div>
      </div>

      {/* ====== STEPPER VISUAL DE ETAPA (representación · no muta backend) ====== */}
      {(() => {
        const etapas = [
          { key: "cotizado", label: "Cotizado", match: ["nuevo", "cotizado"] },
          { key: "contratado", label: "Contratado", match: ["contratado"] },
          { key: "produccion", label: "En producción", match: ["confirmado"] },
          { key: "en_evento", label: "En evento", match: ["en_evento"] },
          { key: "finalizado", label: "Finalizado", match: ["finalizado"] },
        ];
        const est = (evento?.estatus || "").toLowerCase();
        const idxActual = Math.max(0, etapas.findIndex(e => e.match.includes(est)));
        const esCancelado = est === "cancelado" || evento?.cancelado;
        return (
          <div className="mb-4 eo-card px-2 py-2 sm:px-4 sm:py-3 max-w-full min-w-0 overflow-hidden" data-testid="expediente-stepper-etapa">
            <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto">
              {etapas.map((e, i) => {
                const completado = !esCancelado && i < idxActual;
                const activo = !esCancelado && i === idxActual;
                return (
                  <div key={e.key} className="flex items-center gap-0.5 sm:gap-1 flex-1 min-w-max" data-testid={`stepper-${e.key}`}>
                    <div className="flex flex-col items-center gap-1">
                      <div
                        className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-[11px] font-medium border"
                        style={{
                          background: activo ? "var(--brand-primary)" : completado ? "var(--brand-success)" : "var(--brand-surface-muted)",
                          color: (activo || completado) ? "#fff" : "var(--brand-text-muted)",
                          borderColor: activo ? "var(--brand-primary)" : completado ? "var(--brand-success)" : "var(--brand-border)",
                        }}
                      >
                        {completado ? "✓" : i + 1}
                      </div>
                      <span
                        className="text-[9px] sm:text-[10px] whitespace-nowrap"
                        style={{
                          color: activo ? "var(--brand-primary-dark)" : completado ? "var(--brand-success)" : "var(--brand-text-muted)",
                          fontWeight: activo ? 600 : 400,
                        }}
                      >
                        {e.label}
                      </span>
                    </div>
                    {i < etapas.length - 1 && (
                      <div
                        className="flex-1 h-0.5 mx-0.5 sm:mx-1 min-w-[6px] sm:min-w-[16px]"
                        style={{ background: (!esCancelado && i < idxActual) ? "var(--brand-success)" : "var(--brand-border)" }}
                      />
                    )}
                  </div>
                );
              })}
              {esCancelado && (
                <span className="ml-3 eo-pill eo-pill-danger shrink-0" data-testid="stepper-cancelado">Cancelado</span>
              )}
            </div>
          </div>
        );
      })()}

      {/* ====== SUB-NAV UNIDADES DE NEGOCIO ====== */}
      {(unidadesVisibles.length > 0) && (
        <div className="mb-3 overflow-x-auto" data-testid="unidades-subnav">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.18em] font-medium mr-1" style={{ color: "var(--brand-text-muted)" }}>
              Vista:
            </span>
            {puedeVerVistaGeneral && (
              <button
                onClick={() => setUnidadVista("general")}
                data-testid="unidad-tab-general"
                className="px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 border"
                style={{
                  background: unidadVista === "general" ? "var(--brand-primary)" : "transparent",
                  color: unidadVista === "general" ? "#fff" : "var(--brand-text-muted)",
                  borderColor: unidadVista === "general" ? "var(--brand-primary)" : "var(--brand-border)",
                }}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Vista General
              </button>
            )}
            {unidadesVisibles.map(u => {
              const activa = unidadVista === u.slug;
              return (
                <button
                  key={u.slug}
                  onClick={() => setUnidadVista(u.slug)}
                  data-testid={`unidad-tab-${u.slug}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 border"
                  style={{
                    background: activa ? (u.color || "var(--brand-primary)") : (u.color_soft || "transparent"),
                    color: activa ? "#fff" : (u.color_dark || "var(--brand-text-muted)"),
                    borderColor: activa ? (u.color || "var(--brand-primary)") : (u.color_soft || "var(--brand-border)"),
                  }}
                >
                  <Building2 className="w-3.5 h-3.5" />
                  {u.nombre}
                </button>
              );
            })}
            {unidadVista !== "general" && (
              <span className="text-[10px] ml-2" style={{ color: "var(--brand-text-muted)" }}>
                · Mostrando solo secciones de esta empresa
              </span>
            )}
          </div>
        </div>
      )}

      {/* ====== TABS ====== */}
      <div className="mb-2 overflow-x-auto" data-testid="expediente-tabs">
        <div className="flex gap-1 min-w-min border-b relative" style={{ borderColor: "var(--brand-border)" }}>
          {tabsFiltrados.filter(t => !t.secundaria).map(t => {
            const isActive = t.esNav
              ? false
              : (t.group
                  ? t.group.includes(tab)
                  : (t.key === "resumen" ? (tab === "resumen" || tab === "datos") : tab === t.key));
            return (
              <button
                key={t.key}
                onClick={() => t.esNav
                  ? navigate(`/panel/modo-evento/${evento?.evento_id || id}`)
                  : setTab(t.group ? (t.group.includes(tab) ? tab : t.group[0]) : t.key)}
                className="px-4 py-2.5 text-sm font-medium whitespace-nowrap transition relative"
                style={{
                  color: isActive ? "var(--brand-primary-dark)" : "var(--brand-text-muted)",
                }}
                data-testid={`tab-${t.key}`}
              >
                {t.label}
                {isActive && <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-t-full" style={{ background: "var(--brand-primary)" }} />}
              </button>
            );
          })}
          {/* Iter145 · Menú "Más" que agrupa las pestañas secundarias sin cambiar sus keys ni contenido. */}
          {(() => {
            const secundarias = tabsFiltrados.filter(t => t.secundaria);
            if (secundarias.length === 0) return null;
            const activaSecundaria = secundarias.find(t => t.group ? t.group.includes(tab) : tab === t.key);
            const isMasActive = !!activaSecundaria;
            return (
              <div className="relative" data-testid="tab-mas-wrap">
                <button
                  ref={btnMasRef}
                  onClick={abrirMenuMas}
                  className="px-4 py-2.5 text-sm font-medium whitespace-nowrap transition relative flex items-center gap-1"
                  style={{ color: isMasActive ? "var(--brand-primary-dark)" : "var(--brand-text-muted)" }}
                  data-testid="tab-mas"
                >
                  {isMasActive ? activaSecundaria.label : "Más"}
                  <MoreHorizontal className="w-3.5 h-3.5" />
                  {isMasActive && <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-t-full" style={{ background: "var(--brand-primary)" }} />}
                </button>
                {mostrarMenuMas && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMostrarMenuMas(false)} data-testid="tab-mas-backdrop" />
                    <div
                      className="fixed z-50 min-w-[180px] rounded-lg border shadow-lg py-1"
                      style={{ background: "var(--brand-surface)", borderColor: "var(--brand-border)", top: posMenuMas?.top ?? 0, right: posMenuMas?.right ?? 8 }}
                      data-testid="tab-mas-menu"
                    >
                      {secundarias.map(t => {
                        const isItemActive = t.group ? t.group.includes(tab) : tab === t.key;
                        return (
                          <button
                            key={t.key}
                            onClick={() => { setTab(t.group ? (t.group.includes(tab) ? tab : t.group[0]) : t.key); setMostrarMenuMas(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 transition flex items-center justify-between"
                            style={{ color: isItemActive ? "var(--brand-primary-dark)" : "var(--brand-text)", fontWeight: isItemActive ? 600 : 400 }}
                            data-testid={`tab-mas-item-${t.key}`}
                          >
                            <span>{t.label}</span>
                            {isItemActive && <span className="text-[10px]" style={{ color: "var(--brand-primary)" }}>●</span>}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
        {/* Sub-tabs internos para los grupos consolidados (Planeación / Operación) */}
        {(SUBTABS_PLANEACION.some(s => s.key === tab) || SUBTABS_OPERACION.some(s => s.key === tab)) && (
          <div className="flex gap-1 mt-2 pl-1" data-testid="expediente-subtabs">
            {(SUBTABS_PLANEACION.some(s => s.key === tab) ? SUBTABS_PLANEACION : SUBTABS_OPERACION).filter(s => isModuloVisible(s.key, user)).map(s => (
              <button
                key={s.key}
                onClick={() => setTab(s.key)}
                className="px-3 py-1.5 text-xs rounded-full font-medium transition"
                style={{
                  color: tab === s.key ? "var(--brand-primary-dark)" : "var(--brand-text-muted)",
                  background: tab === s.key ? "var(--brand-surface-muted)" : "transparent",
                  border: `1px solid ${tab === s.key ? "var(--brand-border)" : "transparent"}`,
                }}
                data-testid={`subtab-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ====== CONTENT GRID ====== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-w-full min-w-0">
        {/* MAIN (2/3) */}
        <div className="lg:col-span-2 space-y-5 min-w-0">
          {/* Barra contextual "Editar sección" (per-tab). Iter104c */}
          {!editando && tab !== "documentos" && tab !== "finanzas" && tab !== "pagos" && tab !== "orden_maestra" && tab !== "ordenes_trabajo" && tab !== "produccion" && (
            <div className="flex justify-end -mb-2">
              <button
                onClick={() => { setEditando(true); }}
                className="eo-btn eo-btn-outline text-xs"
                data-testid={`btn-editar-seccion-${tab}`}
                title="Editar los campos de esta sección"
              >
                <Edit className="w-3.5 h-3.5" /> Editar {
                  tab === "resumen" ? "datos del evento"
                  : tab === "servicios_menu" ? "servicios y menú"
                  : tab === "montaje" ? "montaje"
                  : tab === "personal_prov" ? "personal y proveedores"
                  : tab === "itinerario_od" ? "itinerario"
                  : "sección"
                }
              </button>
            </div>
          )}
          {editando && (
            <div className="flex justify-end gap-2 -mb-2">
              <button onClick={() => setEditando(false)} className="eo-btn eo-btn-outline text-xs" data-testid="btn-cancelar-edicion">Cancelar</button>
              <button onClick={handleGuardarEdit} className="eo-btn eo-btn-primary text-xs" data-testid="btn-guardar-seccion"><Save className="w-3.5 h-3.5" /> Guardar cambios</button>
            </div>
          )}
          {tab === "resumen" && (
            <HojaGeneralTrabajo
              evento={evento}
              cliente={cliente}
              personalAsignados={personalAsignados}
              proveedoresAsignados={proveedoresAsignados}
              serviciosAsignados={serviciosAsignados}
              menuAsignado={menuAsignado}
              pagos={pagos}
              totalPagado={totalPagado}
              saldo={saldo}
              itemsContratados={itemsContratados}
              cotizacion={cotizacion}
              contrato={contrato}
              itinerario={itinerario}
              ordenMaestra={ordenMaestra}
              checklist={checklist}
              toggleChecklist={toggleChecklist}
              guardarCampo={guardarCampo}
              navigate={navigate}
              setTab={setTab}
              onRegistrarPago={() => setShowPagoQuick(true)}
              onEditar={(destino) => { setTab(destino); setEditando(true); }}
            />
          )}

          {(tab === "datos") && (
            <Bloque titulo="Datos generales del evento">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CampoEdit edit={editando} label="Nombre" value={draft.nombre} onChange={(v) => setDraft({ ...draft, nombre: v })} />
                <CampoEdit edit={editando} label="Tipo de evento" value={draft.tipo} onChange={(v) => setDraft({ ...draft, tipo: v })} />
                <CampoEdit edit={editando} label="Festejado" value={draft.festejado} onChange={(v) => setDraft({ ...draft, festejado: v })} />
                <CampoEdit edit={editando} label="Tema" value={draft.tema} onChange={(v) => setDraft({ ...draft, tema: v })} />
                <CampoEdit edit={editando} label="Fecha" value={draft.fecha} onChange={(v) => setDraft({ ...draft, fecha: v })} tipo="date" />
                <CampoEdit edit={editando} label="Estatus" value={draft.estatus} onChange={(v) => setDraft({ ...draft, estatus: v })} />
                <CampoEdit edit={editando} label="Hora inicio" value={draft.hora_inicio} onChange={(v) => setDraft({ ...draft, hora_inicio: v })} tipo="time" />
                <CampoEdit edit={editando} label="Hora fin" value={draft.hora_fin} onChange={(v) => setDraft({ ...draft, hora_fin: v })} tipo="time" />
                <CampoEdit edit={editando} label="Lugar" value={draft.ubicacion} onChange={(v) => setDraft({ ...draft, ubicacion: v })} />
                <CampoEdit edit={editando} label="Dirección" value={draft.direccion} onChange={(v) => setDraft({ ...draft, direccion: v })} />
                <CampoEdit edit={editando} label="Adultos" value={draft.adultos} onChange={(v) => setDraft({ ...draft, adultos: parseInt(v) || 0 })} tipo="number" />
                <CampoEdit edit={editando} label="Niños" value={draft.ninos} onChange={(v) => setDraft({ ...draft, ninos: parseInt(v) || 0 })} tipo="number" />
                <CampoEdit edit={editando} label="Colorimetría" value={draft.colorimetria} onChange={(v) => setDraft({ ...draft, colorimetria: v })} />
                <CampoEdit edit={editando} label="Total contratado" value={draft.total} onChange={(v) => setDraft({ ...draft, total: parseFloat(v) || 0 })} tipo="number" />
              </div>

              {/* ===== Unidades participantes (fuente única · usa `unidades` array) ===== */}
              <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--brand-border)" }} data-testid="editor-unidades-participantes">
                <p className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--brand-text-muted)" }}>
                  Unidades participantes
                </p>
                <p className="text-xs mb-3" style={{ color: "var(--brand-text-muted)" }}>
                  Cada unidad seleccionada verá este evento en su agenda y en su vista del expediente. Puede participar más de una en el mismo evento.
                </p>
                <div className="flex flex-wrap gap-2">
                  {unidadesDisponibles.filter(u => u.activa !== false).map(u => {
                    const current = Array.isArray(draft.unidades) ? draft.unidades : [];
                    const activo = current.includes(u.slug);
                    return (
                      <button
                        key={u.slug}
                        disabled={!editando}
                        onClick={() => {
                          const next = activo ? current.filter(s => s !== u.slug) : [...current, u.slug];
                          setDraft({ ...draft, unidades: next });
                        }}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition border disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{
                          background: activo ? (u.color || "var(--brand-primary)") : (u.color_soft || "transparent"),
                          color: activo ? "#fff" : (u.color_dark || "var(--brand-text-muted)"),
                          borderColor: activo ? (u.color || "var(--brand-primary)") : "var(--brand-border)",
                        }}
                        data-testid={`chk-unidad-${u.slug}`}
                      >
                        {activo ? "✓ " : ""}{u.nombre}
                      </button>
                    );
                  })}
                </div>
                {(!draft.unidades || draft.unidades.length === 0) && (
                  <p className="text-xs mt-2" style={{ color: "#b45309" }}>
                    Este evento aún no tiene unidades participantes. Solo aparecerá en la Agenda General.
                  </p>
                )}
              </div>
            </Bloque>
          )}

          {/* Iter147 · Bloque "Operación del evento" (6 cards) queda FUERA de Resumen
              para respetar jerarquía Datos generales | Servicios | Menú // Montaje |
              Personal | Proveedores | Cronograma. Sigue accesible en tab Personal. */}
          {(tab === "personal" || tab === "personal_prov") && (
            <>
            {/* Iter124w · P1 · Operación del Evento (6 cards) leyendo GET /eventos/{id}/operacion */}
            <Bloque titulo="Operación del evento" action={<button onClick={fetchOperacion} className="eo-btn eo-btn-outline text-xs" data-testid="btn-refrescar-operacion" disabled={operacionLoading}>{operacionLoading ? "…" : "↻ Actualizar"}</button>}>
              {!operacion ? (
                <p className="text-xs text-stone-500 py-2">Cargando operación…</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* PRODUCCIÓN */}
                  <div className="border rounded-lg p-3 bg-white" data-testid="op-card-produccion">
                    <div className="text-sm font-medium mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-rose-700" /> Producción</div>
                    {(operacion.produccion || []).length === 0 ? (
                      <p className="text-xs text-stone-400 italic">Sin producción generada. Presiona &quot;Materializar operación&quot;.</p>
                    ) : (
                      <ul className="space-y-1.5 text-xs">
                        {operacion.produccion.slice(0, 8).map((p, i) => (
                          <li key={p.produccion_id || i} className="flex justify-between gap-2">
                            <span className="truncate">{p.nombre || `Producción #${i+1}`}</span>
                            <span className="text-stone-500 shrink-0">{p.estatus || p.estado || ""}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* RECETAS */}
                  <div className="border rounded-lg p-3 bg-white" data-testid="op-card-recetas">
                    <div className="text-sm font-medium mb-2 flex items-center gap-2"><ChefHat className="w-4 h-4 text-rose-700" /> Recetas y requerimientos</div>
                    {(operacion.recetas || []).length === 0 ? (
                      <p className="text-xs text-stone-400 italic">Sin recetas asociadas.</p>
                    ) : (
                      <ul className="space-y-2 text-xs">
                        {operacion.recetas.map((r, i) => (
                          <li key={r.receta_id || i} className="border-b border-stone-100 pb-1.5 last:border-0">
                            <div className="flex justify-between items-start gap-2">
                              <span className="font-medium truncate">{r.nombre || r.servicio_origen}</span>
                              <span className={"text-[10px] px-1.5 py-0.5 rounded shrink-0 " + (r.estado === "vinculada" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700")}>
                                {r.estado === "vinculada" ? "vinculada" : "requiere configuración"}
                              </span>
                            </div>
                            {r.estado === "vinculada" && (
                              <div className="text-[11px] text-stone-500 mt-0.5">Rendimiento base: {r.rendimiento_base} · Requerido: {r.cantidad_requerida} · Factor ×{r.factor_escala}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* INGREDIENTES / INVENTARIO */}
                  <div className="border rounded-lg p-3 bg-white md:col-span-2" data-testid="op-card-ingredientes">
                    <div className="text-sm font-medium mb-2 flex items-center gap-2"><Package className="w-4 h-4 text-rose-700" /> Ingredientes / faltantes</div>
                    {(operacion.ingredientes || []).length === 0 ? (
                      <p className="text-xs text-stone-400 italic">Sin ingredientes calculados (requiere receta vinculada).</p>
                    ) : (
                      <div className="overflow-x-auto -mx-1 sm:mx-0">
                        <table className="w-full text-xs">
                          <thead className="text-stone-500 uppercase text-[10px]">
                            <tr><th className="text-left px-1 py-1">Ingrediente</th><th className="text-right">Req</th><th className="text-right">Disp</th><th className="text-right">Reserv</th><th className="text-right">Faltante</th><th></th></tr>
                          </thead>
                          <tbody>
                            {operacion.ingredientes.map((ing, i) => (
                              <tr key={i} className="border-t border-stone-100">
                                <td className="px-1 py-1 truncate">{ing.nombre}</td>
                                <td className="text-right">{ing.requerido} {ing.unidad}</td>
                                <td className="text-right">{ing.disponible}</td>
                                <td className="text-right">{ing.reservado}</td>
                                <td className={"text-right font-medium " + (ing.faltante > 0 ? "text-red-600" : "text-green-700")}>{ing.faltante}</td>
                                <td><span className={"text-[10px] px-1.5 py-0.5 rounded " + (ing.faltante > 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700")}>{ing.estado}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  {/* COMPRAS */}
                  <div className="border rounded-lg p-3 bg-white" data-testid="op-card-compras">
                    <div className="text-sm font-medium mb-2 flex items-center gap-2"><Package className="w-4 h-4 text-rose-700" /> Lista de compras</div>
                    {(operacion.compras || []).length === 0 ? (
                      <p className="text-xs text-stone-400 italic">Sin compras requeridas.</p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {operacion.compras.slice(0, 8).map((c, i) => (
                          <li key={c.compra_id || i} className="flex justify-between gap-2">
                            <span className="truncate">{c.item || c.nombre || c.producto || `Compra #${i+1}`}</span>
                            <span className="text-stone-500 shrink-0">{c.cantidad || ""} {c.unidad || ""}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* PERSONAL */}
                  <div className="border rounded-lg p-3 bg-white" data-testid="op-card-personal-op">
                    <div className="text-sm font-medium mb-2 flex items-center gap-2"><UserCog className="w-4 h-4 text-rose-700" /> Personal del evento</div>
                    {(operacion.personal || []).length === 0 ? (
                      <p className="text-xs text-stone-400 italic">Sin personal asignado.</p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {operacion.personal.slice(0, 8).map((p, i) => (
                          <li key={p.personal_id || i} className="truncate">{p.nombre || p.rol || `Persona #${i+1}`}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* PROVEEDORES */}
                  <div className="border rounded-lg p-3 bg-white md:col-span-2" data-testid="op-card-proveedores-op">
                    <div className="text-sm font-medium mb-2 flex items-center gap-2"><Truck className="w-4 h-4 text-rose-700" /> Proveedores</div>
                    {(operacion.proveedores || []).length === 0 ? (
                      <p className="text-xs text-stone-400 italic">Sin proveedores asignados.</p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {operacion.proveedores.slice(0, 8).map((p, i) => (
                          <li key={p.proveedor_id || i} className="flex justify-between gap-2"><span className="truncate">{p.nombre || `Proveedor #${i+1}`}</span><span className="text-stone-400 text-[10px]">{p.origen || ""}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </Bloque>
            </>
          )}

          {(tab === "personal" || tab === "personal_prov") && (
            <Bloque
              titulo="Personal asignado"
              testid="equipo-personal"
              action={<button onClick={() => setShowPersonal(true)} className="eo-btn eo-btn-primary" data-testid="btn-agregar-personal"><Plus className="w-4 h-4" /> Asignar personal</button>}
            >
              {personalAsignados.length === 0 ? (
                <EmptyBlock icon={UserCog} msg="Sin personal asignado todavía" />
              ) : (
                <div className="overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
                  <table className="w-full text-xs">
                    <thead style={{ background: "var(--brand-surface-muted)" }}>
                      <tr>
                        <th className="text-left p-2">Nombre</th>
                        <th className="text-left p-2">Rol</th>
                        <th className="text-left p-2">Horario</th>
                        <th className="text-left p-2">Teléfono</th>
                        <th className="text-right p-2">Costo/Pago</th>
                        <th className="text-left p-2">Tareas OM</th>
                        <th className="text-left p-2">Estado</th>
                        <th className="text-right p-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personalAsignados.map(p => {
                        const tareas = tareasPorResponsable[(p.nombre || "").trim().toLowerCase()] || [];
                        return (
                          <tr key={p.personal_id} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`equipo-personal-row-${p.personal_id}`}>
                            <td className="p-2 font-medium" style={{ color: "var(--brand-text)" }}>{p.nombre || "—"}</td>
                            <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{p.rol || "—"}</td>
                            <td className="p-2">{p.horario || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                            <td className="p-2">{p.whatsapp || p.telefono || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                            <td className="p-2 text-right">${(Number(p.costo) || 0).toLocaleString("es-MX")}</td>
                            <td className="p-2">
                              {tareas.length === 0 ? (
                                <span className="italic" style={{ color: "var(--brand-text-muted)" }}>0</span>
                              ) : (
                                <button
                                  onClick={() => setEquipoTareasPara({ nombre: p.nombre, items: tareas })}
                                  className="underline"
                                  style={{ color: "var(--brand-primary)" }}
                                  data-testid={`btn-ver-tareas-${p.personal_id}`}
                                >{tareas.length} tarea{tareas.length === 1 ? "" : "s"}</button>
                              )}
                            </td>
                            <td className="p-2"><span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-primary-soft)", color: "var(--brand-primary-dark)" }}>{p.estatus || "asignado"}</span></td>
                            <td className="p-2 text-right">
                              <button onClick={() => {
                                const maestro = personalCatalogo.find(pc => pc.personal_id === p.personal_id) || null;
                                setEquipoEditPersonal({ ...p, _maestro: maestro });
                              }} className="text-[11px] underline mr-2" style={{ color: "var(--brand-primary)" }} data-testid={`btn-editar-personal-${p.personal_id}`}>Editar</button>
                              <button onClick={() => removerPersonal(p.personal_id)} className="text-[11px] underline text-red-500" data-testid={`btn-quitar-personal-${p.personal_id}`}>Desvincular</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Desvincular NO borra al personal del catálogo maestro, solo elimina su relación con este evento.</p>
            </Bloque>
          )}

          {(tab === "proveedores" || tab === "personal_prov") && (
            <Bloque
              titulo="Proveedores"
              testid="equipo-proveedores"
              action={<button onClick={() => setShowProveedores(true)} className="eo-btn eo-btn-primary" data-testid="btn-agregar-proveedor"><Plus className="w-4 h-4" /> Asignar proveedor</button>}
            >
              {proveedoresAsignados.length === 0 ? (
                <EmptyBlock icon={Truck} msg="Sin proveedores asignados" />
              ) : (
                <div className="overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
                  <table className="w-full text-xs">
                    <thead style={{ background: "var(--brand-surface-muted)" }}>
                      <tr>
                        <th className="text-left p-2">Proveedor</th>
                        <th className="text-left p-2">Servicio</th>
                        <th className="text-left p-2">Contacto</th>
                        <th className="text-left p-2">Llegada/Entrega</th>
                        <th className="text-right p-2">Costo acordado</th>
                        <th className="text-right p-2">Pagado</th>
                        <th className="text-right p-2">Saldo</th>
                        <th className="text-left p-2">Próximo pago</th>
                        <th className="text-left p-2">Estado</th>
                        <th className="text-right p-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proveedoresAsignados.map(p => {
                        const costo = Number(p.costo || 0);
                        const pagosProv = egresosDeProveedor(p.proveedor_id);
                        const pagado = pagosProv.reduce((s, e) => s + (Number(e.monto) || 0), 0);
                        const saldo = Math.max(costo - pagado, 0);
                        const hoy = new Date().toISOString().split("T")[0];
                        const programados = pagosProv
                          .filter(e => e.estatus === "programado" && (e.fecha_pago || "") >= hoy)
                          .sort((a, b) => (a.fecha_pago || "").localeCompare(b.fecha_pago || ""));
                        const proximoPago = programados[0]?.fecha_pago || null;
                        return (
                          <tr key={p.proveedor_id} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`equipo-proveedor-row-${p.proveedor_id}`}>
                            <td className="p-2 font-medium" style={{ color: "var(--brand-text)" }}>{p.nombre || "—"}</td>
                            <td className="p-2">{p.servicio || p.categoria || p.servicios || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                            <td className="p-2">{p.whatsapp || p.telefono || p.contacto || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                            <td className="p-2">{p.llegada || p.hora_entrega || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                            <td className="p-2 text-right" data-testid={`prov-costo-${p.proveedor_id}`}>${costo.toLocaleString("es-MX")}</td>
                            <td className="p-2 text-right" style={{ color: pagado > 0 ? "var(--brand-success)" : "var(--brand-text-muted)" }} data-testid={`prov-pagado-${p.proveedor_id}`}>${pagado.toLocaleString("es-MX")}{pagosProv.length ? <span className="ml-1 text-[10px]" style={{ color: "var(--brand-text-muted)" }}>({pagosProv.length})</span> : null}</td>
                            <td className="p-2 text-right font-medium" style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }} data-testid={`prov-saldo-${p.proveedor_id}`}>${saldo.toLocaleString("es-MX")}</td>
                            <td className="p-2" data-testid={`prov-proximo-${p.proveedor_id}`}>{proximoPago || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                            <td className="p-2"><span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-primary-soft)", color: "var(--brand-primary-dark)" }}>{p.estatus || "asignado"}</span></td>
                            <td className="p-2 text-right whitespace-nowrap">
                              <button onClick={() => abrirRegistrarPagoProveedor(p)} className="text-[11px] underline mr-2" style={{ color: "var(--brand-success)" }} data-testid={`btn-registrar-pago-prov-${p.proveedor_id}`}>Registrar pago</button>
                              <button onClick={() => setEquipoEditProveedor({ ...p })} className="text-[11px] underline mr-2" style={{ color: "var(--brand-primary)" }} data-testid={`btn-editar-prov-${p.proveedor_id}`}>Editar</button>
                              <button onClick={() => removerProveedor(p.proveedor_id)} className="text-[11px] underline text-red-500" data-testid={`btn-quitar-prov-${p.proveedor_id}`}>Desvincular</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Desvincular NO borra al proveedor del catálogo maestro, solo elimina su relación con este evento. Los costos aquí alimentarán Finanzas en una siguiente fase.</p>
            </Bloque>
          )}

          {/* Iter98 · Tab "Servicios y menú" combina servicios + menú */}
          {/* Iter148 · Header dedicado del tab Comercial siguiendo la imagen de referencia.
              Reutiliza: itemsContratados (snapshot cotización), cotizacion, contrato,
              pagos, totalPagado, saldo, cliente, evento.fecha_limite_liquidacion.
              NO crea nuevas colecciones. NO duplica datos. */}
          {tab === "servicios_menu" && (
            <div className="space-y-3 mb-3" data-testid="comercial-header">
              {/* 1) Resumen financiero */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="eo-card p-4" data-testid="fin-total">
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Total contratado</p>
                  <p className="font-display text-2xl mt-1" style={{ color: "var(--brand-text)" }}>${(evento?.total || 0).toLocaleString("es-MX")}</p>
                </div>
                <div className="eo-card p-4" style={{ background: "var(--brand-success-soft)" }} data-testid="fin-pagado">
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-success)" }}>Pagado</p>
                  <p className="font-display text-2xl mt-1" style={{ color: "var(--brand-success)" }}>${totalPagado.toLocaleString("es-MX")}</p>
                  {pagos.filter(p => !p.cancelado).slice(-1).map(p => (
                    <p key={p.pago_id} className="text-[10px] mt-1" style={{ color: "var(--brand-text-muted)" }}>Último pago: {p.fecha_pago || (p.created_at || "").substring(0, 10)}</p>
                  ))}
                </div>
                <div className="eo-card p-4" style={{ background: saldo > 0 ? "var(--brand-warning-soft)" : "var(--brand-success-soft)" }} data-testid="fin-saldo">
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>Saldo pendiente</p>
                  <p className="font-display text-2xl mt-1" style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>${saldo.toLocaleString("es-MX")}</p>
                  {evento?.fecha_limite_liquidacion && (
                    <p className="text-[10px] mt-1" style={{ color: "var(--brand-text-muted)" }}>Liquidar a más tardar el {evento.fecha_limite_liquidacion}</p>
                  )}
                </div>
              </div>

              {/* Grid 3 columnas: Cotización | Servicios | Pagos+Cliente */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                {/* 2) Cotización origen */}
                <div className="eo-card p-4 lg:col-span-1" data-testid="com-cotizacion">
                  <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>📋 Cotización origen</p>
                  {cotizacion ? (
                    <div className="space-y-1 text-xs">
                      <p><span style={{ color: "var(--brand-text-muted)" }}>Folio:</span> <span className="font-mono" style={{ color: "var(--brand-text)" }}>{cotizacion.cotizacion_id || cotizacion.folio || "—"}</span></p>
                      <p><span style={{ color: "var(--brand-text-muted)" }}>Fecha:</span> {cotizacion.fecha || (cotizacion.created_at || "").substring(0, 10)}</p>
                      <p><span style={{ color: "var(--brand-text-muted)" }}>Estatus:</span> <span className="eo-pill" style={{ background: "var(--brand-success-soft)", color: "var(--brand-success)" }}>{cotizacion.estatus || "—"}</span></p>
                      {cotizacion.version && <p><span style={{ color: "var(--brand-text-muted)" }}>Versión:</span> {cotizacion.version}</p>}
                      <button onClick={() => navigate(`/panel/cotizar/${cotizacion.cotizacion_id}`)} className="eo-btn eo-btn-outline text-xs mt-2 w-full" data-testid="btn-ver-cotizacion">Ver cotización</button>
                    </div>
                  ) : (
                    <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin cotización vinculada.</p>
                  )}
                </div>

                {/* 3) Servicios contratados · bloque central */}
                <div className="eo-card p-4 lg:col-span-2" data-testid="com-servicios">
                  <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>🎯 Servicios contratados <span className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>({(itemsContratados || []).length})</span></p>
                  {(itemsContratados && itemsContratados.length > 0) ? (
                    <div className="overflow-x-auto max-h-96 border rounded" style={{ borderColor: "var(--brand-border)" }}>
                      <table className="w-full text-xs">
                        <thead style={{ background: "var(--brand-surface-muted)" }}>
                          <tr>
                            <th className="text-left p-2">Concepto</th>
                            <th className="text-right p-2 w-14">Cant.</th>
                            <th className="text-right p-2 w-20">P.Unit</th>
                            <th className="text-right p-2 w-20">Importe</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsContratados.map((it, i) => (
                            <tr key={it.item_id || `com-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`com-item-${i}`}>
                              <td className="p-2">
                                <p style={{ color: "var(--brand-text)" }}>{it.nombre}</p>
                                {(it.nota || it.detalle) && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{it.nota || it.detalle}</p>}
                              </td>
                              <td className="p-2 text-right">{it.cantidad || 1}</td>
                              <td className="p-2 text-right">${(it.precio_unitario || 0).toLocaleString("es-MX")}</td>
                              <td className="p-2 text-right font-medium">${(it.importe || (it.precio_unitario || 0) * (it.cantidad || 1)).toLocaleString("es-MX")}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: "var(--brand-surface-muted)" }}>
                            <td colSpan="3" className="p-2 text-right text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Total</td>
                            <td className="p-2 text-right font-display" style={{ color: "var(--brand-text)" }}>${(evento?.total || 0).toLocaleString("es-MX")}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin servicios en la cotización aceptada.</p>
                  )}
                  <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Fuente: {contrato ? "Contrato firmado" : cotizacion ? "Cotización aceptada" : "Evento"}. La edición se hace desde la cotización.</p>

                  {/* Iter155 · CRUD de extras propios del evento (no toca la cotización) */}
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--brand-border)" }} data-testid="com-extras">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>➕ Extras y servicios del evento <span className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>({serviciosAsignados.length})</span></p>
                      <button
                        onClick={async () => {
                          const sid = `sv_${Date.now()}`;
                          const nueva = [...serviciosAsignados, { servicio_id: sid, nombre: "Nuevo servicio", cantidad: 1, precio: 0, notas: "" }];
                          await guardarCampo("servicios_asignados", nueva);
                          toast.success("Extra agregado");
                        }}
                        className="eo-btn eo-btn-outline text-xs"
                        data-testid="btn-agregar-extra"
                      >
                        + Agregar
                      </button>
                    </div>
                    {serviciosAsignados.length === 0 ? (
                      <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin extras. Añade servicios adicionales que no vengan de la cotización.</p>
                    ) : (
                      <div className="overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
                        <table className="w-full text-xs">
                          <thead style={{ background: "var(--brand-surface-muted)" }}>
                            <tr>
                              <th className="text-left p-2">Concepto</th>
                              <th className="text-right p-2 w-20">Cant.</th>
                              <th className="text-right p-2 w-24">Precio</th>
                              <th className="text-right p-2 w-24">Importe</th>
                              <th className="p-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {serviciosAsignados.map((s, i) => (
                              <tr key={s.servicio_id || `sv-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`extra-row-${s.servicio_id || i}`}>
                                <td className="p-1">
                                  <input
                                    defaultValue={s.nombre || ""}
                                    onBlur={async (e) => {
                                      const nombre = e.target.value.trim();
                                      if (nombre === (s.nombre || "")) return;
                                      const lista = serviciosAsignados.map(x => x.servicio_id === s.servicio_id ? { ...x, nombre } : x);
                                      await guardarCampo("servicios_asignados", lista);
                                    }}
                                    className="eo-input w-full text-xs"
                                    data-testid={`extra-nombre-${s.servicio_id || i}`}
                                  />
                                </td>
                                <td className="p-1">
                                  <input
                                    type="number"
                                    defaultValue={s.cantidad ?? 1}
                                    onBlur={async (e) => {
                                      const cantidad = Number(e.target.value) || 1;
                                      if (cantidad === (s.cantidad || 1)) return;
                                      const lista = serviciosAsignados.map(x => x.servicio_id === s.servicio_id ? { ...x, cantidad } : x);
                                      await guardarCampo("servicios_asignados", lista);
                                    }}
                                    className="eo-input w-full text-xs text-right"
                                    data-testid={`extra-cantidad-${s.servicio_id || i}`}
                                  />
                                </td>
                                <td className="p-1">
                                  <input
                                    type="number"
                                    defaultValue={s.precio ?? 0}
                                    onBlur={async (e) => {
                                      const precio = Number(e.target.value) || 0;
                                      if (precio === (s.precio || 0)) return;
                                      const lista = serviciosAsignados.map(x => x.servicio_id === s.servicio_id ? { ...x, precio } : x);
                                      await guardarCampo("servicios_asignados", lista);
                                    }}
                                    className="eo-input w-full text-xs text-right"
                                    data-testid={`extra-precio-${s.servicio_id || i}`}
                                  />
                                </td>
                                <td className="p-2 text-right font-medium">${((s.precio || 0) * (s.cantidad || 1)).toLocaleString("es-MX")}</td>
                                <td className="p-2 text-right">
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm("¿Eliminar este extra?")) return;
                                      await removerServicio(s.servicio_id);
                                      toast.success("Extra eliminado");
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                    data-testid={`btn-eliminar-extra-${s.servicio_id || i}`}
                                    title="Eliminar"
                                  >
                                    ×
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: "var(--brand-surface-muted)" }}>
                              <td colSpan="3" className="p-2 text-right text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Total extras</td>
                              <td className="p-2 text-right font-display" style={{ color: "var(--brand-text)" }}>${totalServicios.toLocaleString("es-MX")}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* 4/5/6) Contrato + Pagos + Cliente en columna */}
                <div className="space-y-3 lg:col-span-1">
                  {/* Contrato */}
                  <div className="eo-card p-4" data-testid="com-contrato">
                    <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>📄 Contrato</p>
                    {contrato ? (
                      <div className="space-y-1 text-xs">
                        <p><span style={{ color: "var(--brand-text-muted)" }}>Estatus:</span> <span className="eo-pill" style={{ background: "var(--brand-success-soft)", color: "var(--brand-success)" }}>{contrato.estatus || "vigente"}</span></p>
                        {contrato.fecha_firma && <p><span style={{ color: "var(--brand-text-muted)" }}>Firma:</span> {contrato.fecha_firma}</p>}
                        <button onClick={() => navigate(`/panel/contratos/${contrato.contrato_id}`)} className="eo-btn eo-btn-outline text-xs mt-2 w-full" data-testid="btn-ver-contrato">Ver contrato</button>
                      </div>
                    ) : (
                      <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin contrato generado aún.</p>
                    )}
                  </div>

                  {/* Pagos */}
                  <div className="eo-card p-4" data-testid="com-pagos">
                    <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>💳 Pagos</p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between"><span style={{ color: "var(--brand-text-muted)" }}>Total contratado</span><span>${(evento?.total || 0).toLocaleString("es-MX")}</span></div>
                      <div className="flex justify-between"><span style={{ color: "var(--brand-text-muted)" }}>Pagos recibidos</span><span style={{ color: "var(--brand-success)" }}>${totalPagado.toLocaleString("es-MX")}</span></div>
                      <div className="flex justify-between font-medium"><span>Saldo</span><span style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>${saldo.toLocaleString("es-MX")}</span></div>
                      {evento?.fecha_limite_liquidacion && <div className="flex justify-between text-[10px]" style={{ color: "var(--brand-text-muted)" }}><span>Fecha límite</span><span>{evento.fecha_limite_liquidacion}</span></div>}
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button onClick={() => setTab("pagos")} className="eo-btn eo-btn-outline text-xs flex-1" data-testid="btn-ver-pagos">Ver pagos</button>
                      <button onClick={() => setShowPagoQuick(true)} className="eo-btn eo-btn-primary text-xs flex-1" data-testid="btn-registrar-pago-com">+ Registrar</button>
                    </div>
                  </div>

                  {/* Cliente */}
                  <div className="eo-card p-4" data-testid="com-cliente">
                    <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>👤 Cliente</p>
                    {cliente ? (
                      <div className="text-xs space-y-1">
                        <p style={{ color: "var(--brand-text)" }}>{cliente.nombre || "—"}</p>
                        {cliente.telefono && <p style={{ color: "var(--brand-text-muted)" }}>📞 {cliente.telefono}</p>}
                        {cliente.email && <p style={{ color: "var(--brand-text-muted)" }}>✉️ {cliente.email}</p>}
                        {(cliente.observaciones_comerciales || cliente.observaciones) && <p className="text-[10px] mt-1 italic" style={{ color: "var(--brand-text-muted)" }}>{cliente.observaciones_comerciales || cliente.observaciones}</p>}
                      </div>
                    ) : (
                      <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin cliente vinculado.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 7/8) Cambios y Historial comercial · solo si hay datos reales */}
              {((Array.isArray(evento?.cambios_comerciales) && evento.cambios_comerciales.length > 0) || (Array.isArray(evento?.historial_comercial) && evento.historial_comercial.length > 0)) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {Array.isArray(evento?.cambios_comerciales) && evento.cambios_comerciales.length > 0 && (
                    <div className="eo-card p-4" data-testid="com-cambios">
                      <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>🔄 Cambios comerciales</p>
                      <ul className="text-xs space-y-1">
                        {evento.cambios_comerciales.map((c, i) => (
                          <li key={i} className="flex gap-2"><span style={{ color: "var(--brand-text-muted)" }}>{c.fecha}</span><span>{c.descripcion || c.detalle}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(evento?.historial_comercial) && evento.historial_comercial.length > 0 && (
                    <div className="eo-card p-4" data-testid="com-historial">
                      <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>🕐 Historial comercial</p>
                      <ul className="text-xs space-y-1">
                        {evento.historial_comercial.map((h, i) => (
                          <li key={i} className="flex gap-2"><span style={{ color: "var(--brand-text-muted)" }}>{h.fecha}</span><span>{h.evento_hist || h.descripcion}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Iter148 · En Comercial ya no se repite este bloque "Origen comercial"
              porque su información (financiero, cotización, servicios contratados,
              contrato, pagos, cliente) YA está en el header nuevo de arriba. Solo
              se conserva su render dentro de Resumen. */}
          {/* Iter157 · Bloque "Origen comercial" removido de Resumen porque
              ahora vive en la Hoja General de Trabajo (bloque 2 · Cotización /
              Lo contratado). NO se elimina el código; solo se desactiva su
              render con `false &&`. */}
          {false && tab === "resumen" && (
            <>
            {(contrato || cotizacion || (itemsContratados && itemsContratados.length > 0)) && (() => {
              // Iter125 · P1 Fase 2b · Fallback de lectura Contrato → Cotización → Evento
              const first = (...vals) => {
                for (const v of vals) {
                  if (v === 0) continue; // 0 numérico no cuenta como valor definido para invitados
                  if (v !== undefined && v !== null && v !== "" && v !== "—") return v;
                }
                return null;
              };
              const _horarioDe = (o) => {
                if (!o) return null;
                if (o.horario) return o.horario;
                const hi = o.hora_inicio, hf = o.hora_fin;
                if (hi && hf) return `${hi} - ${hf}`;
                return hi || hf || null;
              };
              const _invitadosDe = (o) => {
                if (!o) return null;
                return first(o.invitados_contratados, o.total_invitados, o.invitados);
              };
              const evInvSuma = (parseInt(evento.adultos) || 0) + (parseInt(evento.ninos) || 0);
              const heredadoTipo = first(contrato?.tipo_evento, cotizacion?.tipo_evento, evento.tipo, evento.tipo_evento);
              const heredadoFecha = first(contrato?.fecha, cotizacion?.fecha, evento.fecha);
              const heredadoHorario = first(_horarioDe(contrato), _horarioDe(cotizacion), _horarioDe(evento));
              const heredadoUbicacion = first(contrato?.direccion, contrato?.lugar, cotizacion?.direccion, cotizacion?.lugar, evento.direccion, evento.ubicacion, evento.lugar);
              const heredadoInvitados = first(_invitadosDe(contrato), _invitadosDe(cotizacion), cliente?.total_invitados, _invitadosDe(evento), evInvSuma || null);
              const P = "Pendiente de definir";
              return (
              <Bloque
                titulo="Ya contratado · información heredada"
                testid="planeacion-heredado"
                action={<span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-primary-soft)", color: "var(--brand-primary-dark)" }}>Solo lectura</span>}
              >
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mb-3">
                  <div data-testid="heredado-tipo"><p style={{ color: "var(--brand-text-muted)" }}>Tipo de evento</p><p className="font-medium" style={{ color: "var(--brand-text)" }}>{heredadoTipo || P}</p></div>
                  <div data-testid="heredado-fecha"><p style={{ color: "var(--brand-text-muted)" }}>Fecha</p><p className="font-medium" style={{ color: "var(--brand-text)" }}>{heredadoFecha || P}</p></div>
                  <div data-testid="heredado-horario"><p style={{ color: "var(--brand-text-muted)" }}>Horario contratado</p><p className="font-medium" style={{ color: "var(--brand-text)" }}>{heredadoHorario || P}</p></div>
                  <div data-testid="heredado-ubicacion" className="col-span-2"><p style={{ color: "var(--brand-text-muted)" }}>Ubicación</p><p className="font-medium truncate" style={{ color: "var(--brand-text)" }}>{heredadoUbicacion || P}</p></div>
                  <div data-testid="heredado-invitados"><p style={{ color: "var(--brand-text-muted)" }}>Invitados contratados</p><p className="font-medium" style={{ color: "var(--brand-text)" }}>{heredadoInvitados || P}</p></div>
                </div>
                {(itemsContratados && itemsContratados.length > 0) && (() => {
                  // Iter147 · En Resumen mostramos top 5 + "Ver todos". La lista completa
                  // sigue viviendo en Comercial. NO se duplica la fuente.
                  const enResumen = tab === "resumen";
                  const preview = enResumen ? itemsContratados.slice(0, 5) : itemsContratados;
                  return (
                  <div className={enResumen ? "border rounded overflow-x-auto" : "max-h-52 overflow-y-auto overflow-x-auto border rounded"} style={{ borderColor: "var(--brand-border)" }} data-testid="heredado-items">
                    <table className="w-full text-xs">
                      <thead style={{ background: "var(--brand-surface-muted)" }}>
                        <tr>
                          <th className="text-left p-2">Concepto contratado</th>
                          <th className="text-left p-2">Tipo</th>
                          <th className="text-right p-2">Cant.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((it, i) => (
                          <tr key={it.item_id || `hp-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }}>
                            <td className="p-2">
                              <p className="truncate" style={{ color: "var(--brand-text)" }}>{it.nombre}</p>
                              {it.seccion && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{it.seccion}</p>}
                            </td>
                            <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{it.tipo || it.categoria || "—"}</td>
                            <td className="p-2 text-right">{it.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {enResumen && itemsContratados.length > 5 && (
                      <div className="p-2 text-center border-t" style={{ borderColor: "var(--brand-border)", background: "var(--brand-surface-muted)" }}>
                        <button onClick={() => setTab("servicios_menu")} className="text-xs font-medium underline underline-offset-2" style={{ color: "var(--brand-primary-dark)" }} data-testid="btn-ver-todos-servicios">
                          Ver los {itemsContratados.length} servicios en Comercial →
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })()}
                {(cliente?.alergias || cliente?.necesidades_especiales) && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    {cliente?.alergias && (
                      <div className="p-2 rounded" style={{ background: "var(--brand-warning-soft)" }} data-testid="heredado-alergias">
                        <p className="uppercase text-[10px]" style={{ color: "var(--brand-warning)" }}>Alergias del cliente</p>
                        <p style={{ color: "var(--brand-text)" }}>{cliente.alergias}</p>
                      </div>
                    )}
                    {cliente?.necesidades_especiales && (
                      <div className="p-2 rounded" style={{ background: "var(--brand-primary-soft)" }} data-testid="heredado-necesidades">
                        <p className="uppercase text-[10px]" style={{ color: "var(--brand-primary-dark)" }}>Necesidades especiales</p>
                        <p style={{ color: "var(--brand-text)" }}>{cliente.necesidades_especiales}</p>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Fuente: {contrato ? "Contrato" : cotizacion ? "Cotización" : "Evento"}. Estos datos NO se vuelven a capturar aquí.</p>
              </Bloque>
              );
            })()}

            {/* Iter125 · P1 Fase 2 · Planeación · FALTA DEFINIR (editable) */}
            <Bloque titulo="Falta definir para operar" testid="planeacion-falta-definir">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Adultos finales */}
                <div className="p-3 rounded-lg border" style={{ borderColor: (parseInt(draft.adultos) > 0) ? "var(--brand-success)" : "var(--brand-warning)", background: "var(--brand-surface)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: (parseInt(draft.adultos) > 0) ? "var(--brand-success)" : "var(--brand-warning)" }} />
                    <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Adultos finales</label>
                  </div>
                  <input
                    type="number"
                    value={draft.adultos ?? ""}
                    onChange={(e) => setDraft({ ...draft, adultos: e.target.value })}
                    onBlur={() => guardarCampo("adultos", parseInt(draft.adultos) || 0)}
                    className="eo-input w-full text-sm"
                    placeholder="Ej. 30"
                    data-testid="planeacion-adultos"
                  />
                </div>
                {/* Niños finales */}
                <div className="p-3 rounded-lg border" style={{ borderColor: (draft.ninos !== undefined && draft.ninos !== "" && draft.ninos !== null) ? "var(--brand-success)" : "var(--brand-warning)", background: "var(--brand-surface)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: (draft.ninos !== undefined && draft.ninos !== "" && draft.ninos !== null) ? "var(--brand-success)" : "var(--brand-warning)" }} />
                    <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Niños finales</label>
                  </div>
                  <input
                    type="number"
                    value={draft.ninos ?? ""}
                    onChange={(e) => setDraft({ ...draft, ninos: e.target.value })}
                    onBlur={() => guardarCampo("ninos", parseInt(draft.ninos) || 0)}
                    className="eo-input w-full text-sm"
                    placeholder="Ej. 15"
                    data-testid="planeacion-ninos"
                  />
                </div>
                {/* Horario definitivo */}
                <div className="p-3 rounded-lg border md:col-span-2" style={{ borderColor: (draft.hora_inicio && draft.hora_fin) ? "var(--brand-success)" : "var(--brand-warning)", background: "var(--brand-surface)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: (draft.hora_inicio && draft.hora_fin) ? "var(--brand-success)" : "var(--brand-warning)" }} />
                    <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Horario definitivo del evento</label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="time"
                      value={draft.hora_inicio || ""}
                      onChange={(e) => setDraft({ ...draft, hora_inicio: e.target.value })}
                      onBlur={() => guardarCampo("hora_inicio", draft.hora_inicio || "")}
                      className="eo-input w-full text-sm"
                      data-testid="planeacion-hora-inicio"
                    />
                    <input
                      type="time"
                      value={draft.hora_fin || ""}
                      onChange={(e) => setDraft({ ...draft, hora_fin: e.target.value })}
                      onBlur={() => guardarCampo("hora_fin", draft.hora_fin || "")}
                      className="eo-input w-full text-sm"
                      data-testid="planeacion-hora-fin"
                    />
                  </div>
                </div>
                {/* Solicitudes especiales operativas */}
                <div className="p-3 rounded-lg border md:col-span-2" style={{ borderColor: draft.solicitudes_planeacion ? "var(--brand-success)" : "var(--brand-border)", background: "var(--brand-surface)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: draft.solicitudes_planeacion ? "var(--brand-success)" : "var(--brand-text-muted)" }} />
                    <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Solicitudes especiales del cliente (planeación)</label>
                  </div>
                  <textarea
                    value={draft.solicitudes_planeacion || ""}
                    onChange={(e) => setDraft({ ...draft, solicitudes_planeacion: e.target.value })}
                    onBlur={() => guardarCampo("solicitudes_planeacion", draft.solicitudes_planeacion || "")}
                    className="eo-input w-full text-sm"
                    rows={2}
                    placeholder="Ej. pastel sin nuez, zona sin fotos, mesa infantil separada..."
                    data-testid="planeacion-solicitudes"
                  />
                </div>
                {/* Observaciones operativas */}
                <div className="p-3 rounded-lg border md:col-span-2" style={{ borderColor: draft.observaciones_planeacion ? "var(--brand-success)" : "var(--brand-border)", background: "var(--brand-surface)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: draft.observaciones_planeacion ? "var(--brand-success)" : "var(--brand-text-muted)" }} />
                    <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Observaciones operativas</label>
                  </div>
                  <textarea
                    value={draft.observaciones_planeacion || ""}
                    onChange={(e) => setDraft({ ...draft, observaciones_planeacion: e.target.value })}
                    onBlur={() => guardarCampo("observaciones_planeacion", draft.observaciones_planeacion || "")}
                    className="eo-input w-full text-sm"
                    rows={2}
                    placeholder="Notas internas: accesos, tiempos, riesgos, prioridades..."
                    data-testid="planeacion-observaciones"
                  />
                </div>
                {/* Estado del montaje */}
                <div className="p-3 rounded-lg border md:col-span-2" style={{ borderColor: (draft.tema || draft.colorimetria || draft.mesas || draft.decoracion) ? "var(--brand-success)" : "var(--brand-warning)", background: "var(--brand-surface)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: (draft.tema || draft.colorimetria || draft.mesas || draft.decoracion) ? "var(--brand-success)" : "var(--brand-warning)" }} />
                    <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Montaje y decoración</label>
                  </div>
                  <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>
                    {(draft.tema || draft.colorimetria || draft.mesas || draft.decoracion) ? "Definido parcial. " : "Aún sin definir. "}
                    <button onClick={() => setTab("montaje")} className="underline" style={{ color: "var(--brand-primary)" }} data-testid="planeacion-ir-montaje">Ir a Montaje y decoración →</button>
                  </p>
                </div>
              </div>
              <p className="text-[10px] mt-3" style={{ color: "var(--brand-text-muted)" }}>Solo los datos operativos que faltan definir. Los datos ya contratados no se vuelven a pedir aquí.</p>
            </Bloque>
            <Bloque titulo="Servicios y extras" testid="planeacion-servicios-extras" action={<button onClick={() => setShowServicios(true)} className="eo-btn eo-btn-primary" data-testid="btn-agregar-servicio"><Plus className="w-4 h-4" /> Agregar servicio</button>}>
              {serviciosAsignados.length === 0 ? (
                <EmptyBlock icon={Star} msg="Sin servicios contratados" />
              ) : (
                <div className="space-y-2">
                  {serviciosAsignados.map(s => (
                    <div key={s.servicio_id} className="eo-card p-3 flex items-center gap-3">
                      <button onClick={() => toggleServicioConfirmado(s.servicio_id)} data-testid={`check-servicio-${s.servicio_id}`}>
                        {s.confirmado ? <CheckCircle2 className="w-5 h-5" style={{ color: "var(--brand-success)" }} /> : <Circle className="w-5 h-5" style={{ color: "var(--brand-text-muted)" }} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{s.nombre}</p>
                        {s.notas && <p className="text-xs truncate" style={{ color: "var(--brand-text-muted)" }}>{s.notas}</p>}
                      </div>
                      <span className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>${((s.precio || 0) * (s.cantidad || 1)).toLocaleString("es-MX")}</span>
                      <button onClick={() => removerServicio(s.servicio_id)} className="p-1.5 rounded-lg hover:bg-red-50"><X className="w-4 h-4 text-red-500" /></button>
                    </div>
                  ))}
                  <div className="flex justify-between p-3 rounded-xl" style={{ background: "var(--brand-primary-soft)" }}>
                    <span className="text-sm font-medium" style={{ color: "var(--brand-primary-dark)" }}>Total servicios</span>
                    <span className="font-display text-lg" style={{ color: "var(--brand-primary-dark)" }}>${totalServicios.toLocaleString("es-MX")}</span>
                  </div>
                </div>
              )}
            </Bloque>
            </>
          )}

          {(tab === "menu" || tab === "servicios_menu") && (
            <Bloque
              titulo="Menú del evento"
              action={
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setShowMenu(true)} className="eo-btn eo-btn-secondary text-xs" data-testid="btn-elegir-menu">
                    <ChefHat className="w-4 h-4" /> Elegir del catálogo
                  </button>
                  <button onClick={() => agregarSeccion("Sección")} className="eo-btn eo-btn-primary text-xs" data-testid="btn-agregar-seccion">
                    <Plus className="w-4 h-4" /> Agregar sección
                  </button>
                </div>
              }
            >
              <div className="space-y-3">
                {menuAsignado.id && (
                  <div className="flex items-start justify-between gap-3 flex-wrap p-3 rounded-xl" style={{ background: "var(--brand-surface-muted)" }}>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Menú base</p>
                      <p className="font-display text-xl" style={{ color: "var(--brand-text)" }}>{menuAsignado.nombre}</p>
                      {menuAsignado.precio_por_persona > 0 && (
                        <p className="text-xs mt-1" style={{ color: "var(--brand-text-muted)" }}>
                          ${menuAsignado.precio_por_persona.toLocaleString("es-MX")} / persona · {totalInv} personas
                        </p>
                      )}
                    </div>
                    {menuAsignado.precio_por_persona > 0 && totalInv > 0 && (
                      <div className="px-3 py-2 rounded-xl text-right" style={{ background: "var(--brand-primary-soft)" }}>
                        <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-primary-dark)" }}>Subtotal menú</p>
                        <p className="font-display text-lg" style={{ color: "var(--brand-primary-dark)" }}>
                          ${(menuAsignado.precio_por_persona * totalInv).toLocaleString("es-MX")}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 flex-wrap text-xs" style={{ color: "var(--brand-text-muted)" }}>
                  <span>Agregar rápido:</span>
                  {["Botanas", "Plato fuerte", "Postres", "Bebidas", "Snacks", "Carrito"].map(t => (
                    <button
                      key={`qa-${t}`}
                      onClick={() => agregarSeccion(t)}
                      className="px-2 py-0.5 rounded-full border hover:bg-stone-100"
                      style={{ borderColor: "var(--brand-border)" }}
                      data-testid={`btn-quick-${t.toLowerCase().replace(' ', '-')}`}
                    >
                      + {t}
                    </button>
                  ))}
                </div>

                {menuSecciones.length === 0 && !menuAsignado.id ? (
                  <EmptyBlock icon={ChefHat} msg="Aún no hay secciones. Elige un menú del catálogo o agrega secciones (Botanas, Postres, Carritos, etc.)." />
                ) : menuSecciones.length === 0 ? (
                  <p className="text-xs text-center py-3" style={{ color: "var(--brand-text-muted)" }}>Agrega botanas, plato fuerte, postres o cualquier extra.</p>
                ) : (
                  <div className="space-y-2">
                    {menuSecciones.map((s) => (
                      <div key={s._rid} className="p-3 rounded-xl border" style={{ borderColor: "var(--brand-border)", background: "var(--brand-surface)" }} data-testid={`seccion-${s._rid}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,120px,auto] gap-2 items-start">
                          <div>
                            <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Tipo / Nombre</label>
                            <input
                              value={s.nombre || ""}
                              onChange={(e) => actualizarSeccionLocal(s._rid, "nombre", e.target.value)}
                              onBlur={guardarSeccionesActuales}
                              placeholder="Ej. Botanas, Postres, Carrito snacks"
                              className="eo-input w-full text-sm"
                              data-testid={`seccion-nombre-${s._rid}`}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Descripción / Detalle</label>
                            <input
                              value={s.descripcion || ""}
                              onChange={(e) => actualizarSeccionLocal(s._rid, "descripcion", e.target.value)}
                              onBlur={guardarSeccionesActuales}
                              placeholder="Ej. guacamole, queso, tortillas..."
                              className="eo-input w-full text-sm"
                              data-testid={`seccion-desc-${s._rid}`}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Precio extra ($)</label>
                            <input
                              type="number"
                              value={s.precio_extra ?? 0}
                              onChange={(e) => actualizarSeccionLocal(s._rid, "precio_extra", e.target.value)}
                              onBlur={guardarSeccionesActuales}
                              placeholder="0"
                              className="eo-input w-full text-sm text-right"
                              data-testid={`seccion-precio-${s._rid}`}
                            />
                          </div>
                          <button
                            onClick={() => eliminarSeccion(s._rid)}
                            className="p-2 rounded-lg hover:bg-red-50 self-end"
                            title="Eliminar sección"
                            data-testid={`seccion-del-${s._rid}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {menuSecciones.some(s => (s.precio_extra || 0) > 0) && (
                      <div className="flex justify-end p-2 rounded-xl" style={{ background: "var(--brand-primary-soft)" }}>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-primary-dark)" }}>Total extras del menú</p>
                          <p className="font-display text-lg" style={{ color: "var(--brand-primary-dark)" }}>
                            ${menuSecciones.reduce((sum, sec) => sum + (parseFloat(sec.precio_extra) || 0), 0).toLocaleString("es-MX")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Bloque>
          )}

          {(tab === "montaje") && (
            <Bloque titulo="Montaje y decoración">
              <CampoArea label="Tema visual" value={draft.tema} onChange={(v) => { setDraft({ ...draft, tema: v }); guardarCampo("tema", v); }} />
              <CampoArea label="Colorimetría" value={draft.colorimetria} onChange={(v) => { setDraft({ ...draft, colorimetria: v }); guardarCampo("colorimetria", v); }} />
              <CampoArea label="Distribución de mesas" value={draft.mesas} onChange={(v) => { setDraft({ ...draft, mesas: v }); guardarCampo("mesas", v); }} multiline />
              <CampoArea label="Mobiliario y vajilla" value={draft.vajilla} onChange={(v) => { setDraft({ ...draft, vajilla: v }); guardarCampo("vajilla", v); }} multiline />
              <CampoArea label="Decoración" value={draft.decoracion} onChange={(v) => { setDraft({ ...draft, decoracion: v }); guardarCampo("decoracion", v); }} multiline />
            </Bloque>
          )}

          {(tab === "itinerario" || tab === "itinerario_od" || tab === "orden_maestra") && (
            <ItinerarioEditor
              itinerario={itinerario}
              ordenMaestra={ordenMaestra}
              evento={evento}
              onChange={(nuevo) => guardarCampo("itinerario", nuevo)}
              onIrOrdenMaestra={() => setTab("orden_maestra")}
              itinerarioRef={itinerarioRef}
              dirtyItinerarioRef={dirtyItinerarioRef}
            />
          )}

          {(tab === "orden_dia" || tab === "itinerario_od") && (
            <>
              <ItinerarioVinculadoOM eventoId={id} />
              <div className="mt-4">
                <OrdenDelDiaTab eventoId={id} />
              </div>
            </>
          )}

          {tab === "orden_maestra" && (
            <OrdenMaestraTab eventoId={id} evento={evento} />
          )}

          {(tab === "ordenes_trabajo") && (
            <OrdenesTrabajoTab eventoId={id} />
          )}

          {tab === "produccion" && (
            <ProduccionTab eventoId={id} />
          )}

          {tab === "cocina_chef" && (
            <CocinaChefTab
              evento={evento}
              eventoId={id}
              menuAsignado={evento?.menu_asignado}
            />
          )}

          {(tab === "documentos") && (
            <DocumentosTab evento={evento} onIA={ejecutarIA} iaLoading={iaLoading} />
          )}

          {tab === "pagos" && (
            <Bloque
              titulo="Pagos del cliente"
              testid="pagos-tab"
              action={<button onClick={() => setShowPagoQuick(true)} className="eo-btn eo-btn-primary" data-testid="btn-registrar-pago-tab"><CreditCard className="w-4 h-4" /> Registrar pago</button>}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="pagos-resumen">
                <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
                  <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Total contratado</p>
                  <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>${(evento.total || 0).toLocaleString("es-MX")}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: "var(--brand-success-soft)" }}>
                  <p className="text-[10px] uppercase" style={{ color: "var(--brand-success)" }}>Pagado ({pctPago}%)</p>
                  <p className="font-display text-lg" style={{ color: "var(--brand-success)" }}>${totalPagado.toLocaleString("es-MX")}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: saldo > 0 ? "var(--brand-warning-soft)" : "var(--brand-success-soft)" }}>
                  <p className="text-[10px] uppercase" style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>Saldo cliente</p>
                  <p className="font-display text-lg" style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>${saldo.toLocaleString("es-MX")}</p>
                </div>
              </div>
              {pagos.length === 0 ? (
                <EmptyBlock icon={CreditCard} msg="Aún no hay pagos registrados para este cliente" />
              ) : (
                <div className="space-y-1.5">
                  {pagos.map(p => (
                    <div key={p.pago_id} className="flex items-center gap-2 p-3 rounded-xl" style={{ background: p.cancelado ? "var(--brand-danger-soft)" : "var(--brand-success-soft)" }} data-testid={`pago-row-tab-${p.pago_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>
                          {p.fecha_pago || (p.created_at || "").substring(0, 10)}
                          {p.cancelado ? <span className="ml-2 text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-danger)" }}>· cancelado</span> : null}
                        </p>
                        <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{p.metodo_pago || p.metodo || "—"} · {p.tipo || "anticipo"}</p>
                      </div>
                      <span className="font-display text-lg" style={{ color: p.cancelado ? "var(--brand-danger)" : "var(--brand-success)" }}>
                        ${(p.monto_pagado || 0).toLocaleString("es-MX")}
                      </span>
                      <button onClick={() => setPagoVer(p)} className="p-2 rounded-lg hover:bg-white/60" title="Ver pago" data-testid={`btn-ver-pago-tab-${p.pago_id}`}>
                        <Eye className="w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
                      </button>
                      {!p.cancelado && (
                        <button onClick={() => abrirEditarPago(p)} className="p-2 rounded-lg hover:bg-white/60" title="Editar pago" data-testid={`btn-editar-pago-tab-${p.pago_id}`}>
                          <Edit className="w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
                        </button>
                      )}
                      {!p.cancelado && (
                        <button onClick={() => cancelarPago(p)} className="p-2 rounded-lg hover:bg-white/60" title="Cancelar pago" data-testid={`btn-cancelar-pago-tab-${p.pago_id}`}>
                          <X className="w-4 h-4" style={{ color: "var(--brand-danger)" }} />
                        </button>
                      )}
                      <button onClick={() => eliminarPago(p)} className="p-2 rounded-lg hover:bg-white/60" title="Eliminar pago" data-testid={`btn-eliminar-pago-tab-${p.pago_id}`}>
                        <Trash2 className="w-4 h-4" style={{ color: "var(--brand-danger)" }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] mt-3" style={{ color: "var(--brand-text-muted)" }}>
                Mismo historial que aparece en Finanzas. Los cambios se sincronizan automáticamente.
              </p>
            </Bloque>
          )}

          {tab === "finanzas" && (
            <FinanzasTab
              evento={evento}
              pagos={pagos}
              totalPagado={totalPagado}
              saldo={saldo}
              pctPago={pctPago}
              navigate={navigate}
              onRegistrarPago={() => setShowPagoQuick(true)}
              onCancelarPago={cancelarPago}
              onEliminarPago={eliminarPago}
              onVerPago={setPagoVer}
              onEditarPago={abrirEditarPago}
              contrato={contrato}
              cotizacion={cotizacion}
              resumenFinanciero={resumenFinanciero}
              personalAsignados={personalAsignados}
              proveedoresAsignados={proveedoresAsignados}
              comprasEvento={comprasEvento}
              egresosEvento={egresosEvento}
              ordenMaestra={ordenMaestra}
              produccionEvento={produccionEvento}
              onFinalizarEvento={async () => {
                if (evento.estatus === "finalizado") { toast.info("El evento ya está finalizado."); return; }
                if (!window.confirm("¿Marcar este evento como Finalizado? Podrás revisar cierre después.")) return;
                setFinalizando(true);
                try {
                  await guardarCampo("estatus", "finalizado");
                  toast.success("Evento marcado como Finalizado");
                } finally { setFinalizando(false); }
              }}
              finalizando={finalizando}
            />
          )}

          {(tab === "notas") && (
            <Bloque titulo="Notas y observaciones">
              <div className="flex gap-2 mb-3">
                <input value={nuevaNota} onChange={(e) => setNuevaNota(e.target.value)} placeholder="Escribe una nota..." className="eo-input" onKeyDown={(e) => e.key === "Enter" && agregarNota()} data-testid="input-nota" />
                <button onClick={agregarNota} className="eo-btn eo-btn-primary" data-testid="btn-agregar-nota"><Plus className="w-4 h-4" /> Agregar</button>
              </div>
              {notas.length === 0 ? <EmptyBlock icon={FileText} msg="Sin notas todavía" /> : (
                <div className="space-y-2">
                  {notas.slice().reverse().map((n, idx) => {
                    const realIdx = notas.length - 1 - idx;
                    return (
                      <div key={realIdx} className="p-3 rounded-xl flex items-start gap-3" style={{ background: "var(--brand-primary-soft)" }}>
                        <div className="flex-1">
                          <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>
                            {new Date(n.fecha).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })} · {n.autor || "—"}
                          </p>
                          <p className="text-sm mt-1" style={{ color: "var(--brand-text)" }}>{n.texto}</p>
                        </div>
                        <button onClick={() => eliminarNota(realIdx)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Bloque>
          )}

          {tab === "flujo_operativo" && (
            <FlujoOperativoPanel evento={evento} onRefresh={() => {}} />
          )}
        </div>

        {/* SIDEBAR DERECHO (1/3) — Acciones rápidas, Próxima actividad, Checklist */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          {/* SEMÁFORO FINANCIERO */}
          {estadoFin && (
            <div className="eo-card overflow-hidden" data-testid="semaforo-card">
              <div className="px-4 py-3 flex items-center gap-3" style={{
                background: estadoFin.semaforo === "verde" ? "var(--brand-success-soft)"
                  : estadoFin.semaforo === "amarillo" ? "var(--brand-warning-soft)"
                  : estadoFin.semaforo === "rojo" ? "var(--brand-danger-soft)"
                  : "var(--brand-surface-muted)",
              }}>
                <div className="w-3 h-3 rounded-full shrink-0" style={{
                  background: estadoFin.semaforo === "verde" ? "var(--brand-success)"
                    : estadoFin.semaforo === "amarillo" ? "var(--brand-warning)"
                    : estadoFin.semaforo === "rojo" ? "var(--brand-danger)"
                    : "var(--brand-text-muted)",
                }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Estado financiero</p>
                  <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>{estadoFin.mensaje}</p>
                </div>
              </div>
              <div className="px-4 py-3 grid grid-cols-3 gap-2 text-center text-xs">
                <div><p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Total</p><p className="font-medium" style={{ color: "var(--brand-text)" }}>${estadoFin.total.toLocaleString("es-MX")}</p></div>
                <div><p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Pagado</p><p className="font-medium" style={{ color: "var(--brand-success)" }}>${estadoFin.pagado.toLocaleString("es-MX")}</p></div>
                <div><p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Saldo</p><p className="font-medium" style={{ color: "var(--brand-primary)" }}>${estadoFin.saldo.toLocaleString("es-MX")}</p></div>
              </div>
              {estadoFin.dias_para_evento !== null && (
                <div className="px-4 py-2 text-center text-xs" style={{ borderTop: "1px solid var(--brand-border)", background: "var(--brand-surface-muted)" }}>
                  <span style={{ color: "var(--brand-text-muted)" }}>
                    {estadoFin.dias_para_evento >= 0 ? `Faltan ${estadoFin.dias_para_evento}d para el evento` : `Evento hace ${-estadoFin.dias_para_evento}d`}
                  </span>
                </div>
              )}
            </div>
          )}
          {/* Acciones rápidas */}
          <div className="eo-card p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--brand-text-muted)" }}>Acciones rápidas</p>
            {evento.estatus === "cotizado" && (
              <button onClick={() => setShowConfirmar(true)} className="w-full mb-3 px-3 py-3 rounded-xl text-sm font-medium text-white transition" style={{ background: `linear-gradient(135deg, var(--brand-success), #1F6F44)`, boxShadow: "0 4px 12px rgba(46,143,90,0.3)" }} data-testid="btn-confirmar-evento">
                ✓ Confirmar evento · Registrar anticipo
              </button>
            )}
            <div className="space-y-1.5">
              {ACCIONES_RAPIDAS.map(a => (
                <button
                  key={a.key}
                  onClick={async () => {
                    if (a.whatsapp) handleWhatsapp();
                    else if (a.doc) ejecutarIA(a.key);
                    else if (a.accion === "produccion") {
                      try {
                        const { data } = await axios.post(`${API}/eventos/${id}/generar-produccion`);
                        toast.success(data.ya_existia ? "Producción ya existente" : "Producción generada");
                        navigate("/panel/produccion");
                      } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar"); }
                    }
                    else if (a.key === "modo_evento") navigate(`/panel/modo-evento/${id}`);
                    else if (a.ruta) navigate(a.ruta);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition border"
                  style={{ background: "var(--brand-surface-card)", borderColor: "var(--brand-border)", color: "var(--brand-text)" }}
                  data-testid={`accion-${a.key}`}
                >
                  <a.icon className="w-4 h-4 shrink-0" style={{ color: "var(--brand-primary)" }} />
                  <span className="flex-1 text-left">{a.label}</span>
                  {iaLoading === a.key && <Loader2 className="w-4 h-4 animate-spin" />}
                </button>
              ))}
            </div>
          </div>

          {/* Próxima actividad */}
          <div className="eo-card overflow-hidden">
            <div className="px-5 py-4" style={{ background: "var(--brand-primary-soft)" }}>
              <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-primary)" }}>Próxima actividad</p>
              {proximaActividad ? (
                <>
                  <p className="font-display text-2xl mt-1" style={{ color: "var(--brand-text)" }}>{proximaActividad.hora || "—"}</p>
                  <p className="text-sm" style={{ color: "var(--brand-text)" }}>{proximaActividad.actividad}</p>
                </>
              ) : (
                <p className="text-sm mt-1" style={{ color: "var(--brand-text-muted)" }}>Sin actividades programadas. Agrega tu itinerario.</p>
              )}
            </div>
            <button onClick={() => navigate(`/panel/modo-evento/${id}`)} className="w-full px-5 py-3.5 text-sm font-medium text-white transition" style={{ background: "var(--brand-primary)", borderTop: "1px solid var(--brand-border)" }} onMouseEnter={(e) => e.currentTarget.style.background = "var(--brand-primary-dark)"} onMouseLeave={(e) => e.currentTarget.style.background = "var(--brand-primary)"} data-testid="btn-ir-modo-evento">
              Ir a Modo Evento
            </button>
          </div>

          {/* Checklist */}
          <div className="eo-card p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Checklist general</p>
              <span className="text-xs font-medium" style={{ color: "var(--brand-text-muted)" }}>{checklistDone} / {checklist.length}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background: "var(--brand-surface-muted)" }}>
              <div className="h-full rounded-full" style={{ width: `${(checklistDone / checklist.length) * 100}%`, background: `linear-gradient(90deg, var(--brand-primary), var(--brand-success))` }} />
            </div>
            <div className="space-y-1">
              {checklist.slice(0, 6).map(c => (
                <button key={c.id} onClick={() => toggleChecklist(c.id)} className="w-full flex items-center gap-2 py-1.5 text-sm text-left" data-testid={`check-${c.id}`}>
                  {c.hecho ? <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "var(--brand-success)" }} /> : <Circle className="w-4 h-4 shrink-0" style={{ color: "var(--brand-text-muted)" }} />}
                  <span style={{ color: c.hecho ? "var(--brand-text-muted)" : "var(--brand-text)", textDecoration: c.hecho ? "line-through" : "none" }}>{c.label}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setTab("itinerario_od")} className="w-full mt-3 text-xs font-medium" style={{ color: "var(--brand-primary)" }}>Ver checklist completo →</button>
          </div>
        </aside>
      </div>

      {/* ====== MODALES ====== */}
      <SelectorModal
        abierto={showVincularCliente} onClose={() => setShowVincularCliente(false)}
        titulo="Vincular cliente al evento"
        items={clientesCatalogo}
        yaSeleccionados={evento?.cliente_id ? [evento.cliente_id] : []}
        getId={(c) => c.cliente_id}
        getNombre={(c) => c.nombre}
        getSubtitulo={(c) => c.tipo_evento || c.email}
        getMeta={(c) => c.whatsapp || c.telefono}
        multi={false}
        onConfirmar={vincularCliente}
        emptyMsg="No tienes clientes registrados. Crea uno en Clientes."
        ctaCrear="Nuevo" onCrearNuevo={() => navigate("/panel/clientes")}
      />
      <SelectorModal
        abierto={showPersonal} onClose={() => setShowPersonal(false)}
        titulo="Agregar personal al evento"
        items={personalCatalogo}
        yaSeleccionados={personalIdsAsignados}
        getId={(p) => p.personal_id}
        getNombre={(p) => p.nombre}
        getSubtitulo={(p) => p.rol || "Personal"}
        getMeta={(p) => p.whatsapp}
        onConfirmar={onConfirmarPersonal}
        emptyMsg="No tienes personal cargado. Ve a Personal para agregarlos."
        ctaCrear="Nuevo" onCrearNuevo={() => navigate("/panel/personal")}
      />
      <SelectorModal
        abierto={showProveedores} onClose={() => setShowProveedores(false)}
        titulo="Agregar proveedor"
        items={proveedoresCatalogo}
        yaSeleccionados={proveedoresIdsAsignados}
        getId={(p) => p.proveedor_id}
        getNombre={(p) => p.nombre}
        getSubtitulo={(p) => p.categoria || p.servicios}
        getMeta={(p) => p.whatsapp}
        onConfirmar={onConfirmarProveedores}
        emptyMsg="No tienes proveedores cargados. Ve a Proveedores para agregarlos."
        ctaCrear="Nuevo" onCrearNuevo={() => navigate("/panel/proveedores")}
      />
      {/* Iter125 · P1 Fase 5 · Modales de edición de relación (Equipo) */}
      <EquipoEditPersonalModal
        abierto={!!equipoEditPersonal}
        draft={equipoEditPersonal}
        onClose={() => setEquipoEditPersonal(null)}
        onSave={async (patch) => {
          if (!equipoEditPersonal) return;
          await updatePersonalRelacion(equipoEditPersonal.personal_id, patch);
          setEquipoEditPersonal(null);
          toast.success("Relación actualizada");
        }}
      />
      <EquipoEditProveedorModal
        abierto={!!equipoEditProveedor}
        draft={equipoEditProveedor}
        onClose={() => setEquipoEditProveedor(null)}
        onSave={async (patch) => {
          if (!equipoEditProveedor) return;
          await updateProveedorRelacion(equipoEditProveedor.proveedor_id, patch);
          setEquipoEditProveedor(null);
          toast.success("Relación actualizada");
        }}
      />
      <EquipoTareasModal
        abierto={!!equipoTareasPara}
        data={equipoTareasPara}
        onClose={() => setEquipoTareasPara(null)}
      />
      <SelectorModal
        abierto={showServicios} onClose={() => setShowServicios(false)}
        titulo="Agregar servicio / extra"
        items={serviciosCatalogo}
        yaSeleccionados={serviciosIdsAsignados}
        getId={(s) => s.servicio_id || s.id}
        getNombre={(s) => s.nombre || s.titulo}
        getSubtitulo={(s) => s.categoria || s.descripcion?.substring(0, 60)}
        getMeta={(s) => s.precio ? `$${s.precio}` : ""}
        onConfirmar={onConfirmarServicios}
        emptyMsg="No tienes servicios cargados."
        ctaCrear="Nuevo" onCrearNuevo={() => navigate("/panel/servicios-rapidos")}
      />
      <SelectorModal
        abierto={showMenu} onClose={() => setShowMenu(false)}
        titulo="Elegir menú del catálogo"
        items={menusCatalogo}
        yaSeleccionados={menuAsignado.id ? [menuAsignado.id] : []}
        getId={(m) => m.menu_id || m.id}
        getNombre={(m) => m.nombre}
        getSubtitulo={(m) => m.descripcion || `${(m.platillos || []).length} platillos`}
        getMeta={(m) => m.precio_por_persona ? `$${m.precio_por_persona}/p` : ""}
        multi={false}
        onConfirmar={async (ids) => {
          const m = menusCatalogo.find(x => (x.menu_id || x.id) === ids[0]);
          if (!m) return;
          const personas = (parseInt(evento?.adultos) || 0) + (parseInt(evento?.ninos) || 0);
          const precioPP = parseFloat(m.precio_por_persona) || 0;
          await guardarCampo("menu_asignado", {
            id: m.menu_id || m.id, nombre: m.nombre,
            precio_por_persona: precioPP,
            botanas: m.botanas || "", plato_fuerte: m.plato_fuerte || "",
            bebidas: m.bebidas || "", postres: m.postres || "",
          });
          // Si hay precio por persona y personas > 0, recalcula el total automáticamente
          if (precioPP > 0 && personas > 0) {
            const nuevoTotal = precioPP * personas;
            await guardarCampo("total", nuevoTotal);
            toast.success(`Menú asignado · Total recalculado: $${nuevoTotal.toLocaleString("es-MX")}`);
          } else {
            toast.success("Menú asignado");
          }
        }}
        emptyMsg="No tienes menús cargados."
        ctaCrear="Nuevo menú" onCrearNuevo={() => navigate("/panel/menus")}
      />

      {/* Modal IA */}
      {showIaModal && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowIaModal(false)} data-testid="ia-modal">
          <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--brand-border)" }}>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: "var(--brand-primary)" }} />
                <h3 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>Documento generado</h3>
              </div>
              <button onClick={() => setShowIaModal(false)} className="p-2 rounded-lg hover:bg-[var(--brand-surface-muted)]"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {iaLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--brand-primary)" }} />
                  <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Generando documento...</p>
                </div>
              ) : iaError ? (
                <div className="py-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ background: "var(--brand-danger-soft)" }}>
                    <Bell className="w-7 h-7" style={{ color: "var(--brand-danger)" }} />
                  </div>
                  <h4 className="font-display text-xl mb-2" style={{ color: "var(--brand-text)" }}>No se pudo generar</h4>
                  <p className="text-sm max-w-md mx-auto" style={{ color: "var(--brand-text-muted)" }}>{iaError.msg}</p>
                </div>
              ) : iaResult ? (
                <div className="whitespace-pre-wrap text-sm" style={{ color: "var(--brand-text)" }}>{iaResult.texto}</div>
              ) : null}
            </div>
            {iaResult && (
              <div className="p-4 flex gap-2 justify-end" style={{ borderTop: "1px solid var(--brand-border)" }}>
                <button onClick={() => { navigator.clipboard.writeText(iaResult.texto); toast.success("Copiado"); }} className="eo-btn eo-btn-outline">Copiar</button>
                <button onClick={() => setShowIaModal(false)} className="eo-btn eo-btn-primary">Cerrar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal REGISTRAR PAGO RÁPIDO (desde el expediente, vincula cliente+evento). Iter105e */}
      {showPagoQuick && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPagoQuick(false)} data-testid="modal-pago-quick">
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-5" style={{ background: `linear-gradient(135deg, var(--brand-primary-soft), var(--brand-accent-soft))` }}>
              <p className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>Registrar pago</p>
              <h3 className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>{cliente?.nombre || "Cliente"}</h3>
              <p className="text-xs mt-1" style={{ color: "var(--brand-text-muted)" }}>Evento: {evento?.nombre || "—"} · Saldo actual: ${(evento?.saldo || 0).toLocaleString("es-MX")}</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Monto *</label>
                <input type="number" min="0" value={pagoQuick.monto} onChange={(e) => setPagoQuick({ ...pagoQuick, monto: e.target.value })} className="eo-input text-lg font-medium" data-testid="input-pago-monto" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Tipo</label>
                  <select value={pagoQuick.tipo} onChange={(e) => setPagoQuick({ ...pagoQuick, tipo: e.target.value })} className="eo-input" data-testid="select-pago-tipo">
                    <option value="anticipo">Anticipo</option>
                    <option value="parcial">Pago parcial</option>
                    <option value="liquidacion">Liquidación</option>
                    <option value="adicional">Adicional</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Método</label>
                  <select value={pagoQuick.metodo} onChange={(e) => setPagoQuick({ ...pagoQuick, metodo: e.target.value })} className="eo-input" data-testid="select-pago-metodo">
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="deposito">Depósito</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Fecha</label>
                <input type="date" value={pagoQuick.fecha} onChange={(e) => setPagoQuick({ ...pagoQuick, fecha: e.target.value })} className="eo-input" data-testid="input-pago-fecha" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Notas (opcional)</label>
                <input value={pagoQuick.notas} onChange={(e) => setPagoQuick({ ...pagoQuick, notas: e.target.value })} placeholder="Referencia, observaciones…" className="eo-input" data-testid="input-pago-notas" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowPagoQuick(false)} className="eo-btn eo-btn-outline flex-1" data-testid="btn-pago-cancelar">Cancelar</button>
                <button onClick={registrarPagoQuick} className="eo-btn eo-btn-primary flex-1" data-testid="btn-pago-guardar"><Save className="w-4 h-4" /> Registrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iter132 · Modal VER PAGO (solo lectura) */}
      {pagoVer && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setPagoVer(null)} data-testid="modal-ver-pago">
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-5 flex items-start justify-between" style={{ background: `linear-gradient(135deg, var(--brand-primary-soft), var(--brand-accent-soft))` }}>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>Detalle del pago</p>
                <h3 className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>${(pagoVer.monto_pagado || pagoVer.monto || 0).toLocaleString("es-MX")}</h3>
                {pagoVer.cancelado ? (
                  <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--brand-danger)" }}>Cancelado</p>
                ) : null}
              </div>
              <button onClick={() => setPagoVer(null)} className="p-2 rounded-lg hover:bg-white/60" data-testid="btn-cerrar-ver-pago"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>Fecha</p><p>{pagoVer.fecha_pago || (pagoVer.created_at || "").substring(0, 10) || "—"}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>Tipo</p><p>{pagoVer.tipo || "anticipo"}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>Método</p><p>{pagoVer.metodo_pago || pagoVer.metodo || "—"}</p></div>
                <div><p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>Estatus</p><p>{pagoVer.estatus || "—"}</p></div>
              </div>
              {pagoVer.notas ? (
                <div><p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>Notas</p><p className="whitespace-pre-wrap">{pagoVer.notas}</p></div>
              ) : null}
              {pagoVer.comprobante_url ? (
                <div><p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>Comprobante</p><a href={pagoVer.comprobante_url} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: "var(--brand-primary)" }}>Abrir comprobante</a></div>
              ) : null}
              {pagoVer.cancelado ? (
                <div><p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-danger)" }}>Motivo cancelación</p><p>{pagoVer.motivo_cancelacion || "—"}</p></div>
              ) : null}
              <p className="text-[10px] pt-2" style={{ color: "var(--brand-text-muted)" }}>ID: {pagoVer.pago_id}</p>
            </div>
            <div className="p-4 flex gap-2 justify-end" style={{ borderTop: "1px solid var(--brand-border)" }}>
              {!pagoVer.cancelado && (
                <button onClick={() => { const p = pagoVer; setPagoVer(null); abrirEditarPago(p); }} className="eo-btn eo-btn-outline" data-testid="btn-ver-a-editar"><Edit className="w-4 h-4" /> Editar</button>
              )}
              <button onClick={() => setPagoVer(null)} className="eo-btn eo-btn-primary" data-testid="btn-cerrar-ver-pago-b">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Iter132 · Modal EDITAR PAGO — reutiliza PUT /api/pagos/{id}; recalcula Cobrado/Saldo vía refrescarEvento */}
      {pagoEditar && pagoEditarDraft && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => { if (!guardandoPago) { setPagoEditar(null); setPagoEditarDraft(null); } }} data-testid="modal-editar-pago">
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-5" style={{ background: `linear-gradient(135deg, var(--brand-primary-soft), var(--brand-accent-soft))` }}>
              <p className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>Editar pago</p>
              <h3 className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>${Number(pagoEditarDraft.monto || 0).toLocaleString("es-MX")}</h3>
              <p className="text-xs mt-1" style={{ color: "var(--brand-text-muted)" }}>ID: {pagoEditar.pago_id}</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Monto *</label>
                <input type="number" min="0" value={pagoEditarDraft.monto} onChange={(e) => setPagoEditarDraft({ ...pagoEditarDraft, monto: e.target.value })} className="eo-input text-lg font-medium" data-testid="input-editar-pago-monto" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Tipo</label>
                  <select value={pagoEditarDraft.tipo} onChange={(e) => setPagoEditarDraft({ ...pagoEditarDraft, tipo: e.target.value })} className="eo-input" data-testid="select-editar-pago-tipo">
                    <option value="anticipo">Anticipo</option>
                    <option value="parcial">Pago parcial</option>
                    <option value="liquidacion">Liquidación</option>
                    <option value="adicional">Adicional</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Método</label>
                  <select value={pagoEditarDraft.metodo} onChange={(e) => setPagoEditarDraft({ ...pagoEditarDraft, metodo: e.target.value })} className="eo-input" data-testid="select-editar-pago-metodo">
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="deposito">Depósito</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Fecha</label>
                <input type="date" value={pagoEditarDraft.fecha_pago || ""} onChange={(e) => setPagoEditarDraft({ ...pagoEditarDraft, fecha_pago: e.target.value })} className="eo-input" data-testid="input-editar-pago-fecha" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Notas</label>
                <input value={pagoEditarDraft.notas || ""} onChange={(e) => setPagoEditarDraft({ ...pagoEditarDraft, notas: e.target.value })} placeholder="Referencia, observaciones…" className="eo-input" data-testid="input-editar-pago-notas" />
              </div>
              <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>Al guardar se recalcularán automáticamente <b>Cobrado</b> y <b>Saldo</b> del evento.</p>
              <div className="flex gap-2 pt-2">
                <button onClick={() => { setPagoEditar(null); setPagoEditarDraft(null); }} disabled={guardandoPago} className="eo-btn eo-btn-outline flex-1" data-testid="btn-editar-pago-cancelar">Cancelar</button>
                <button onClick={guardarEdicionPago} disabled={guardandoPago} className="eo-btn eo-btn-primary flex-1" data-testid="btn-editar-pago-guardar"><Save className="w-4 h-4" /> {guardandoPago ? "Guardando…" : "Guardar"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iter132 · Modal REGISTRAR PAGO A PROVEEDOR — usa POST /api/egresos (tipo=proveedor).
          Cada pago es un egreso independiente: NO sobrescribe pagos anteriores. */}
      {pagoProveedor && pagoProvDraft && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => { if (!guardandoPagoProv) { setPagoProveedor(null); setPagoProvDraft(null); } }} data-testid="modal-pago-proveedor">
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-5" style={{ background: `linear-gradient(135deg, var(--brand-primary-soft), var(--brand-accent-soft))` }}>
              <p className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>Pago a proveedor</p>
              <h3 className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>{pagoProveedor.nombre || "Proveedor"}</h3>
              {(() => {
                const pagosPrev = egresosDeProveedor(pagoProveedor.proveedor_id);
                const pagado = pagosPrev.reduce((s, e) => s + (Number(e.monto) || 0), 0);
                const saldo = Math.max(Number(pagoProveedor.costo || 0) - pagado, 0);
                return (
                  <p className="text-xs mt-1" style={{ color: "var(--brand-text-muted)" }}>
                    Costo acordado: <b>${Number(pagoProveedor.costo || 0).toLocaleString("es-MX")}</b> · Pagado: <b>${pagado.toLocaleString("es-MX")}</b> · Saldo: <b>${saldo.toLocaleString("es-MX")}</b>
                  </p>
                );
              })()}
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Tipo de pago</label>
                <select value={pagoProvDraft.tipo_pago} onChange={(e) => setPagoProvDraft({ ...pagoProvDraft, tipo_pago: e.target.value })} className="eo-input" data-testid="select-tipo-pago-prov">
                  <option value="anticipo">Anticipo</option>
                  <option value="parcial">Pago parcial</option>
                  <option value="liquidacion">Liquidación</option>
                  <option value="adicional">Adicional</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Monto *</label>
                <input type="number" min="0" value={pagoProvDraft.monto} onChange={(e) => setPagoProvDraft({ ...pagoProvDraft, monto: e.target.value })} className="eo-input text-lg font-medium" data-testid="input-monto-pago-prov" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Fecha</label>
                  <input type="date" value={pagoProvDraft.fecha_pago} onChange={(e) => setPagoProvDraft({ ...pagoProvDraft, fecha_pago: e.target.value })} className="eo-input" data-testid="input-fecha-pago-prov" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Método</label>
                  <select value={pagoProvDraft.metodo_pago} onChange={(e) => setPagoProvDraft({ ...pagoProvDraft, metodo_pago: e.target.value })} className="eo-input" data-testid="select-metodo-pago-prov">
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="deposito">Depósito</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Concepto / Referencia</label>
                <input value={pagoProvDraft.concepto} onChange={(e) => setPagoProvDraft({ ...pagoProvDraft, concepto: e.target.value })} placeholder="Opcional — se autocompleta con el tipo de pago" className="eo-input" data-testid="input-concepto-pago-prov" />
              </div>
              <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>Cada pago crea un egreso independiente vinculado al proveedor y al evento. No sobrescribe los anteriores.</p>
              <div className="flex gap-2 pt-2">
                <button onClick={() => { setPagoProveedor(null); setPagoProvDraft(null); }} disabled={guardandoPagoProv} className="eo-btn eo-btn-outline flex-1" data-testid="btn-pago-prov-cancelar">Cancelar</button>
                <button onClick={registrarPagoProveedor} disabled={guardandoPagoProv} className="eo-btn eo-btn-primary flex-1" data-testid="btn-pago-prov-guardar"><Save className="w-4 h-4" /> {guardandoPagoProv ? "Guardando…" : "Registrar pago"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal CONFIRMAR EVENTO (registrar anticipo) */}
      {showConfirmar && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowConfirmar(false)} data-testid="modal-confirmar">
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-5" style={{ background: `linear-gradient(135deg, var(--brand-success-soft), var(--brand-accent-soft))` }}>
              <p className="text-[10px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-success)" }}>Cerrar venta</p>
              <h3 className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>Confirmar evento</h3>
              <p className="text-xs mt-1" style={{ color: "var(--brand-text-muted)" }}>Se registrará el anticipo, el evento pasará a Contratado y se generará la producción.</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Monto del anticipo *</label>
                <input type="number" value={confirmarData.monto} onChange={(e) => setConfirmarData({ ...confirmarData, monto: parseFloat(e.target.value) || 0 })} className="eo-input text-lg font-medium" data-testid="input-monto-anticipo" />
                <p className="text-[11px] mt-1" style={{ color: "var(--brand-text-muted)" }}>Sugerido: ${(evento?.anticipo_sugerido || Math.max(5000, (evento?.total || 0) * 0.5)).toLocaleString("es-MX")}</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Método de pago</label>
                <select value={confirmarData.metodo} onChange={(e) => setConfirmarData({ ...confirmarData, metodo: e.target.value })} className="eo-input" data-testid="select-metodo">
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="deposito">Depósito</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Referencia (opcional)</label>
                <input value={confirmarData.referencia} onChange={(e) => setConfirmarData({ ...confirmarData, referencia: e.target.value })} className="eo-input" placeholder="Folio, banco, etc." data-testid="input-referencia" />
              </div>
            </div>
            <div className="p-4 flex gap-2 justify-end" style={{ borderTop: "1px solid var(--brand-border)" }}>
              <button onClick={() => setShowConfirmar(false)} className="eo-btn eo-btn-outline" data-testid="btn-cancelar-confirmar">Cancelar</button>
              <button onClick={handleConfirmarEvento} disabled={confirmando} className="eo-btn eo-btn-primary" data-testid="btn-procesar-confirmar">
                {confirmando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {confirmando ? "Procesando..." : "Confirmar y registrar anticipo"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ==================== SUB-COMPONENTES ====================
const Cifra = ({ label, value, color }) => (
  <div className="text-center">
    <p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>{label}</p>
    <p className="font-display text-xl md:text-2xl mt-0.5" style={{ color: color || "var(--brand-text)" }}>{value}</p>
  </div>
);

const Bloque = ({ titulo, action, children, testid }) => (
  <div className="eo-card p-5 md:p-6 eo-animate-in" {...(testid ? { "data-testid": testid } : {})}>
    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
      <h3 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>{titulo}</h3>
      {action}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const BloqueMini = ({ label, valor }) => (
  <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: "var(--brand-surface-muted)" }}>
    <span className="text-xs uppercase tracking-[0.14em]" style={{ color: "var(--brand-text-muted)" }}>{label}</span>
    <span className="text-sm font-medium truncate ml-3" style={{ color: "var(--brand-text)" }}>{valor}</span>
  </div>
);

const EmptyBlock = ({ icon: Icon, msg }) => (
  <div className="py-8 text-center">
    <Icon className="w-10 h-10 mx-auto mb-2" style={{ color: "var(--brand-text-muted)", opacity: 0.4 }} />
    <p className="text-sm italic" style={{ color: "var(--brand-text-muted)" }}>{msg}</p>
  </div>
);

const CampoEdit = ({ label, value, onChange, edit, tipo }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--brand-text-muted)" }}>{label}</p>
    {edit ? (
      <input type={tipo || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="eo-input" />
    ) : (
      <p className="text-sm" style={{ color: "var(--brand-text)" }}>{value || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>Sin definir</span>}</p>
    )}
  </div>
);

const CampoArea = ({ label, value, onChange, multiline }) => {
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--brand-text-muted)" }}>{label}</p>
      {multiline ? (
        <textarea value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => val !== value && onChange(val)} rows={3} className="eo-input" />
      ) : (
        <input value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => val !== value && onChange(val)} className="eo-input" />
      )}
    </div>
  );
};

const ItinerarioEditor = ({ itinerario, ordenMaestra = [], evento = {}, onChange, onIrOrdenMaestra, itinerarioRef: externalItinerarioRef, dirtyItinerarioRef: externalDirtyRef }) => {
  const [lista, setLista] = useState(itinerario);
  // Iter132d · Fix "no me deja escribir / me borra": no re-sincronizar desde la prop
  // mientras el usuario está editando (dirty). Antes cada tecla disparaba PUT →
  // el useEffect re-inyectaba la respuesta del backend y sobrescribía el cursor.
  const dirtyRef = useRef(false);
  const listaRef = useRef(lista);
  // Iter132e · Refs expuestas al padre para incluir cambios pendientes en el
  // "Guardar cambios" del expediente y evitar la carrera con onBlur async.
  const syncExternal = (n, dirty) => {
    if (externalItinerarioRef) externalItinerarioRef.current = n;
    if (externalDirtyRef) externalDirtyRef.current = dirty;
  };
  useEffect(() => {
    if (!dirtyRef.current) {
      setLista(itinerario);
      listaRef.current = itinerario;
      syncExternal(itinerario, false);
    }
     
  }, [itinerario]);
  const add = () => {
    // Iter140 · Fix P0 · No persistir una fila vacía; solo actualizamos estado
    // local y marcamos dirty. El commit (onBlur) hará el PUT cuando el usuario
    // haya llenado hora/actividad. Filas totalmente vacías se descartan en commit.
    const n = [...(listaRef.current || lista), { _rid: `it_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, hora: "", actividad: "", responsable: "", ubicacion: "" }];
    setLista(n);
    listaRef.current = n;
    dirtyRef.current = true;
    syncExternal(n, true);
  };
  const update = (i, k, v) => {
    // Solo estado local. NO llamamos onChange en cada tecla para evitar el
    // ciclo PUT → useEffect → resetLista → cursor perdido / letras comidas.
    const base = listaRef.current || lista;
    const n = base.map((it, idx) => idx === i ? { ...it, [k]: v } : it);
    setLista(n);
    listaRef.current = n;
    dirtyRef.current = true;
    syncExternal(n, true);
  };
  const commit = () => {
    // Se dispara en onBlur. Usa el ref para NO caer en stale-closure.
    // Iter132g · NO reseteamos dirtyItinerarioRef aquí — el reset lo hace
    // handleGuardarEdit tras un PUT atómico exitoso. Esto evita la carrera
    // en la que "Guardar cambios" del expediente veía dirty=false por el
    // blur previo y omitía el itinerario del payload.
    // Iter140 · Antes de persistir, descartamos filas manuales totalmente vacías
    // (hora vacía Y actividad vacía). Evita que el botón "Agregar actividad
    // manual" acumule fantasmas en evento.itinerario.
    if (dirtyRef.current) {
      const limpio = (listaRef.current || []).filter(it => (it.hora || "").trim() || (it.actividad || "").trim());
      listaRef.current = limpio;
      setLista(limpio);
      syncExternal(limpio, true);
      onChange(limpio);
      dirtyRef.current = false;
    }
  };
  const remove = (i) => {
    const base = listaRef.current || lista;
    const n = base.filter((_, idx) => idx !== i);
    setLista(n); listaRef.current = n; dirtyRef.current = false; syncExternal(n, false); onChange(n);
  };
  const move = (i, dir) => {
    const base = listaRef.current || lista;
    const arr = [...base];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setLista(arr); listaRef.current = arr; dirtyRef.current = false; syncExternal(arr, false); onChange(arr);
  };

  // Iter125 · P1 Fase 6 · Filas heredadas de Orden Maestra (solo si tienen hora)
  // No se persisten en evento.itinerario — se leen frescas en cada render.
  const filasOM = useMemo(() => (ordenMaestra || [])
    .filter(o => (o.hora || "").trim() && (o.actividad || "").trim())
    .map(o => ({
      _rid: `om_${o.item_id}`,
      origen: "orden_maestra",
      item_id: o.item_id,
      hora: o.hora,
      hora_fin: o.hora_fin || "",
      actividad: o.actividad,
      responsable: o.responsable || "",
      ubicacion: evento.ubicacion || evento.direccion || "",
      estado: o.estado || "pendiente",
      descripcion: o.descripcion || "",
    })), [ordenMaestra, evento?.ubicacion, evento?.direccion]);

  // Iter132 · Cronograma: actividades del itinerario del evento (manuales).
  // Iter140 · Fix P0 · Mostramos también filas recién agregadas SIN hora para que
  // el usuario pueda llenarlas (antes filtrábamos por hora y quedaban invisibles,
  // por eso el botón "Agregar actividad manual" parecía no hacer nada).
  const filasManuales = lista
    .map((it, idx) => ({ ...it, origen: "manual", _idx: idx, _rid: it._rid || `manual-${idx}` }))
    .filter(it => (it.hora || "").trim() || (it.actividad || "").trim() || dirtyRef.current);
  const todas = [...filasOM, ...filasManuales].sort((a, b) => (a.hora || "99:99").localeCompare(b.hora || "99:99"));

  return (
    <Bloque
      titulo="Itinerario del evento"
      testid="itinerario-editor"
      action={<button onClick={add} className="eo-btn eo-btn-primary" data-testid="btn-add-itinerario"><Plus className="w-4 h-4" /> Agregar actividad manual</button>}
    >
      {todas.length === 0 ? (
        <EmptyBlock icon={Clock} msg="Sin actividades con hora. Agrega actividades manuales o define horas en Orden Maestra." />
      ) : (
        <>
          {/* Cronograma · Desktop: tabla (≥ md). Móvil: cards apiladas (< md). */}
          <div className="hidden md:block overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
            <table className="w-full text-xs">
              <thead style={{ background: "var(--brand-surface-muted)" }}>
                <tr>
                  <th className="text-left p-2 w-28">Hora</th>
                  <th className="text-left p-2">Actividad</th>
                  <th className="text-left p-2">Responsable</th>
                  <th className="text-left p-2">Ubicación</th>
                  <th className="text-left p-2">Detalle</th>
                  <th className="text-left p-2 w-32">Estado / Origen</th>
                  <th className="text-right p-2 w-28">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {todas.map(row => row.origen === "orden_maestra" ? (
                  <tr key={row._rid} className="border-t align-top" style={{ borderColor: "var(--brand-border)", background: "var(--brand-primary-soft)" }} data-testid={`iti-om-${row.item_id}`}>
                    <td className="p-2 font-mono whitespace-nowrap">{row.hora}{row.hora_fin ? <span className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}> – {row.hora_fin}</span> : null}</td>
                    <td className="p-2" style={{ color: "var(--brand-text)" }}>{row.actividad}</td>
                    <td className="p-2">{row.responsable || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                    <td className="p-2">{row.ubicacion || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                    <td className="p-2 whitespace-pre-wrap">{row.descripcion || <span className="italic" style={{ color: "var(--brand-text-muted)" }}>—</span>}</td>
                    <td className="p-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full mr-1" style={{ background: "var(--brand-primary)", color: "#fff" }}>Orden Maestra</span>
                      <span className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{row.estado}</span>
                    </td>
                    <td className="p-2 text-right">
                      <button onClick={onIrOrdenMaestra} className="text-[11px] underline" style={{ color: "var(--brand-primary)" }} data-testid={`btn-iti-editar-om-${row.item_id}`}>Editar en OM →</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={row._rid} className="border-t align-top" style={{ borderColor: "var(--brand-border)" }} data-testid={`iti-manual-${row._idx}`}>
                    <td className="p-1">
                      <input type="time" value={row.hora || ""} onChange={(e) => update(row._idx, "hora", e.target.value)} onBlur={commit} className="eo-input text-center text-xs w-24" />
                      <input type="time" value={row.hora_fin || ""} onChange={(e) => update(row._idx, "hora_fin", e.target.value)} onBlur={commit} className="eo-input text-center text-[10px] w-24 mt-1" placeholder="fin" title="Hora fin (opcional)" />
                    </td>
                    <td className="p-1">
                      <input value={row.actividad || ""} onChange={(e) => update(row._idx, "actividad", e.target.value)} onBlur={commit} placeholder="Actividad" className="eo-input w-full text-xs" />
                    </td>
                    <td className="p-1">
                      <input value={row.responsable || ""} onChange={(e) => update(row._idx, "responsable", e.target.value)} onBlur={commit} placeholder="Responsable" className="eo-input w-full text-xs" />
                    </td>
                    <td className="p-1">
                      <input value={row.ubicacion || ""} onChange={(e) => update(row._idx, "ubicacion", e.target.value)} onBlur={commit} placeholder="Ubicación" className="eo-input w-full text-xs" data-testid={`iti-manual-ubicacion-${row._idx}`} />
                    </td>
                    <td className="p-1">
                      <textarea value={row.descripcion || ""} onChange={(e) => update(row._idx, "descripcion", e.target.value)} onBlur={commit} placeholder="Detalle / instrucciones" rows={2} className="eo-input w-full text-xs" />
                    </td>
                    <td className="p-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }}>Manual</span>
                    </td>
                    <td className="p-1 text-right whitespace-nowrap">
                      <button onClick={() => move(row._idx, -1)} className="p-1 text-[10px]" title="Subir">↑</button>
                      <button onClick={() => move(row._idx, +1)} className="p-1 text-[10px]" title="Bajar">↓</button>
                      <button onClick={() => remove(row._idx)} className="p-1 rounded-lg hover:bg-red-50" data-testid={`btn-iti-eliminar-${row._idx}`}><Trash2 className="w-3 h-3 text-red-500" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cronograma · Móvil: cards apiladas, sin scroll horizontal. */}
          <div className="md:hidden space-y-3">
            {todas.map(row => row.origen === "orden_maestra" ? (
              <div key={row._rid} className="rounded-xl p-3 border" style={{ background: "var(--brand-primary-soft)", borderColor: "var(--brand-primary)" }} data-testid={`iti-om-card-${row.item_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono text-sm" style={{ color: "var(--brand-text)" }}>{row.hora}{row.hora_fin ? <span className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}> – {row.hora_fin}</span> : null}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-primary)", color: "#fff" }}>Orden Maestra</span>
                </div>
                <p className="text-sm font-medium mb-1" style={{ color: "var(--brand-text)" }}>{row.actividad}</p>
                {row.responsable ? <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}><span className="uppercase text-[9px] tracking-wider">Responsable:</span> {row.responsable}</p> : null}
                {row.ubicacion ? <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}><span className="uppercase text-[9px] tracking-wider">Ubicación:</span> {row.ubicacion}</p> : null}
                {row.descripcion ? <p className="text-xs mt-1 whitespace-pre-wrap" style={{ color: "var(--brand-text)" }}>{row.descripcion}</p> : null}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{row.estado}</span>
                  <button onClick={onIrOrdenMaestra} className="text-[11px] underline" style={{ color: "var(--brand-primary)" }} data-testid={`btn-iti-editar-om-card-${row.item_id}`}>Editar en OM →</button>
                </div>
              </div>
            ) : (
              <div key={row._rid} className="rounded-xl p-3 border space-y-2" style={{ background: "var(--brand-surface)", borderColor: "var(--brand-border)" }} data-testid={`iti-manual-card-${row._idx}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }}>Manual</span>
                  <div className="flex gap-1">
                    <button onClick={() => move(row._idx, -1)} className="p-2" title="Subir">↑</button>
                    <button onClick={() => move(row._idx, +1)} className="p-2" title="Bajar">↓</button>
                    <button onClick={() => remove(row._idx)} className="p-2 rounded-lg hover:bg-red-50" data-testid={`btn-iti-eliminar-card-${row._idx}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: "var(--brand-text-muted)" }}>Hora inicio</label>
                    <input type="time" value={row.hora || ""} onChange={(e) => update(row._idx, "hora", e.target.value)} onBlur={commit} className="eo-input w-full text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: "var(--brand-text-muted)" }}>Hora fin (opcional)</label>
                    <input type="time" value={row.hora_fin || ""} onChange={(e) => update(row._idx, "hora_fin", e.target.value)} onBlur={commit} className="eo-input w-full text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: "var(--brand-text-muted)" }}>Actividad</label>
                  <input value={row.actividad || ""} onChange={(e) => update(row._idx, "actividad", e.target.value)} onBlur={commit} placeholder="Actividad" className="eo-input w-full text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: "var(--brand-text-muted)" }}>Responsable</label>
                  <input value={row.responsable || ""} onChange={(e) => update(row._idx, "responsable", e.target.value)} onBlur={commit} placeholder="Responsable" className="eo-input w-full text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: "var(--brand-text-muted)" }}>Ubicación</label>
                  <input value={row.ubicacion || ""} onChange={(e) => update(row._idx, "ubicacion", e.target.value)} onBlur={commit} placeholder="Ubicación (opcional)" className="eo-input w-full text-sm" data-testid={`iti-manual-card-ubicacion-${row._idx}`} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: "var(--brand-text-muted)" }}>Detalle / Instrucciones</label>
                  <textarea value={row.descripcion || ""} onChange={(e) => update(row._idx, "descripcion", e.target.value)} onBlur={commit} placeholder="Instrucciones, materiales, notas…" rows={3} className="eo-input w-full text-sm" />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>
        El Cronograma muestra automáticamente actividades <span className="font-medium" style={{ color: "var(--brand-primary-dark)" }}>Orden Maestra</span> con hora, ordenadas por hora ascendente. Las actividades sin hora quedan en Orden Maestra y no aparecen aquí. Las <b>Manuales</b> se guardan en el itinerario del evento.
      </p>
    </Bloque>
  );
};

const OrdenDelDiaTab = ({ eventoId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [saving, setSaving] = useState(false);
  const [secciones, setSecciones] = useState(null);
  const [esPersonalizada, setEsPersonalizada] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/eventos/${eventoId}/orden-dia`)
      .then(r => {
        setData(r.data);
        setSecciones(JSON.parse(JSON.stringify(r.data.secciones || {})));
        setEsPersonalizada(!!r.data.es_personalizada);
      })
      .catch(() => toast.error("No se pudo cargar la Orden del Día"))
      .finally(() => setLoading(false));
  }, [eventoId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) return <Bloque titulo="Orden del Día"><div className="p-6 text-center text-sm" style={{ color: "var(--brand-text-muted)" }}>Cargando…</div></Bloque>;
  if (!data || !secciones) return <Bloque titulo="Orden del Día"><EmptyBlock icon={Clock} msg="Sin datos disponibles" /></Bloque>;

  const SECCIONES = [
    { key: "cronograma", titulo: "Cronograma", icon: Clock, isTable: true },
    { key: "montaje", titulo: "Montaje", icon: ClipboardList },
    { key: "cocina", titulo: "Cocina", icon: ChefHat },
    { key: "barra", titulo: "Barra", icon: CreditCard },
    { key: "personal", titulo: "Personal", icon: Users },
    { key: "produccion", titulo: "Producción", icon: PartyPopper },
    { key: "limpieza", titulo: "Limpieza", icon: Truck },
  ];

  const print = () => window.print();

  // ===== Helpers de mutación =====
  const updateSeccion = (key, items) => setSecciones(s => ({ ...s, [key]: items }));

  const addRowCronograma = () => updateSeccion("cronograma", [...(secciones.cronograma || []), { _rid: `c_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, hora: "", actividad: "", responsable: "" }]);
  const updateRowCronograma = (idx, field, val) => updateSeccion("cronograma", secciones.cronograma.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  const removeRowCronograma = (idx) => updateSeccion("cronograma", secciones.cronograma.filter((_, i) => i !== idx));
  const moveCronograma = (idx, dir) => {
    const arr = [...(secciones.cronograma || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    updateSeccion("cronograma", arr);
  };

  const addRowLista = (key) => updateSeccion(key, [...(secciones[key] || []), ""]);
  const updateRowLista = (key, idx, val) => updateSeccion(key, secciones[key].map((it, i) => i === idx ? val : it));
  const removeRowLista = (key, idx) => updateSeccion(key, secciones[key].filter((_, i) => i !== idx));
  const moveLista = (key, idx, dir) => {
    const arr = [...(secciones[key] || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    updateSeccion(key, arr);
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/eventos/${eventoId}/orden-dia`, { secciones });
      toast.success("Orden del Día guardada");
      setEditando(false);
      setEsPersonalizada(true);
      cargar();
    } catch (e) {
      toast.error("Error al guardar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const restaurar = async () => {
    if (!window.confirm("¿Restaurar la Orden del Día auto-generada? Perderás tus cambios manuales.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/eventos/${eventoId}/orden-dia`);
      toast.success("Restaurada a auto-generada");
      setEditando(false);
      setEsPersonalizada(false);
      cargar();
    } catch (e) {
      toast.error("Error al restaurar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const cancelar = () => {
    setSecciones(JSON.parse(JSON.stringify(data.secciones || {})));
    setEditando(false);
  };

  const acciones = (
    <div className="flex flex-wrap gap-2">
      {editando ? (
        <>
          <button onClick={cancelar} className="eo-btn eo-btn-outline" disabled={saving} data-testid="btn-cancelar-orden">Cancelar</button>
          <button onClick={guardar} className="eo-btn eo-btn-primary" disabled={saving} data-testid="btn-guardar-orden">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </>
      ) : (
        <>
          {esPersonalizada && (
            <button onClick={restaurar} className="eo-btn eo-btn-outline" disabled={saving} data-testid="btn-restaurar-orden">
              <Sparkles className="w-4 h-4" /> Restaurar auto
            </button>
          )}
          <button onClick={() => setEditando(true)} className="eo-btn eo-btn-outline" data-testid="btn-editar-orden">
            <Edit className="w-4 h-4" /> Editar
          </button>
          <button onClick={print} className="eo-btn eo-btn-outline" data-testid="btn-imprimir-orden">
            <Download className="w-4 h-4" /> Imprimir
          </button>
        </>
      )}
    </div>
  );

  const tituloBloque = `Orden del Día${esPersonalizada ? " — personalizada" : " — auto-generada"}`;

  return (
    <Bloque titulo={tituloBloque} action={acciones}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
        <BloqueMini label="Fecha" valor={data.evento.fecha || "—"} />
        <BloqueMini label="Horario" valor={`${data.evento.hora_inicio} – ${data.evento.hora_fin}`} />
        <BloqueMini label="Invitados" valor={`${data.evento.invitados} (${data.evento.adultos}+${data.evento.ninos})`} />
        <BloqueMini label="Ubicación" valor={data.evento.ubicacion || "—"} />
      </div>
      <div className="space-y-4">
        {SECCIONES.map(s => {
          const items = secciones[s.key] || [];
          if (!editando && items.length === 0) return null;
          return (
            <div key={s.key} className="eo-card p-4" data-testid={`orden-seccion-${s.key}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "var(--brand-primary-soft)" }}>
                    <s.icon className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                  </div>
                  <p className="font-display text-base" style={{ color: "var(--brand-text)" }}>{s.titulo}</p>
                </div>
                {editando && (
                  <button
                    onClick={() => s.isTable ? addRowCronograma() : addRowLista(s.key)}
                    className="eo-btn eo-btn-outline text-xs"
                    data-testid={`btn-add-${s.key}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Agregar
                  </button>
                )}
              </div>
              {s.isTable ? (
                editando ? (
                  <div className="space-y-1.5">
                    {items.length === 0 && <p className="text-xs italic text-stone-500">Sin actividades. Agrega la primera.</p>}
                    {items.map((it, idx) => (
                      <div key={it._rid || `${s.key}-cron-${idx}`} className="flex items-center gap-1.5 p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid={`row-${s.key}-${idx}`}>
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => moveCronograma(idx, -1)} disabled={idx === 0} className="text-stone-400 hover:text-stone-700 disabled:opacity-30" data-testid={`up-${s.key}-${idx}`}>▲</button>
                          <button onClick={() => moveCronograma(idx, 1)} disabled={idx === items.length - 1} className="text-stone-400 hover:text-stone-700 disabled:opacity-30" data-testid={`down-${s.key}-${idx}`}>▼</button>
                        </div>
                        <input value={it.hora || ""} onChange={(e) => updateRowCronograma(idx, "hora", e.target.value)} placeholder="13:00" className="eo-input max-w-[90px] text-center font-mono text-sm" data-testid={`hora-${idx}`} />
                        <input value={it.actividad || ""} onChange={(e) => updateRowCronograma(idx, "actividad", e.target.value)} placeholder="Actividad" className="eo-input flex-1 text-sm" data-testid={`actividad-${idx}`} />
                        <input value={it.responsable || ""} onChange={(e) => updateRowCronograma(idx, "responsable", e.target.value)} placeholder="Responsable" className="eo-input max-w-[140px] text-sm" data-testid={`resp-${idx}`} />
                        <button onClick={() => removeRowCronograma(idx)} className="p-1.5 rounded-lg hover:bg-red-100" data-testid={`del-${s.key}-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {items.map((it, idx) => (
                      <div key={it._rid || `${s.key}-${idx}-${it.hora || ''}`} className="grid grid-cols-[70px,1fr,140px] gap-2 text-xs py-1.5" style={{ borderBottom: idx < items.length - 1 ? "1px dashed var(--brand-border)" : "none" }}>
                        <span className="font-mono font-medium" style={{ color: "var(--brand-primary)" }}>{it.hora}</span>
                        <span style={{ color: "var(--brand-text)" }}>{it.actividad}</span>
                        <span className="text-right" style={{ color: "var(--brand-text-muted)" }}>{it.responsable}</span>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                editando ? (
                  <div className="space-y-1.5">
                    {items.length === 0 && <p className="text-xs italic text-stone-500">Sin items. Agrega el primero.</p>}
                    {items.map((it, idx) => (
                      <div key={`${s.key}-row-${idx}`} className="flex items-center gap-1.5 p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid={`row-${s.key}-${idx}`}>
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => moveLista(s.key, idx, -1)} disabled={idx === 0} className="text-stone-400 hover:text-stone-700 disabled:opacity-30" data-testid={`up-${s.key}-${idx}`}>▲</button>
                          <button onClick={() => moveLista(s.key, idx, 1)} disabled={idx === items.length - 1} className="text-stone-400 hover:text-stone-700 disabled:opacity-30" data-testid={`down-${s.key}-${idx}`}>▼</button>
                        </div>
                        <input value={it} onChange={(e) => updateRowLista(s.key, idx, e.target.value)} placeholder="Item" className="eo-input flex-1 text-sm" data-testid={`txt-${s.key}-${idx}`} />
                        <button onClick={() => removeRowLista(s.key, idx)} className="p-1.5 rounded-lg hover:bg-red-100" data-testid={`del-${s.key}-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-1 text-xs" style={{ color: "var(--brand-text)" }}>
                    {items.map((it, idx) => <li key={`${s.key}-ro-${idx}`} className="flex gap-2"><span style={{ color: "var(--brand-primary)" }}>›</span><span>{it}</span></li>)}
                  </ul>
                )
              )}
            </div>
          );
        })}
      </div>
    </Bloque>
  );
};

const DocumentosTab = ({ evento, onIA, iaLoading }) => {
  const navigate = useNavigate();
  // Iter105 · Centro único de Documentos. Simplificado a UNA lista:
  // 10 documentos del cliente + 12 internos. Cada uno con:
  //   · Ver PDF (nuevo tab)
  //   · Editar plantilla (nuevo tab en /panel/plantillas)
  //   · Enviar por WhatsApp (solo docs del cliente)
  // Se eliminó la sección duplicada "Editor pro" y las plantillas nueva-edición.
  const urlPDF = (tipo) => `${API}/eventos/${evento.evento_id}/documento-pdf?tipo=${tipo}`;

  const enviarWhatsApp = (tipo) => {
    const raw = (evento.cliente?.whatsapp || evento.cliente_whatsapp || "");
    const tel = raw.replace(/\D/g, "");
    if (!tel) { toast.error("Cliente sin WhatsApp"); return; }
    // Prefija 52 solo si no la trae (10 dígitos = número mexicano sin lada país)
    const t = tel.length === 10 ? `52${tel}` : tel;
    const nombreDoc = [...DOCS_CLIENTE, ...DOCS_INTERNOS].find(d => d.tipo === tipo)?.label || "documento";
    const origin = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    const msg = `Hola, aquí está tu ${nombreDoc.toLowerCase()} del evento:\n${origin}${urlPDF(tipo)}`;
    window.open(`https://wa.me/${t}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Bloque titulo="Documentos del evento" testid="documentos-centro">
      <p className="text-xs mb-4" style={{ color: "var(--brand-text-muted)" }}>
        Todos los documentos se generan con los datos del evento — sin recaptura. Puedes verlos, descargarlos, editar sus plantillas o enviarlos por WhatsApp al cliente.
      </p>

      <p className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--brand-primary)" }}>Documentos del cliente</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
        {DOCS_CLIENTE.map(d => (
          <DocFila key={d.tipo} tipo={d.tipo} label={d.label} esCliente urlPDF={urlPDF} enviarWhatsApp={enviarWhatsApp} navigate={navigate} />
        ))}
      </div>

      <p className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--brand-primary)" }}>Documentos internos · Operación</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {DOCS_INTERNOS.map(d => (
          <DocFila key={d.tipo} tipo={d.tipo} label={d.label} urlPDF={urlPDF} enviarWhatsApp={enviarWhatsApp} navigate={navigate} />
        ))}
      </div>

      {(evento.estatus === "contratado" || evento.estatus === "confirmado") && (
        <button onClick={async () => {
          try {
            const r = await axios.post(`${API}/eventos/${evento.evento_id}/generar-produccion`);
            toast.success(r.data.ya_existia ? "Producción ya existía" : "Producción generada");
          } catch (e) { toast.error(e.response?.data?.detail || "No se pudo generar producción"); }
        }} className="mt-6 eo-btn eo-btn-outline w-full" data-testid="btn-generar-produccion-manual">
          <ChefHat className="w-4 h-4" /> Generar producción desde este contrato
        </button>
      )}
    </Bloque>
  );
};

// Iter105 · Constantes movidas fuera para evitar re-creación en cada render
const DOCS_CLIENTE = [
  { tipo: "cotizacion", label: "Cotización" },
  { tipo: "contrato", label: "Contrato" },
  { tipo: "solicitud_anticipo", label: "Recibo / Solicitud de anticipo" },
  { tipo: "reglamento", label: "Reglamento del evento" },
  { tipo: "clausulas", label: "Cláusulas y condiciones" },
  { tipo: "menu", label: "Menú" },
  { tipo: "responsiva", label: "Responsivas" },
  { tipo: "carta_cancelacion", label: "Carta de cancelación" },
  { tipo: "carta_liquidacion", label: "Carta de liquidación" },
  { tipo: "invitacion", label: "Invitación" },
  { tipo: "whatsapp_msg", label: "Mensaje WhatsApp / CRM" },
];
const DOCS_INTERNOS = [
  { tipo: "expediente_maestro", label: "Expediente maestro" },
  { tipo: "itinerario", label: "Itinerario" },
  { tipo: "orden_dia", label: "Orden del día" },
  { tipo: "montaje_decoracion", label: "Montaje y decoración" },
  { tipo: "orden_mobiliario", label: "Orden de mobiliario" },
  { tipo: "orden_materiales", label: "Orden de materiales" },
  { tipo: "orden_proveedores", label: "Orden para proveedores" },
  { tipo: "finanzas_evento", label: "Finanzas / estado de cuenta" },
];

const DocFila = ({ tipo, label, esCliente, urlPDF, enviarWhatsApp, navigate }) => {
  // Iter158 · Fix P0 · Los `<a href>` directos no envían el header
  // Authorization en producción → 401. Se usa axios + Blob URL (mismo patrón
  // que `DocumentosEvento.jsx` `abrirEnNuevaVentana` / `descargarPDF`).
  const verPDF = async () => {
    try {
      const res = await axios.get(urlPDF(tipo), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir el PDF");
    }
  };
  const descargarPDF = async () => {
    try {
      const res = await axios.get(urlPDF(tipo), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${tipo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descargar el PDF");
    }
  };
  return (
    <div className="flex items-center justify-between gap-2 p-3 rounded-xl eo-card eo-card-hover" data-testid={`doc-row-${tipo}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--brand-primary-soft)" }}>
          <FileText className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
        </div>
        <span className="text-sm truncate" style={{ color: "var(--brand-text)" }}>{label}</span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={verPDF} className="p-1.5 rounded-lg hover:bg-stone-100" title="Ver PDF" data-testid={`btn-ver-${tipo}`}>
          <Eye className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
        </button>
        <button onClick={descargarPDF} className="p-1.5 rounded-lg hover:bg-stone-100" title="Descargar PDF" data-testid={`btn-pdf-${tipo}`}>
          <Download className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
        </button>
        <button onClick={() => navigate(`/panel/plantillas?tipo=${tipo}`)}
                className="p-1.5 rounded-lg hover:bg-stone-100" title="Editar plantilla" data-testid={`btn-editar-${tipo}`}>
          <Edit className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
        </button>
        {esCliente && (
          <button onClick={() => enviarWhatsApp(tipo)}
                  className="p-1.5 rounded-lg hover:bg-green-50" title="Enviar por WhatsApp" data-testid={`btn-wa-${tipo}`}>
            <MessageCircle className="w-4 h-4 text-green-600" />
          </button>
        )}
      </div>
    </div>
  );
};

const FinanzasTab = ({
  evento, pagos, totalPagado, saldo, pctPago, navigate,
  onRegistrarPago, onCancelarPago, onEliminarPago, onVerPago, onEditarPago,
  contrato, cotizacion, resumenFinanciero,
  personalAsignados = [], proveedoresAsignados = [],
  comprasEvento = [], egresosEvento = [], ordenMaestra = [], produccionEvento = [],
  onFinalizarEvento, finalizando,
}) => {
  // ==== INGRESOS (heredados, sin doble captura) ====
  const venta = Number(
    resumenFinanciero?.total_venta ??
    contrato?.total ?? cotizacion?.total ?? evento?.total ?? 0
  );

  // ==== COSTOS reales del expediente ====
  const nn = (v) => Number(v) || 0;
  // Compras: se prioriza costo_real cuando existe, luego costo_total, luego cantidad*costo_unitario
  const costoCompras = comprasEvento.reduce((sum, c) => {
    if (!c || c.estatus === "cancelado") return sum;
    const total = c.costo_total || c.costo_real || (nn(c.cantidad) * nn(c.costo_unitario));
    return sum + nn(total);
  }, 0);
  // Personal: costo por relación en el evento (Fase 5)
  const costoPersonal = personalAsignados.reduce((s, p) => s + nn(p.costo), 0);
  // Proveedores: costo por relación en el evento (Fase 5)
  const costoProveedores = proveedoresAsignados.reduce((s, p) => s + nn(p.costo), 0);
  // Otros gastos: egresos manuales del evento (módulo existente)
  const costoOtros = egresosEvento.reduce((s, e) => {
    if (!e || e.estatus === "cancelado") return s;
    return s + nn(e.monto);
  }, 0);
  const costoTotal = costoCompras + costoPersonal + costoProveedores + costoOtros;
  const utilidad = venta - costoTotal;
  const margen = venta > 0 ? (utilidad / venta) * 100 : 0;

  // ==== Pendientes para el bloque de Cierre (lectura) ====
  const tareasPendientes = (ordenMaestra || []).filter(o => ["pendiente", "en_proceso"].includes(o.estado));
  const produccionPendiente = (produccionEvento || []).filter(p => !["terminado", "empacado", "cargado", "entregado", "no_aplica", "excluido"].includes(p.estado));
  const comprasPendientes = (comprasEvento || []).filter(c => c.estatus === "pendiente");
  const yaFinalizado = evento?.estatus === "finalizado";

  const fmt = (v) => `$${(v || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

  return (
  <>
  <Bloque
    titulo="Finanzas del evento"
    testid="finanzas-tab"
    action={<button onClick={onRegistrarPago} className="eo-btn eo-btn-primary" data-testid="btn-registrar-pago"><CreditCard className="w-4 h-4" /> Registrar pago</button>}
  >
    {/* INGRESOS */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="fin-ingresos">
      <div className="eo-card p-5 text-center">
        <div className="relative w-32 h-32 mx-auto">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="var(--brand-surface-muted)" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="var(--brand-primary)" strokeWidth="3" strokeDasharray={`${pctPago} 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>{pctPago}%</p>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Cobrado</p>
          </div>
        </div>
      </div>
      <div className="md:col-span-2 space-y-2">
        <BloqueMini label="Venta (total contratado)" valor={fmt(venta)} valCls={{ color: "var(--brand-text)" }} />
        <BloqueMini label="Cobrado" valor={fmt(totalPagado)} valCls={{ color: "var(--brand-success)" }} />
        <BloqueMini label="Saldo cliente" valor={fmt(saldo)} valCls={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }} />
      </div>
    </div>
    <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Ingresos leídos de {contrato ? "Contrato" : cotizacion ? "Cotización" : "Evento"} + pagos del expediente. NO se capturan aquí de nuevo.</p>

    {/* COSTOS */}
    <div className="mt-5" data-testid="fin-costos">
      <p className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--brand-text-muted)" }}>Costos reales del expediente</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid="fin-costo-compras">
          <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Compras</p>
          <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>{fmt(costoCompras)}</p>
          <button onClick={() => navigate(`/panel/eventos/${evento.evento_id}?tab=produccion`)} className="text-[10px] underline" style={{ color: "var(--brand-primary)" }}>{comprasEvento.length} vinculadas</button>
        </div>
        <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid="fin-costo-personal">
          <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Personal</p>
          <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>{fmt(costoPersonal)}</p>
          <button onClick={() => navigate(`/panel/eventos/${evento.evento_id}?tab=personal_prov`)} className="text-[10px] underline" style={{ color: "var(--brand-primary)" }}>{personalAsignados.length} personas</button>
        </div>
        <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid="fin-costo-proveedores">
          <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Proveedores</p>
          <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>{fmt(costoProveedores)}</p>
          <button onClick={() => navigate(`/panel/eventos/${evento.evento_id}?tab=personal_prov`)} className="text-[10px] underline" style={{ color: "var(--brand-primary)" }}>{proveedoresAsignados.length} vinculados</button>
        </div>
        <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid="fin-costo-otros">
          <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Otros gastos</p>
          <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>{fmt(costoOtros)}</p>
          <p className="text-[10px] italic" style={{ color: "var(--brand-text-muted)" }}>{egresosEvento.length} egresos</p>
        </div>
      </div>
    </div>

    {/* TOTALES */}
    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="fin-totales">
      <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
        <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Costo total</p>
        <p className="font-display text-xl" style={{ color: "var(--brand-text)" }} data-testid="fin-costo-total">{fmt(costoTotal)}</p>
      </div>
      <div className="p-3 rounded-lg" style={{ background: utilidad >= 0 ? "var(--brand-success-soft)" : "var(--brand-danger-soft)" }}>
        <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Utilidad = Venta − Costo Total</p>
        <p className="font-display text-xl" style={{ color: utilidad >= 0 ? "var(--brand-success)" : "var(--brand-danger)" }} data-testid="fin-utilidad">{fmt(utilidad)}</p>
      </div>
      <div className="p-3 rounded-lg" style={{ background: "var(--brand-primary-soft)" }}>
        <p className="text-[10px] uppercase" style={{ color: "var(--brand-primary-dark)" }}>Margen</p>
        <p className="font-display text-xl" style={{ color: "var(--brand-primary-dark)" }} data-testid="fin-margen">{margen.toFixed(1)}%</p>
      </div>
    </div>
    <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Fórmula: <b>Costo Total = Compras + Personal + Proveedores + Otros</b>. <b>Utilidad = Venta − Costo Total</b>. No se usa &quot;Cobrado&quot; para calcular utilidad.</p>

    {pagos.length > 0 && (
      <>
        <p className="text-[10px] uppercase tracking-[0.18em] mt-5 mb-2" style={{ color: "var(--brand-text-muted)" }}>Historial de pagos del cliente</p>
        <div className="space-y-1.5">
          {pagos.map(p => (
            <div key={p.pago_id} className="flex items-center gap-2 p-3 rounded-xl" style={{ background: p.cancelado ? "var(--brand-danger-soft)" : "var(--brand-success-soft)" }} data-testid={`pago-row-${p.pago_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>
                  {p.fecha_pago || (p.created_at || "").substring(0, 10)}
                  {p.cancelado ? <span className="ml-2 text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-danger)" }}>· cancelado</span> : null}
                </p>
                <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{p.metodo_pago || p.metodo || "—"} · {p.tipo || "anticipo"}</p>
              </div>
              <span className="font-display text-lg" style={{ color: p.cancelado ? "var(--brand-danger)" : "var(--brand-success)" }}>
                ${(p.monto_pagado || 0).toLocaleString("es-MX")}
              </span>
              <button onClick={() => onVerPago?.(p)} className="p-2 rounded-lg hover:bg-white/60" title="Ver pago" data-testid={`btn-ver-pago-${p.pago_id}`}>
                <Eye className="w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
              </button>
              {!p.cancelado && (
                <button onClick={() => onEditarPago?.(p)} className="p-2 rounded-lg hover:bg-white/60" title="Editar pago" data-testid={`btn-editar-pago-${p.pago_id}`}>
                  <Edit className="w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
                </button>
              )}
              {!p.cancelado && (
                <button onClick={() => onCancelarPago?.(p)} className="p-2 rounded-lg hover:bg-white/60" title="Cancelar pago" data-testid={`btn-cancelar-pago-${p.pago_id}`}>
                  <X className="w-4 h-4" style={{ color: "var(--brand-danger)" }} />
                </button>
              )}
              <button onClick={() => onEliminarPago?.(p)} className="p-2 rounded-lg hover:bg-white/60" title="Eliminar pago" data-testid={`btn-eliminar-pago-${p.pago_id}`}>
                <Trash2 className="w-4 h-4" style={{ color: "var(--brand-danger)" }} />
              </button>
            </div>
          ))}
        </div>
      </>
    )}
  </Bloque>

  {/* CIERRE DEL EVENTO */}
  <Bloque titulo="Cierre del evento" testid="fin-cierre">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs mb-3">
      <div className="p-2 rounded" style={{ background: "var(--brand-surface-muted)" }}><p style={{ color: "var(--brand-text-muted)" }}>Venta</p><p className="font-display text-base">{fmt(venta)}</p></div>
      <div className="p-2 rounded" style={{ background: "var(--brand-success-soft)" }}><p style={{ color: "var(--brand-success)" }}>Cobrado</p><p className="font-display text-base" style={{ color: "var(--brand-success)" }}>{fmt(totalPagado)}</p></div>
      <div className="p-2 rounded" style={{ background: "var(--brand-warning-soft)" }}><p style={{ color: "var(--brand-warning)" }}>Saldo cliente</p><p className="font-display text-base" style={{ color: "var(--brand-warning)" }}>{fmt(saldo)}</p></div>
      <div className="p-2 rounded" style={{ background: "var(--brand-surface-muted)" }}><p style={{ color: "var(--brand-text-muted)" }}>Costos</p><p className="font-display text-base">{fmt(costoTotal)}</p></div>
      <div className="p-2 rounded" style={{ background: utilidad >= 0 ? "var(--brand-primary-soft)" : "var(--brand-danger-soft)" }}><p style={{ color: utilidad >= 0 ? "var(--brand-primary-dark)" : "var(--brand-danger)" }}>Utilidad</p><p className="font-display text-base" style={{ color: utilidad >= 0 ? "var(--brand-primary-dark)" : "var(--brand-danger)" }}>{fmt(utilidad)}</p></div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
      <div className="p-3 rounded-lg border" style={{ borderColor: tareasPendientes.length ? "var(--brand-warning)" : "var(--brand-success)", background: "var(--brand-surface)" }} data-testid="fin-pend-tareas">
        <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Tareas pendientes</p>
        <p className="font-display text-lg" style={{ color: tareasPendientes.length ? "var(--brand-warning)" : "var(--brand-success)" }}>{tareasPendientes.length}</p>
      </div>
      <div className="p-3 rounded-lg border" style={{ borderColor: produccionPendiente.length ? "var(--brand-warning)" : "var(--brand-success)", background: "var(--brand-surface)" }} data-testid="fin-pend-prod">
        <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Producción pendiente</p>
        <p className="font-display text-lg" style={{ color: produccionPendiente.length ? "var(--brand-warning)" : "var(--brand-success)" }}>{produccionPendiente.length}</p>
      </div>
      <div className="p-3 rounded-lg border" style={{ borderColor: comprasPendientes.length ? "var(--brand-warning)" : "var(--brand-success)", background: "var(--brand-surface)" }} data-testid="fin-pend-compras">
        <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Compras pendientes</p>
        <p className="font-display text-lg" style={{ color: comprasPendientes.length ? "var(--brand-warning)" : "var(--brand-success)" }}>{comprasPendientes.length}</p>
      </div>
    </div>
    {(evento?.notas || "").trim() && (
      <div className="mt-3">
        <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Incidencias / notas registradas</p>
        <pre className="text-xs whitespace-pre-wrap max-h-40 overflow-y-auto p-2 rounded mt-1" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }} data-testid="fin-notas">{evento.notas}</pre>
      </div>
    )}
    <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
      <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>
        Estado actual: <b>{evento?.estatus || "—"}</b>
        {yaFinalizado ? " · Evento ya finalizado." : (tareasPendientes.length + produccionPendiente.length + comprasPendientes.length) > 0 ? " · Aún hay pendientes; el planner decide si cerrar." : " · Sin pendientes conocidos."}
      </p>
      <button
        onClick={onFinalizarEvento}
        disabled={finalizando || yaFinalizado}
        className="eo-btn eo-btn-primary text-sm"
        data-testid="fin-finalizar-evento"
      >
        {yaFinalizado ? "Ya finalizado" : (finalizando ? "Finalizando…" : "Marcar como Finalizado")}
      </button>
    </div>
  </Bloque>
  </>
  );
};

const ResumenView = ({ evento, cliente, personalAsignados, proveedoresAsignados, serviciosAsignados, menuAsignado, pagos, totalPagado, saldo, pctPago, navigate, setTab, onWhatsapp, notas, onEditar, onRegistrarPago, onVincularCliente, contrato, cotizacion, itemsContratados, resumenFinanciero }) => (
  <>
    {/* Iter125 · P1 Fase 1 · Origen comercial heredado (Cotización + Contrato)
        Solo lectura. Los datos NO se vuelven a capturar aquí — se toman del
        expediente único servido por GET /api/eventos/{id}. */}
    {(contrato || cotizacion) && (
      <div className="eo-card p-4" data-testid="resumen-origen-comercial">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Origen comercial · información heredada</p>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-primary-soft)", color: "var(--brand-primary-dark)" }}>Solo lectura</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cotizacion ? (
            <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid="origen-cotizacion">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Cotización origen</p>
                  <p className="font-medium truncate" style={{ color: "var(--brand-text)" }}>{cotizacion.folio || cotizacion.cotizacion_id}</p>
                </div>
                <button
                  onClick={() => navigate(`/panel/cotizar/${cotizacion.cotizacion_id}`)}
                  className="eo-btn eo-btn-outline text-xs shrink-0"
                  data-testid="btn-ver-cotizacion-origen"
                >Ver</button>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                <p><span style={{ color: "var(--brand-text-muted)" }}>Estatus:</span> {cotizacion.estatus || "—"}</p>
                <p><span style={{ color: "var(--brand-text-muted)" }}>Total:</span> ${(cotizacion.total || 0).toLocaleString("es-MX")}</p>
                {cotizacion.tipo_evento && <p><span style={{ color: "var(--brand-text-muted)" }}>Tipo:</span> {cotizacion.tipo_evento}</p>}
                {cotizacion.fecha_evento && <p><span style={{ color: "var(--brand-text-muted)" }}>Fecha:</span> {cotizacion.fecha_evento}</p>}
                {cotizacion.horario && <p className="col-span-2"><span style={{ color: "var(--brand-text-muted)" }}>Horario:</span> {cotizacion.horario}</p>}
                {cotizacion.lugar && <p className="col-span-2"><span style={{ color: "var(--brand-text-muted)" }}>Lugar:</span> {cotizacion.lugar}</p>}
                {(() => {
                  const inv = (cotizacion.adultos || 0) + (cotizacion.jovenes || 0) + (cotizacion.ninos || 0) + (cotizacion.bebes || 0);
                  return inv > 0 ? (
                    <p className="col-span-2"><span style={{ color: "var(--brand-text-muted)" }}>Invitados:</span> {inv} <span style={{ color: "var(--brand-text-muted)" }}>({cotizacion.adultos || 0}A · {cotizacion.jovenes || 0}J · {cotizacion.ninos || 0}N · {cotizacion.bebes || 0}B)</span></p>
                  ) : null;
                })()}
                {cotizacion.paquete && <p className="col-span-2"><span style={{ color: "var(--brand-text-muted)" }}>Paquete:</span> {cotizacion.paquete}</p>}
                {cotizacion.menu && <p className="col-span-2"><span style={{ color: "var(--brand-text-muted)" }}>Menú:</span> {cotizacion.menu}</p>}
                {cotizacion.observaciones && <p className="col-span-2"><span style={{ color: "var(--brand-text-muted)" }}>Observaciones:</span> {cotizacion.observaciones}</p>}
              </div>
              {Array.isArray(cotizacion.servicios) && cotizacion.servicios.length > 0 && (
                <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--brand-border)" }}>
                  <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-text-muted)" }}>Servicios ({cotizacion.servicios.length})</p>
                  <ul className="text-[11px] space-y-0.5 max-h-32 overflow-y-auto">
                    {cotizacion.servicios.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate" style={{ color: "var(--brand-text)" }}>{s.nombre || s.concepto || "—"}{s.cantidad ? ` × ${s.cantidad}` : ""}</span>
                        <span className="shrink-0" style={{ color: "var(--brand-text-muted)" }}>${((s.precio || 0) * (s.cantidad || 1)).toLocaleString("es-MX")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
              <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-text-muted)" }}>Cotización origen</p>
              <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Este evento no tiene cotización vinculada.</p>
            </div>
          )}
          {contrato ? (
            <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid="origen-contrato">
              <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-text-muted)" }}>Contrato</p>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium truncate" style={{ color: "var(--brand-text)" }}>{contrato.folio || contrato.contrato_id}</p>
                  <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>
                    {contrato.estatus || "—"} · ${(contrato.total || 0).toLocaleString("es-MX")}
                    {contrato.fecha_firma ? ` · firmado ${contrato.fecha_firma}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/panel/contratos/${contrato.contrato_id}`)}
                  className="eo-btn eo-btn-outline text-xs shrink-0"
                  data-testid="btn-ver-contrato-origen"
                >Ver</button>
              </div>
            </div>
          ) : (
            <div className="p-3 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
              <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-text-muted)" }}>Contrato</p>
              <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Este evento no tiene contrato vinculado.</p>
            </div>
          )}
        </div>
        {(itemsContratados || []).length > 0 && (
          <div className="mt-3" data-testid="lo-contratado">
            <p className="text-[10px] uppercase mb-2" style={{ color: "var(--brand-text-muted)" }}>Lo contratado ({itemsContratados.length} conceptos)</p>
            <div className="max-h-56 overflow-y-auto overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
              <table className="w-full text-xs">
                <thead style={{ background: "var(--brand-surface-muted)" }}>
                  <tr>
                    <th className="text-left p-2">Concepto</th>
                    <th className="text-left p-2">Tipo</th>
                    <th className="text-right p-2">Cant.</th>
                    <th className="text-right p-2">P. Unit</th>
                    <th className="text-right p-2">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsContratados.map((it, i) => (
                    <tr key={it.item_id || `it-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`item-contratado-${i}`}>
                      <td className="p-2">
                        <p className="truncate" style={{ color: "var(--brand-text)" }}>{it.nombre}</p>
                        {it.descripcion && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{it.descripcion}</p>}
                        {it.seccion && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{it.seccion}</p>}
                      </td>
                      <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{it.tipo || it.categoria || "—"}</td>
                      <td className="p-2 text-right">{it.cantidad}</td>
                      <td className="p-2 text-right">${(it.precio_unitario || 0).toLocaleString("es-MX")}</td>
                      <td className="p-2 text-right font-medium">${(it.importe || 0).toLocaleString("es-MX")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] mt-1" style={{ color: "var(--brand-text-muted)" }}>Snapshot heredado de la cotización aprobada. Preserva el histórico comercial.</p>
          </div>
        )}
        {resumenFinanciero && (
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div className="p-2 rounded" style={{ background: "var(--brand-surface-muted)" }} data-testid="rf-total-venta">
              <p style={{ color: "var(--brand-text-muted)" }}>Total contratado</p>
              <p className="font-display text-sm" style={{ color: "var(--brand-text)" }}>${(resumenFinanciero.total_venta || 0).toLocaleString("es-MX")}</p>
            </div>
            <div className="p-2 rounded" style={{ background: "var(--brand-success-soft)" }} data-testid="rf-cobrado">
              <p style={{ color: "var(--brand-text-muted)" }}>Cobrado</p>
              <p className="font-display text-sm" style={{ color: "var(--brand-success)" }}>${(resumenFinanciero.total_cobrado || 0).toLocaleString("es-MX")}</p>
            </div>
            <div className="p-2 rounded" style={{ background: "var(--brand-warning-soft)" }} data-testid="rf-saldo">
              <p style={{ color: "var(--brand-text-muted)" }}>Saldo</p>
              <p className="font-display text-sm" style={{ color: "var(--brand-warning)" }}>${(resumenFinanciero.saldo || 0).toLocaleString("es-MX")}</p>
            </div>
          </div>
        )}
      </div>
    )}

    {/* Iter98 · FILA 1: Cliente · Evento · Invitación · Planeación
        Reemplaza los cuadros duplicados "Lugar del evento" e "Información general".
        Cada dato se captura UNA vez (edición desde el ícono de lápiz o `Ver más`). */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {/* Cliente: nombre, correo, teléfono, WhatsApp, info de pago */}
      <div className="eo-card p-5" data-testid="resumen-cliente">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Cliente</p>
          <button onClick={() => cliente ? navigate(`/panel/clientes/${cliente.cliente_id}`) : onVincularCliente?.()} className="text-[11px] font-medium" style={{ color: "var(--brand-primary)" }} data-testid="btn-editar-cliente">✎ Editar</button>
        </div>
        {cliente ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm" style={{ background: "var(--brand-primary)" }}>{(cliente.nombre || "?").charAt(0).toUpperCase()}</div>
              <p className="font-medium truncate" style={{ color: "var(--brand-text)" }}>{cliente.nombre}</p>
            </div>
            {cliente.whatsapp && (
              <button onClick={onWhatsapp} className="flex items-center gap-2 text-sm py-1 hover:underline" style={{ color: "var(--brand-text-muted)" }}>
                <Phone className="w-3.5 h-3.5" /> {cliente.whatsapp}
                <MessageCircle className="w-3.5 h-3.5" style={{ color: "var(--brand-success)" }} />
              </button>
            )}
            {cliente.telefono && cliente.telefono !== cliente.whatsapp && <p className="flex items-center gap-2 text-sm py-1" style={{ color: "var(--brand-text-muted)" }}><Phone className="w-3.5 h-3.5" /> {cliente.telefono}</p>}
            {cliente.email && <p className="flex items-center gap-2 text-sm py-1 break-all" style={{ color: "var(--brand-text-muted)" }}><Mail className="w-3.5 h-3.5" /> {cliente.email}</p>}
            <p className="text-[11px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Pagos: ${totalPagado.toLocaleString("es-MX")} · Saldo ${saldo.toLocaleString("es-MX")}</p>
          </>
        ) : (
          <div>
            <EmptyBlock icon={Users} msg="Sin cliente vinculado" />
            <button onClick={() => onVincularCliente?.()} className="eo-btn eo-btn-primary w-full mt-2 text-xs justify-center" data-testid="btn-vincular-cliente">
              <Plus className="w-4 h-4" /> Vincular cliente
            </button>
          </div>
        )}
      </div>

      {/* Evento: tipo, festejado, tema, fecha, horario, ubicación (Google Maps) */}
      <div className="eo-card p-5" data-testid="resumen-evento">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Evento</p>
          <button onClick={() => onEditar?.("datos")} className="text-[11px] font-medium" style={{ color: "var(--brand-primary)" }} data-testid="btn-editar-evento">✎ Editar</button>
        </div>
        <div className="space-y-1.5 text-sm">
          <Row label="Tipo" v={evento.tipo} />
          <Row label="Festejado" v={evento.festejado} />
          {evento.tema && <Row label="Tema" v={evento.tema} />}
          <Row label="Fecha" v={evento.fecha} />
          <Row label="Horario" v={`${evento.hora_inicio || "—"} - ${evento.hora_fin || "—"}`} />
          {(evento.ubicacion || evento.direccion) && (
            <div className="pt-1">
              <p className="text-[11px] font-medium" style={{ color: "var(--brand-text)" }}>{evento.ubicacion || "—"}</p>
              {evento.direccion && (
                <a href={`https://maps.google.com/?q=${encodeURIComponent(evento.direccion)}`} target="_blank" rel="noreferrer" className="text-[11px] underline break-words" style={{ color: "var(--brand-primary)" }} data-testid="link-google-maps">
                  <MapPin className="w-3 h-3 inline mr-1" />{evento.direccion}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Invitación: diseño, código de vestimenta, datos para compartir */}
      <div className="eo-card p-5" data-testid="resumen-invitacion">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Invitación</p>
          <button onClick={() => onEditar?.("datos")} className="text-[11px] font-medium" style={{ color: "var(--brand-primary)" }} data-testid="btn-editar-invitacion">✎ Editar</button>
        </div>
        <div className="space-y-1.5 text-sm">
          <Row label="Diseño" v={evento.invitacion_diseno || evento.diseno_invitacion} />
          <Row label="Código vestimenta" v={evento.codigo_vestimenta || evento.dress_code} />
          <Row label="URL para compartir" v={evento.url_invitacion} />
          {!evento.invitacion_diseno && !evento.codigo_vestimenta && !evento.url_invitacion && (
            <EmptyBlock icon={Sparkles} msg="Sin datos de invitación" />
          )}
        </div>
      </div>

      {/* Planeación: adultos, niños, alergias, necesidades, decoración, servicios */}
      <div className="eo-card p-5" data-testid="resumen-planeacion">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Planeación</p>
          <button onClick={() => onEditar?.("servicios_menu")} className="text-[11px] font-medium" style={{ color: "var(--brand-primary)" }} data-testid="btn-editar-planeacion">✎ Editar</button>
        </div>
        <div className="space-y-1.5 text-sm">
          <Row label="Adultos" v={evento.adultos || 0} />
          <Row label="Niños" v={evento.ninos || 0} />
          {evento.alergias && <Row label="Alergias" v={evento.alergias} />}
          {evento.necesidades_especiales && <Row label="Necesidades" v={evento.necesidades_especiales} />}
          {evento.decoracion && <Row label="Decoración" v={evento.decoracion} />}
          <Row label="Servicios contratados" v={`${(itemsContratados || []).length > 0 ? itemsContratados.length : serviciosAsignados.length} conceptos`} />
        </div>
      </div>
    </div>

    {/* FILA 2: Menú, Personal, Proveedores, Servicios */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="eo-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Menú seleccionado</p>
          <button onClick={() => setTab("servicios_menu")} className="text-[11px]" style={{ color: "var(--brand-primary)" }}>Ver menú</button>
        </div>
        {(() => {
          // Iter144 · Considera menú presente si hay nombre, secciones o campos planos legacy.
          const hasSecs = Array.isArray(menuAsignado?.secciones) && menuAsignado.secciones.length > 0;
          const hasPlanos = !!(menuAsignado?.botanas || menuAsignado?.plato_fuerte || menuAsignado?.bebidas || menuAsignado?.postres);
          const hasMenu = !!(menuAsignado?.nombre || menuAsignado?.id) || hasSecs || hasPlanos;
          if (!hasMenu) return <EmptyBlock icon={ChefHat} msg="Sin menú asignado" />;
          return (
            <>
              {menuAsignado?.nombre ? <p className="font-display text-lg mb-2" style={{ color: "var(--brand-text)" }}>{menuAsignado.nombre}</p> : null}
              {menuAsignado?.botanas && <Row label="Botanas" v={menuAsignado.botanas} />}
              {menuAsignado?.plato_fuerte && <Row label="Plato fuerte" v={menuAsignado.plato_fuerte} />}
              {menuAsignado?.bebidas && <Row label="Bebidas" v={menuAsignado.bebidas} />}
              {menuAsignado?.postres && <Row label="Postres" v={menuAsignado.postres} />}
              {hasSecs && menuAsignado.secciones.slice(0, 4).map((s, i) => (
                <Row key={s._rid || `res-sec-${i}`} label={s.tipo || s.nombre || `Sección ${i + 1}`} v={s.descripcion || s.nombre || ""} />
              ))}
            </>
          );
        })()}
      </div>

      <div className="eo-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Personal asignado</p>
          <button onClick={() => setTab("personal_prov")} className="text-[11px]" style={{ color: "var(--brand-primary)" }}>Ver todo ({personalAsignados.length})</button>
        </div>
        {personalAsignados.length === 0 ? <EmptyBlock icon={UserCog} msg="Sin personal asignado" /> : (
          <div className="space-y-2">
            {personalAsignados.slice(0, 4).map(p => (
              <div key={p.personal_id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm shrink-0" style={{ background: "var(--brand-primary)" }}>{(p.nombre || "?").charAt(0).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{p.nombre}</p>
                  <p className="text-xs truncate" style={{ color: "var(--brand-text-muted)" }}>{p.rol}</p>
                </div>
              </div>
            ))}
            {personalAsignados.length > 4 && <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>+ {personalAsignados.length - 4} más</p>}
          </div>
        )}
      </div>

      <div className="eo-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Proveedores</p>
          <button onClick={() => setTab("personal_prov")} className="text-[11px]" style={{ color: "var(--brand-primary)" }}>Ver todo ({proveedoresAsignados.length})</button>
        </div>
        {proveedoresAsignados.length === 0 ? <EmptyBlock icon={Truck} msg="Sin proveedores" /> : (
          <div className="space-y-2">
            {proveedoresAsignados.slice(0, 4).map(p => (
              <div key={p.proveedor_id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-accent-soft)" }}><Truck className="w-4 h-4" style={{ color: "var(--brand-accent)" }} /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{p.nombre}</p>
                  <p className="text-xs truncate" style={{ color: "var(--brand-text-muted)" }}>{p.categoria || p.servicios || "Proveedor"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="eo-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Servicios y extras</p>
          <button onClick={() => setTab("servicios_menu")} className="text-[11px]" style={{ color: "var(--brand-primary)" }}>Ver todo ({serviciosAsignados.length})</button>
        </div>
        {serviciosAsignados.length === 0 ? <EmptyBlock icon={Star} msg="Sin servicios" /> : (
          <div className="space-y-1.5">
            {serviciosAsignados.slice(0, 5).map(s => (
              <div key={s.servicio_id} className="flex items-center gap-2">
                {s.confirmado ? <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "var(--brand-success)" }} /> : <Circle className="w-4 h-4 shrink-0" style={{ color: "var(--brand-text-muted)" }} />}
                <span className="text-sm flex-1 truncate" style={{ color: "var(--brand-text)" }}>{s.nombre}</span>
                <span className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>${((s.precio || 0) * (s.cantidad || 1)).toLocaleString("es-MX")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* FILA 3: Pagos, Documentos, Notas */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="eo-card p-5">
        <p className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--brand-text-muted)" }}>Pagos</p>
        <div className="flex items-center gap-4">
          <div className="relative w-20 h-20 shrink-0">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <circle cx="18" cy="18" r="15.9155" fill="none" stroke="var(--brand-surface-muted)" strokeWidth="3.5" />
              <circle cx="18" cy="18" r="15.9155" fill="none" stroke="var(--brand-primary)" strokeWidth="3.5" strokeDasharray={`${pctPago} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="font-display text-base" style={{ color: "var(--brand-text)" }}>{pctPago}%</p>
            </div>
          </div>
          <div className="text-sm space-y-1 min-w-0 flex-1">
            <Row label="Total" v={`$${(evento.total || 0).toLocaleString("es-MX")}`} />
            <Row label="Pagado" v={`$${totalPagado.toLocaleString("es-MX")}`} valCls={{ color: "var(--brand-success)" }} />
            <Row label="Saldo" v={`$${saldo.toLocaleString("es-MX")}`} valCls={{ color: "var(--brand-primary-dark)" }} />
          </div>
        </div>
        <button onClick={onRegistrarPago} className="w-full mt-3 eo-btn eo-btn-primary" data-testid="btn-resumen-pago"><CreditCard className="w-4 h-4" /> Registrar pago</button>
      </div>

      <div className="eo-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Documentos</p>
          <button onClick={() => setTab("documentos")} className="text-[11px]" style={{ color: "var(--brand-primary)" }}>Ver todos</button>
        </div>
        <div className="space-y-1.5 text-sm">
          {["Cotización", "Contrato", "Hoja Operativa", "Confirmación Cliente"].map(d => (
            <div key={d} className="flex items-center justify-between py-1">
              <span style={{ color: "var(--brand-text)" }}>{d}</span>
              <button onClick={() => setTab("documentos")} className="text-xs font-medium" style={{ color: "var(--brand-primary)" }}>PDF</button>
            </div>
          ))}
        </div>
      </div>

      <div className="eo-card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Notas y observaciones</p>
          <button onClick={() => setTab("notas")} className="text-[11px]" style={{ color: "var(--brand-primary)" }}>Ver todas</button>
        </div>
        {notas.length === 0 ? <p className="text-sm italic" style={{ color: "var(--brand-text-muted)" }}>Sin notas todavía</p> : (
          <div className="space-y-2">
            {[...notas].reverse().slice(0, 2).map((n, i) => (
              <div key={n.fecha || `n-${i}`} className="p-2 rounded-lg" style={{ background: "var(--brand-primary-soft)" }}>
                <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{new Date(n.fecha).toLocaleDateString("es-MX")} · {n.autor}</p>
                <p className="text-sm mt-0.5" style={{ color: "var(--brand-text)" }}>{n.texto}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </>
);

const Row = ({ label, v, valCls }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{label}</span>
    <span className="truncate text-right" style={{ color: "var(--brand-text)", ...(valCls || {}) }}>{v || "—"}</span>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Iter124x · ORDEN MAESTRA · Centro operativo editable del evento
// ─────────────────────────────────────────────────────────────────────────
// Convive con "Itinerario" y "Orden del Día" (no los reemplaza).
// Todos los cambios persisten en /api/eventos/{id}/orden-maestra/*
// ═══════════════════════════════════════════════════════════════════════════
const PRIORIDAD_META = {
  alta:  { label: "Alta",  bg: "#FEE2E2", fg: "#991B1B" },
  media: { label: "Media", bg: "#FEF3C7", fg: "#92400E" },
  baja:  { label: "Baja",  bg: "#E0F2FE", fg: "#0369A1" },
};
const ORIGEN_META = {
  automatico: { label: "Automático", bg: "#EDE9FE", fg: "#5B21B6" },
  manual:     { label: "Manual",     bg: "#DCFCE7", fg: "#166534" },
  ajustado:   { label: "Ajustado",   bg: "#FFEDD5", fg: "#9A3412" },
};
const ESTADO_META_OM = {
  pendiente:   { label: "Pendiente",   bg: "#FEF3C7", fg: "#92400E" },
  completado:  { label: "Completada",  bg: "#DCFCE7", fg: "#166534" },
  no_aplica:   { label: "No aplica",   bg: "#E5E7EB", fg: "#374151" },
  atrasado:    { label: "Atrasada",    bg: "#FEE2E2", fg: "#991B1B" },
};

const MOMENTOS_BASE = [
  "Planeación", "Días antes", "Producción", "Carga", "Montaje",
  "Antes de invitados", "Durante el evento", "Desmontaje", "Regreso", "Cierre",
];

const OrdenMaestraTab = ({ eventoId, evento }) => {
  const [items, setItems] = useState([]);
  const [contadores, setContadores] = useState({ pendientes: 0, completadas: 0, no_aplica: 0, atrasadas: 0 });
  const [requiereConfig, setRequiereConfig] = useState([]);
  const [cabecera, setCabecera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState("activos"); // activos | excluidos | dia_evento
  const [excluidos, setExcluidos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [quickText, setQuickText] = useState("");
  const [seleccion, setSeleccion] = useState(new Set());
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  // Filtros
  const [fArea, setFArea] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [fResponsable, setFResponsable] = useState("");
  const [fPrioridad, setFPrioridad] = useState("");
  const [fFecha, setFFecha] = useState("");
  const [fMomento, setFMomento] = useState("");
  const [fSinResp, setFSinResp] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  // Momentos personalizados (extras a MOMENTOS_BASE)
  const [momentosExtra, setMomentosExtra] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/eventos/${eventoId}/orden-maestra`);
      setItems(data.items || []);
      setContadores(data.contadores || {});
      setRequiereConfig(data.items_requieren_configuracion || []);
      setCabecera(data.evento || null);
      // Cargar excluidos aparte
      const { data: dExc } = await axios.get(`${API}/eventos/${eventoId}/orden-maestra?incluir_excluidos=true`);
      setExcluidos(dExc.items || []);
    } catch {
      toast.error("No se pudo cargar la Orden Maestra");
    } finally {
      setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const areasUnicas = useMemo(() => {
    const s = new Set();
    items.forEach(i => { if (i.area) s.add(i.area); });
    return Array.from(s).sort();
  }, [items]);
  const responsablesUnicos = useMemo(() => {
    const s = new Set();
    items.forEach(i => { if (i.responsable) s.add(i.responsable); });
    return Array.from(s).sort();
  }, [items]);
  const momentosDisponibles = useMemo(() => {
    const s = new Set([...MOMENTOS_BASE, ...momentosExtra]);
    items.forEach(i => { if (i.momento) s.add(i.momento); });
    return Array.from(s);
  }, [items, momentosExtra]);

  const hoy = new Date().toISOString().slice(0, 10);

  // Indicadores compactos calculados
  const stats = useMemo(() => {
    const activos = items.filter(i => i.estado !== "no_aplica" && i.estado !== "completado");
    return {
      hoy: activos.filter(i => i.fecha === hoy).length,
      vencidos: activos.filter(i => i.fecha && i.fecha < hoy).length,
      sin_resp: activos.filter(i => !i.responsable).length,
      alta: activos.filter(i => i.prioridad === "alta").length,
    };
  }, [items, hoy]);

  const listaBase = vista === "excluidos" ? excluidos : items;

  const itemsFiltrados = useMemo(() => {
    let base = listaBase;
    // Modo Día del evento: priorizar atrasados + hoy + prioridad alta, ordenados por hora
    if (vista === "dia_evento") {
      base = items.filter(i => {
        if (i.estado === "no_aplica") return false;
        if (i.fecha && i.fecha === hoy) return true;
        if (i.fecha && i.fecha < hoy && i.estado === "pendiente") return true;
        if (i.prioridad === "alta" && i.estado === "pendiente") return true;
        return false;
      });
      base = [...base].sort((a, b) => {
        // atrasados primero
        const atA = a.fecha && a.fecha < hoy && a.estado === "pendiente" ? 0 : 1;
        const atB = b.fecha && b.fecha < hoy && b.estado === "pendiente" ? 0 : 1;
        if (atA !== atB) return atA - atB;
        return (a.hora || "99:99").localeCompare(b.hora || "99:99");
      });
    }
    return base.filter(i => {
      if (fArea && i.area !== fArea) return false;
      if (fResponsable && i.responsable !== fResponsable) return false;
      if (fPrioridad && i.prioridad !== fPrioridad) return false;
      if (fFecha && i.fecha !== fFecha) return false;
      if (fMomento && i.momento !== fMomento) return false;
      if (fSinResp && i.responsable) return false;
      if (fEstado) {
        if (fEstado === "atrasado") {
          if (!(i.estado === "pendiente" && i.fecha && i.fecha < hoy)) return false;
        } else if (i.estado !== fEstado) return false;
      }
      if (busqueda) {
        const q = busqueda.toLowerCase();
        const hay = [i.actividad, i.descripcion, i.area, i.responsable, i.observaciones, i.momento]
          .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
        if (!hay) return false;
      }
      return true;
    });
  }, [listaBase, items, vista, fArea, fEstado, fResponsable, fPrioridad, fFecha, fMomento, fSinResp, busqueda, hoy]);

  // Mapa item_id → item para resolver dependencias
  const itemById = useMemo(() => {
    const m = {};
    items.forEach(i => { m[i.item_id] = i; });
    return m;
  }, [items]);

  const guardarNuevo = async (payload) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra`, payload);
      toast.success("Actividad agregada");
      setShowForm(false);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };
  const crearRapido = async () => {
    const txt = quickText.trim();
    if (!txt) return;
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra`, { actividad: txt });
      setQuickText("");
      toast.success("Agregado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar");
    }
  };
  const guardarEdicion = async (itemId, payload) => {
    try {
      await axios.put(`${API}/eventos/${eventoId}/orden-maestra/${itemId}`, payload);
      toast.success("Guardado correctamente");
      setEditingItem(null);
      setShowForm(false);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };
  const cambiarEstado = async (itemId, accion) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${itemId}/estado`, { accion });
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };
  const excluir = async (itemId) => {
    if (!window.confirm("¿Excluir este elemento automático de este evento? No reaparecerá al re-materializar.")) return;
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${itemId}/excluir`);
      toast.success("Excluido de este evento");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo excluir");
    }
  };
  const restaurar = async (itemId) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${itemId}/estado`, { accion: "restaurar" });
      toast.success("Restaurado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar");
    }
  };
  const agregarItinerario = async (item) => {
    const hora = window.prompt(`Hora en el Itinerario para "${item.actividad}" (HH:MM):`, item.hora || "18:00");
    if (!hora) return;
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${item.item_id}/itinerario`, { accion: "agregar", hora_itinerario: hora });
      toast.success("Agregado al Itinerario");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar al Itinerario");
    }
  };
  const quitarItinerario = async (itemId) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${itemId}/itinerario`, { accion: "quitar" });
      toast.success("Quitado del Itinerario");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo quitar");
    }
  };
  const eliminar = async (itemId) => {
    if (!window.confirm("¿Eliminar este elemento de la Orden Maestra?")) return;
    try {
      await axios.delete(`${API}/eventos/${eventoId}/orden-maestra/${itemId}`);
      toast.success("Eliminado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };
  const duplicar = async (itemId) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${itemId}/duplicar`);
      toast.success("Duplicado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo duplicar");
    }
  };
  const mover = async (itemId, dir) => {
    const idx = items.findIndex(i => i.item_id === itemId);
    if (idx < 0) return;
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= items.length) return;
    const nuevos = [...items];
    [nuevos[idx], nuevos[swap]] = [nuevos[swap], nuevos[idx]];
    setItems(nuevos);
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/reordenar`, {
        orden: nuevos.map(i => i.item_id),
      });
      cargar();
    } catch {
      toast.error("No se pudo reordenar");
      cargar();
    }
  };
  const bulkAction = async (accion, valor) => {
    const ids = Array.from(seleccion);
    if (!ids.length) return;
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/bulk`, { ids, accion, valor });
      toast.success("Aplicado a la selección");
      setSeleccion(new Set());
      setShowBulkMenu(false);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar");
    }
  };

  // Aplicar filtro rápido al hacer click en un chip
  const aplicarChip = (chip) => {
    // Limpiar antes
    setFArea(""); setFEstado(""); setFResponsable(""); setFPrioridad(""); setFFecha("");
    setFMomento(""); setFSinResp(false); setBusqueda("");
    if (chip === "hoy") setFFecha(hoy);
    else if (chip === "vencidos") { setFEstado("atrasado"); }
    else if (chip === "sin_resp") setFSinResp(true);
    else if (chip === "alta") { setFPrioridad("alta"); setFEstado("pendiente"); }
  };

  const toggleSel = (id) => {
    const n = new Set(seleccion);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSeleccion(n);
  };

  const hdr = cabecera || {};
  const invit = hdr.invitados || ((evento?.adultos || 0) + (evento?.ninos || 0));

  return (
    <div className="space-y-4" data-testid="orden-maestra-tab">
      {/* Header con datos del evento + contadores */}
      <div className="rounded-2xl p-4 md:p-5" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-bold" style={{ color: "var(--brand-text)" }} data-testid="om-header-nombre">
              {hdr.nombre || evento?.nombre || "Evento"}
            </h2>
            <div className="text-xs md:text-sm mt-1 flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--brand-text-muted)" }}>
              <span><Calendar className="w-3.5 h-3.5 inline mr-1" />{hdr.fecha || evento?.fecha || "—"}</span>
              <span><MapPin className="w-3.5 h-3.5 inline mr-1" />{hdr.ubicacion || evento?.ubicacion || "—"}</span>
              <span><Users className="w-3.5 h-3.5 inline mr-1" />{invit} invitados</span>
              <span className="inline-flex items-center gap-1"><Circle className="w-2 h-2 fill-current" style={{ color: "var(--brand-primary)" }} />{hdr.estado || "planeacion"}</span>
            </div>
          </div>
        </div>
        {/* Contadores principales */}
        <div className="grid grid-cols-4 gap-2 md:gap-3 mt-4">
          <Contador testid="om-cnt-pendientes" label="Pendientes" value={contadores.pendientes} color="#F59E0B" />
          <Contador testid="om-cnt-completadas" label="Completadas" value={contadores.completadas} color="#16A34A" />
          <Contador testid="om-cnt-no-aplica" label="No aplica" value={contadores.no_aplica} color="#6B7280" />
          <Contador testid="om-cnt-atrasadas" label="Atrasadas" value={contadores.atrasadas} color="#DC2626" />
        </div>
        {/* Indicadores compactos filtrables (chips) */}
        <div className="flex flex-wrap gap-2 mt-3">
          <ChipStat label="Hoy" value={stats.hoy} color="#2563EB" onClick={() => aplicarChip("hoy")} testid="om-chip-hoy" />
          <ChipStat label="Vencidos" value={stats.vencidos} color="#DC2626" onClick={() => aplicarChip("vencidos")} testid="om-chip-vencidos" />
          <ChipStat label="Sin responsable" value={stats.sin_resp} color="#7C3AED" onClick={() => aplicarChip("sin_resp")} testid="om-chip-sin-resp" />
          <ChipStat label="Prioridad alta" value={stats.alta} color="#B91C1C" onClick={() => aplicarChip("alta")} testid="om-chip-alta" />
        </div>
      </div>

      {/* Iter125 · P1 Fase 3a · Items contratados sin configuración operativa */}
      {requiereConfig.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: "var(--brand-warning-soft)", border: "1px dashed var(--brand-warning)" }} data-testid="om-requiere-config">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-medium" style={{ color: "var(--brand-warning)" }}>Requiere configuración · {requiereConfig.length}</p>
              <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Items contratados sin regla operativa en el catálogo. NO se crean tareas automáticas hasta que el catálogo defina qué hacer con ellos.</p>
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)", background: "var(--brand-surface)" }}>
            <table className="w-full text-xs">
              <thead style={{ background: "var(--brand-surface-muted)" }}>
                <tr>
                  <th className="text-left p-2">Concepto contratado</th>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-right p-2">Cant.</th>
                  <th className="text-left p-2">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {requiereConfig.map((r, i) => (
                  <tr key={r.item_id || `rc-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`om-requiere-item-${i}`}>
                    <td className="p-2" style={{ color: "var(--brand-text)" }}>{r.nombre || r.item_id}</td>
                    <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{r.tipo || "—"}</td>
                    <td className="p-2 text-right">{r.cantidad}</td>
                    <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{r.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Selector de vista */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-xl p-1" style={{ background: "var(--brand-surface-muted)", border: "1px solid var(--brand-border)" }}>
          <BtnVista active={vista === "activos"} onClick={() => setVista("activos")} testid="om-vista-activos">Activos</BtnVista>
          <BtnVista active={vista === "dia_evento"} onClick={() => setVista("dia_evento")} testid="om-vista-dia">Día del evento</BtnVista>
          <BtnVista active={vista === "excluidos"} onClick={() => setVista("excluidos")} testid="om-vista-excluidos">
            Excluidos {excluidos.length > 0 && <span className="ml-1 text-[10px] px-1.5 rounded-full" style={{ background: "#DC2626", color: "#fff" }}>{excluidos.length}</span>}
          </BtnVista>
        </div>
        {seleccion.size > 0 && vista === "activos" && (
          <div className="relative">
            <button onClick={() => setShowBulkMenu(v => !v)} className="eo-btn eo-btn-outline text-xs" data-testid="om-bulk-btn">
              Acciones ({seleccion.size}) ▾
            </button>
            {showBulkMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowBulkMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] rounded-xl shadow-lg py-2" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }}>
                  <MenuAction icon={CheckCircle2} label="Completar seleccionados" onClick={() => bulkAction("completar")} testid="om-bulk-completar" />
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase" style={{ color: "var(--brand-text-muted)" }}>Cambiar responsable</div>
                  <input
                    placeholder="Nuevo responsable"
                    className="eo-input mx-3 my-1 text-xs w-[calc(100%-24px)]"
                    onKeyDown={(e) => { if (e.key === "Enter") bulkAction("cambiar_responsable", e.target.value); }}
                    data-testid="om-bulk-input-responsable"
                  />
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase mt-1" style={{ color: "var(--brand-text-muted)" }}>Cambiar prioridad</div>
                  <div className="flex gap-1 px-3">
                    <button onClick={() => bulkAction("cambiar_prioridad", "alta")} className="text-[10px] px-2 py-1 rounded" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="om-bulk-pri-alta">Alta</button>
                    <button onClick={() => bulkAction("cambiar_prioridad", "media")} className="text-[10px] px-2 py-1 rounded" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="om-bulk-pri-media">Media</button>
                    <button onClick={() => bulkAction("cambiar_prioridad", "baja")} className="text-[10px] px-2 py-1 rounded" style={{ background: "#E0F2FE", color: "#0369A1" }} data-testid="om-bulk-pri-baja">Baja</button>
                  </div>
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase mt-1" style={{ color: "var(--brand-text-muted)" }}>Cambiar momento</div>
                  <select
                    className="eo-input mx-3 my-1 text-xs w-[calc(100%-24px)]"
                    defaultValue=""
                    onChange={(e) => bulkAction("cambiar_momento", e.target.value)}
                    data-testid="om-bulk-select-momento"
                  >
                    <option value="">— seleccionar —</option>
                    {momentosDisponibles.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Barra de creación rápida */}
      {vista === "activos" && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={quickText}
            onChange={(e) => setQuickText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") crearRapido(); }}
            placeholder="Escribe y presiona Enter para agregar rápido… (Ej: Comprar hielo extra)"
            className="eo-input flex-1 min-w-[200px] text-sm"
            data-testid="om-quick-input"
          />
          <button className="eo-btn eo-btn-primary" onClick={crearRapido} disabled={!quickText.trim()} data-testid="om-quick-btn">
            <Plus className="w-4 h-4" /> Agregar rápido
          </button>
          <button className="eo-btn eo-btn-outline text-sm" onClick={() => { setEditingItem(null); setShowForm(true); }} data-testid="om-btn-agregar">
            Agregar con detalles
          </button>
        </div>
      )}

      {/* Búsqueda + filtros */}
      {vista !== "excluidos" && (
        <>
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar en la Orden Maestra…"
            className="eo-input w-full text-sm"
            data-testid="om-search"
          />
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <select value={fArea} onChange={e => setFArea(e.target.value)} className="eo-input text-xs" data-testid="om-filter-area">
              <option value="">Área: Todas</option>
              {areasUnicas.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={fEstado} onChange={e => setFEstado(e.target.value)} className="eo-input text-xs" data-testid="om-filter-estado">
              <option value="">Estado: Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="completado">Completada</option>
              <option value="no_aplica">No aplica</option>
              <option value="atrasado">Atrasada</option>
            </select>
            <select value={fResponsable} onChange={e => { setFResponsable(e.target.value); if (e.target.value) setFSinResp(false); }} className="eo-input text-xs" data-testid="om-filter-responsable">
              <option value="">Responsable: Todos</option>
              <option value="__SIN__" disabled>─────</option>
              {responsablesUnicos.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={fPrioridad} onChange={e => setFPrioridad(e.target.value)} className="eo-input text-xs" data-testid="om-filter-prioridad">
              <option value="">Prioridad: Todas</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
            <select value={fMomento} onChange={e => setFMomento(e.target.value)} className="eo-input text-xs" data-testid="om-filter-momento">
              <option value="">Momento: Todos</option>
              {momentosDisponibles.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input type="date" value={fFecha} onChange={e => setFFecha(e.target.value)} className="eo-input text-xs" data-testid="om-filter-fecha" />
          </div>
          <label className="flex items-center gap-2 text-xs" style={{ color: "var(--brand-text-muted)" }}>
            <input type="checkbox" checked={fSinResp} onChange={(e) => { setFSinResp(e.target.checked); if (e.target.checked) setFResponsable(""); }} data-testid="om-filter-sin-resp" />
            Solo Sin responsable
          </label>
        </>
      )}

      {/* Formulario alta/edición */}
      {showForm && (
        <OrdenMaestraForm
          modo={editingItem ? "editar" : "crear"}
          initial={editingItem}
          areasUnicas={areasUnicas}
          momentosDisponibles={momentosDisponibles}
          onAddMomento={(m) => setMomentosExtra(prev => Array.from(new Set([...prev, m])))}
          itemsTodos={items}
          onCancel={() => { setShowForm(false); setEditingItem(null); }}
          onSave={(payload) => editingItem
            ? guardarEdicion(editingItem.item_id, payload)
            : guardarNuevo(payload)}
        />
      )}

      {/* Lista de items */}
      {loading ? (
        <div className="p-8 text-center text-sm" style={{ color: "var(--brand-text-muted)" }}>Cargando…</div>
      ) : itemsFiltrados.length === 0 ? (
        <div className="p-8 rounded-2xl text-center" style={{ background: "var(--brand-surface-muted)", border: "1px dashed var(--brand-border)" }}>
          <ClipboardList className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--brand-text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>
            {vista === "excluidos" ? "Sin elementos excluidos." : vista === "dia_evento" ? "Sin actividades relevantes para hoy o próximas urgentes." : "Sin actividades por ahora. Escribe arriba para agregar rápido."}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="om-lista">
          {itemsFiltrados.map((it, idx) => (
            <OrdenMaestraCard
              key={it.item_id}
              item={it}
              itemById={itemById}
              seleccionado={seleccion.has(it.item_id)}
              onToggleSel={() => toggleSel(it.item_id)}
              vista={vista}
              index={idx}
              total={itemsFiltrados.length}
              hoy={hoy}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              onEditar={() => { setEditingItem(it); setShowForm(true); }}
              onCompletar={() => cambiarEstado(it.item_id, "completar")}
              onReabrir={() => cambiarEstado(it.item_id, "reabrir")}
              onNoAplica={() => cambiarEstado(it.item_id, "no_aplica")}
              onDuplicar={() => duplicar(it.item_id)}
              onExcluir={() => excluir(it.item_id)}
              onEliminar={() => eliminar(it.item_id)}
              onRestaurar={() => restaurar(it.item_id)}
              onAgregarItinerario={() => agregarItinerario(it)}
              onQuitarItinerario={() => quitarItinerario(it.item_id)}
              onMoverArriba={() => mover(it.item_id, "up")}
              onMoverAbajo={() => mover(it.item_id, "down")}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Contador = ({ label, value, color, testid }) => (
  <div className="rounded-xl p-2 md:p-3 text-center" style={{ background: "var(--brand-surface-muted)", border: "1px solid var(--brand-border)" }} data-testid={testid}>
    <div className="text-xl md:text-2xl font-bold" style={{ color }}>{value || 0}</div>
    <div className="text-[10px] md:text-xs" style={{ color: "var(--brand-text-muted)" }}>{label}</div>
  </div>
);

const ChipStat = ({ label, value, color, onClick, testid }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition hover:opacity-80"
    style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
    data-testid={testid}
  >
    <span className="font-bold">{value || 0}</span> {label}
  </button>
);

const BtnVista = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 text-xs font-medium rounded-lg transition"
    style={{
      background: active ? "var(--brand-primary)" : "transparent",
      color: active ? "#fff" : "var(--brand-text-muted)",
    }}
    data-testid={testid}
  >
    {children}
  </button>
);

const OrdenMaestraForm = ({ modo, initial, onCancel, onSave, areasUnicas, momentosDisponibles, onAddMomento, itemsTodos }) => {
  const [actividad, setActividad] = useState(initial?.actividad || "");
  const [descripcion, setDescripcion] = useState(initial?.descripcion || "");
  const [area, setArea] = useState(initial?.area || "");
  const [responsable, setResponsable] = useState(initial?.responsable || "");
  const [fecha, setFecha] = useState(initial?.fecha || "");
  const [hora, setHora] = useState(initial?.hora || "");
  const [horaFin, setHoraFin] = useState(initial?.hora_fin || "");
  const [prioridad, setPrioridad] = useState(initial?.prioridad || "media");
  const [momento, setMomento] = useState(initial?.momento || "");
  const [dependeDe, setDependeDe] = useState(initial?.depende_de || "");
  const [observaciones, setObservaciones] = useState(initial?.observaciones || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!actividad.trim()) { toast.error("La actividad es obligatoria"); return; }
    setSaving(true);
    await onSave({ actividad, descripcion, area, responsable, fecha, hora, hora_fin: horaFin, prioridad, momento, depende_de: dependeDe || null, observaciones });
    setSaving(false);
  };

  const crearMomento = () => {
    const nuevo = window.prompt("Nombre del nuevo momento:");
    if (nuevo && nuevo.trim()) {
      onAddMomento(nuevo.trim());
      setMomento(nuevo.trim());
    }
  };

  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-primary)" }} data-testid="om-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: "var(--brand-text)" }}>
          {modo === "editar" ? "Editar actividad" : "Nueva actividad"}
        </h3>
        <button onClick={onCancel} className="p-1" data-testid="om-form-cancelar"><X className="w-4 h-4" /></button>
      </div>
      <div>
        <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Actividad *</label>
        <input
          value={actividad}
          onChange={e => setActividad(e.target.value)}
          className="eo-input w-full text-sm"
          placeholder="Ej: Comprar hielo"
          autoFocus
          data-testid="om-form-actividad"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Área / categoría</label>
          <input
            value={area}
            onChange={e => setArea(e.target.value)}
            className="eo-input w-full text-sm"
            placeholder="Escribe o elige…"
            list="om-areas-datalist"
            data-testid="om-form-area"
          />
          <datalist id="om-areas-datalist">
            {Array.from(new Set(["Montaje","Cocina","Barra","Servicio","Compras","Producción","Cliente","Decoración","Protocolo","Transporte","Coordinación","Personal","Especial"].concat(areasUnicas || []))).map(a => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1 flex items-center justify-between" style={{ color: "var(--brand-text-muted)" }}>
            <span>Momento</span>
            <button type="button" onClick={crearMomento} className="text-[10px] underline" data-testid="om-form-crear-momento">+ Crear momento</button>
          </label>
          <select value={momento} onChange={e => setMomento(e.target.value)} className="eo-input w-full text-sm" data-testid="om-form-momento">
            <option value="">— sin momento —</option>
            {(momentosDisponibles || []).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Responsable</label>
          <input value={responsable} onChange={e => setResponsable(e.target.value)} className="eo-input w-full text-sm" data-testid="om-form-responsable" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Fecha</label>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="eo-input w-full text-sm" data-testid="om-form-fecha" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Hora inicio</label>
          <input type="time" value={hora} onChange={e => setHora(e.target.value)} className="eo-input w-full text-sm" data-testid="om-form-hora" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Hora fin (opcional)</label>
          <input type="time" value={horaFin} onChange={e => setHoraFin(e.target.value)} className="eo-input w-full text-sm" data-testid="om-form-hora-fin" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Prioridad</label>
          <select value={prioridad} onChange={e => setPrioridad(e.target.value)} className="eo-input w-full text-sm" data-testid="om-form-prioridad">
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Depende de</label>
          <select value={dependeDe} onChange={e => setDependeDe(e.target.value)} className="eo-input w-full text-sm" data-testid="om-form-depende-de">
            <option value="">— ninguna dependencia —</option>
            {(itemsTodos || []).filter(x => x.item_id !== initial?.item_id).map(x => (
              <option key={x.item_id} value={x.item_id}>{x.actividad}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Detalle / Instrucciones</label>
        <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} className="eo-input w-full text-sm" rows={3} placeholder="Instrucciones, materiales, notas para el responsable…" data-testid="om-form-descripcion" />
      </div>
      <div>
        <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Observaciones</label>
        <textarea value={observaciones} onChange={e => setObservaciones(e.target.value)} className="eo-input w-full text-sm" rows={2} data-testid="om-form-observaciones" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="eo-btn eo-btn-outline text-sm" data-testid="om-form-btn-cancelar">Cancelar</button>
        <button onClick={submit} disabled={saving || !actividad.trim()} className="eo-btn eo-btn-primary text-sm" data-testid="om-form-btn-guardar">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
};

const OrdenMaestraCard = ({
  item, itemById, seleccionado, onToggleSel, vista, index, total, hoy, openMenu, setOpenMenu,
  onEditar, onCompletar, onReabrir, onNoAplica, onDuplicar, onExcluir, onEliminar, onRestaurar,
  onAgregarItinerario, onQuitarItinerario,
  onMoverArriba, onMoverAbajo,
}) => {
  const esAtrasado = item.estado === "pendiente" && item.fecha && item.fecha < hoy;
  const estadoKey = esAtrasado ? "atrasado" : (item.estado || "pendiente");
  const est = ESTADO_META_OM[estadoKey] || ESTADO_META_OM.pendiente;
  const pri = PRIORIDAD_META[item.prioridad] || PRIORIDAD_META.media;
  const ori = ORIGEN_META[item.origen] || ORIGEN_META.manual;
  const menuOpen = openMenu === item.item_id;
  const dep = item.depende_de ? itemById?.[item.depende_de] : null;
  const depPendiente = dep && dep.estado !== "completado" && dep.estado !== "no_aplica";
  const esExcluido = item.estado === "excluido";

  return (
    <div
      className="rounded-2xl p-3 md:p-4"
      style={{
        background: "var(--brand-surface)",
        border: seleccionado ? "1px solid var(--brand-primary)" : "1px solid var(--brand-border)",
        boxShadow: seleccionado ? "0 0 0 3px var(--brand-primary-soft)" : "none",
        opacity: item.estado === "no_aplica" || esExcluido ? 0.65 : 1,
      }}
      data-testid={`om-card-${item.item_id}`}
    >
      <div className="flex items-start gap-2">
        {/* Checkbox selección + flechas orden */}
        <div className="flex flex-col gap-1 shrink-0 items-center">
          {!esExcluido && vista === "activos" && (
            <input
              type="checkbox"
              checked={seleccionado}
              onChange={onToggleSel}
              className="w-4 h-4"
              data-testid={`om-sel-${item.item_id}`}
              aria-label="Seleccionar"
            />
          )}
          {vista === "activos" && (
            <>
              <button onClick={onMoverArriba} disabled={index === 0} className="p-1 rounded-md disabled:opacity-30" style={{ background: "var(--brand-surface-muted)" }} title="Subir" data-testid={`om-mov-up-${item.item_id}`}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M6 3l4 5H2z" /></svg>
              </button>
              <button onClick={onMoverAbajo} disabled={index === total - 1} className="p-1 rounded-md disabled:opacity-30" style={{ background: "var(--brand-surface-muted)" }} title="Bajar" data-testid={`om-mov-down-${item.item_id}`}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M6 9L2 4h8z" /></svg>
              </button>
            </>
          )}
        </div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4
                  className="text-sm md:text-base font-semibold truncate"
                  style={{
                    color: "var(--brand-text)",
                    textDecoration: item.estado === "completado" ? "line-through" : "none",
                  }}
                  data-testid={`om-actividad-${item.item_id}`}
                >
                  {item.actividad}
                </h4>
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: ori.bg, color: ori.fg }} data-testid={`om-origen-${item.item_id}`}>{ori.label}</span>
              </div>
              {item.descripcion && (
                <p className="text-xs mt-0.5" style={{ color: "var(--brand-text-muted)" }}>{item.descripcion}</p>
              )}
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px]" style={{ color: "var(--brand-text-muted)" }}>
                {item.momento && <span className="inline-flex items-center px-2 py-0.5 rounded-full" style={{ background: "#EEF2FF", color: "#3730A3" }} data-testid={`om-momento-${item.item_id}`}>{item.momento}</span>}
                {item.area && <span><LayoutGrid className="w-3 h-3 inline mr-1" />{item.area}</span>}
                {item.responsable ? (
                  <span><UserCog className="w-3 h-3 inline mr-1" />{item.responsable}</span>
                ) : (
                  <span style={{ color: "#7C3AED" }} data-testid={`om-sin-resp-${item.item_id}`}><UserCog className="w-3 h-3 inline mr-1" />Sin responsable</span>
                )}
                {item.fecha && <span><Calendar className="w-3 h-3 inline mr-1" />{item.fecha}</span>}
                {item.hora && <span><Clock className="w-3 h-3 inline mr-1" />{item.hora}</span>}
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: est.bg, color: est.fg }} data-testid={`om-estado-${item.item_id}`}>{est.label}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: pri.bg, color: pri.fg }}>{pri.label}</span>
                {item.en_itinerario && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1" style={{ background: "#DBEAFE", color: "#1E40AF" }} data-testid={`om-en-itinerario-${item.item_id}`}>
                    <Clock className="w-3 h-3" /> Itinerario {item.hora_itinerario || ""}
                  </span>
                )}
              </div>
              {dep && (
                <div
                  className="mt-2 p-2 rounded-lg text-[11px] flex items-center gap-1.5"
                  style={{
                    background: depPendiente ? "#FEF3C7" : "#DCFCE7",
                    color: depPendiente ? "#92400E" : "#166534",
                  }}
                  data-testid={`om-depende-${item.item_id}`}
                >
                  <Clock className="w-3 h-3" />
                  {depPendiente ? `Esperando: ${dep.actividad}` : `Dependencia lista: ${dep.actividad}`}
                </div>
              )}
              {item.observaciones && (
                <p className="text-xs mt-2 p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text-muted)" }}>{item.observaciones}</p>
              )}
            </div>

            {/* Acciones */}
            {esExcluido ? (
              <div className="shrink-0">
                <button
                  onClick={onRestaurar}
                  className="eo-btn eo-btn-outline text-xs"
                  data-testid={`om-restaurar-${item.item_id}`}
                >
                  Restaurar
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 shrink-0">
                {/* Acciones inline desktop */}
                {item.estado === "completado" ? (
                  <button
                    onClick={onReabrir}
                    className="hidden md:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
                    style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }}
                    data-testid={`om-inline-reabrir-${item.item_id}`}
                    title="Reabrir"
                  >
                    <Circle className="w-3.5 h-3.5" /> Reabrir
                  </button>
                ) : (
                  <button
                    onClick={onCompletar}
                    className="hidden md:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
                    style={{ background: "#DCFCE7", color: "#166534" }}
                    data-testid={`om-inline-completar-${item.item_id}`}
                    title="Completar"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Completar
                  </button>
                )}
                <button
                  onClick={onEditar}
                  className="hidden md:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
                  style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }}
                  data-testid={`om-inline-editar-${item.item_id}`}
                  title="Editar"
                >
                  <Edit className="w-3.5 h-3.5" /> Editar
                </button>
                <div className="relative">
                  <button
                    onClick={() => setOpenMenu(menuOpen ? null : item.item_id)}
                    className="p-1.5 rounded-lg"
                    style={{ background: "var(--brand-surface-muted)" }}
                    data-testid={`om-menu-btn-${item.item_id}`}
                    aria-label="Más acciones"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {menuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                      <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-xl shadow-lg py-1"
                        style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }}
                        data-testid={`om-menu-${item.item_id}`}
                      >
                        {/* En mobile mostrar todas; en desktop las más comunes están inline */}
                        <MenuAction icon={Edit} label="Editar" onClick={() => { setOpenMenu(null); onEditar(); }} testid={`om-act-editar-${item.item_id}`} />
                        {item.estado === "completado" ? (
                          <MenuAction icon={Circle} label="Reabrir" onClick={() => { setOpenMenu(null); onReabrir(); }} testid={`om-act-reabrir-${item.item_id}`} />
                        ) : (
                          <MenuAction icon={CheckCircle2} label="Completar" onClick={() => { setOpenMenu(null); onCompletar(); }} testid={`om-act-completar-${item.item_id}`} />
                        )}
                        {item.estado === "no_aplica" ? (
                          <MenuAction icon={Circle} label="Volver a pendiente" onClick={() => { setOpenMenu(null); onReabrir(); }} testid={`om-act-restaurar-noaplica-${item.item_id}`} />
                        ) : (
                          <MenuAction icon={X} label="No aplica" onClick={() => { setOpenMenu(null); onNoAplica(); }} testid={`om-act-noaplica-${item.item_id}`} />
                        )}
                        <MenuAction icon={Plus} label="Duplicar" onClick={() => { setOpenMenu(null); onDuplicar(); }} testid={`om-act-duplicar-${item.item_id}`} />
                        {item.en_itinerario ? (
                          <MenuAction icon={Clock} label="Quitar del Itinerario" onClick={() => { setOpenMenu(null); onQuitarItinerario(); }} testid={`om-act-quitar-itinerario-${item.item_id}`} />
                        ) : (
                          <MenuAction icon={Clock} label="Agregar al Itinerario" onClick={() => { setOpenMenu(null); onAgregarItinerario(); }} testid={`om-act-agregar-itinerario-${item.item_id}`} />
                        )}
                        {item.origen === "manual" ? (
                          <MenuAction icon={Trash2} label="Eliminar" danger onClick={() => { setOpenMenu(null); onEliminar(); }} testid={`om-act-eliminar-${item.item_id}`} />
                        ) : (
                          <MenuAction icon={Trash2} label="Excluir de este evento" danger onClick={() => { setOpenMenu(null); onExcluir(); }} testid={`om-act-excluir-${item.item_id}`} />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MenuAction = ({ icon: Icon, label, onClick, testid, danger }) => (
  <button
    onClick={onClick}
    className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-black/5"
    style={{ color: danger ? "#DC2626" : "var(--brand-text)" }}
    data-testid={testid}
  >
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════
// Iter127 · ITINERARIO VINCULADO A ORDEN MAESTRA
// ─────────────────────────────────────────────────────────────────────────
// Muestra los items de OM con en_itinerario=true, ordenados por hora.
// Convive con el Itinerario histórico (no lo reemplaza).
// ═══════════════════════════════════════════════════════════════════════════
const ItinerarioVinculadoOM = ({ eventoId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tempActividad, setTempActividad] = useState("");
  const [tempHora, setTempHora] = useState("");
  const [tempHoraFin, setTempHoraFin] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/eventos/${eventoId}/orden-maestra`);
      const enIt = (data.items || []).filter(i => i.en_itinerario);
      enIt.sort((a, b) => {
        const ha = a.hora_itinerario || "99:99";
        const hb = b.hora_itinerario || "99:99";
        if (ha !== hb) return ha.localeCompare(hb);
        return (a.orden_itinerario || 0) - (b.orden_itinerario || 0);
      });
      setItems(enIt);
    } catch {
      toast.error("No se pudo cargar el Itinerario");
    } finally {
      setLoading(false);
    }
  }, [eventoId]);
  useEffect(() => { cargar(); }, [cargar]);

  const agregarNuevo = async () => {
    if (!tempActividad.trim() || !tempHora) {
      toast.error("Actividad y hora son obligatorias");
      return;
    }
    try {
      // 1) crear item en OM
      const { data } = await axios.post(`${API}/eventos/${eventoId}/orden-maestra`, { actividad: tempActividad });
      // 2) agregar al itinerario
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${data.item.item_id}/itinerario`, {
        accion: "agregar",
        hora_itinerario: tempHora,
        hora_fin_itinerario: tempHoraFin,
      });
      toast.success("Actividad agregada al Itinerario");
      setShowAdd(false);
      setTempActividad("");
      setTempHora("");
      setTempHoraFin("");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar");
    }
  };

  const guardarEdicion = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API}/eventos/${eventoId}/orden-maestra/${editing.item_id}/itinerario`, {
        actividad: tempActividad,
        hora_itinerario: tempHora,
        hora_fin_itinerario: tempHoraFin,
      });
      toast.success("Actualizado");
      setEditing(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const quitar = async (item) => {
    if (!window.confirm(`¿Quitar "${item.actividad}" del Itinerario? La actividad permanecerá en Orden Maestra.`)) return;
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${item.item_id}/itinerario`, { accion: "quitar" });
      toast.success("Quitado del Itinerario");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo quitar");
    }
  };

  const mover = async (idx, dir) => {
    const swap = dir === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= items.length) return;
    const nuevos = [...items];
    [nuevos[idx], nuevos[swap]] = [nuevos[swap], nuevos[idx]];
    setItems(nuevos);
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/itinerario/reordenar`, {
        orden: nuevos.map(i => i.item_id),
      });
      cargar();
    } catch {
      toast.error("No se pudo reordenar");
      cargar();
    }
  };

  return (
    <div className="rounded-2xl p-4" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }} data-testid="itinerario-vinculado">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <h3 className="text-base font-bold" style={{ color: "var(--brand-text)" }}>Itinerario del evento</h3>
          <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>Vinculado a Orden Maestra · convive con el itinerario histórico</p>
        </div>
        <button
          onClick={() => { setEditing(null); setTempActividad(""); setTempHora(""); setTempHoraFin(""); setShowAdd(true); }}
          className="eo-btn eo-btn-primary text-xs"
          data-testid="itv-btn-agregar"
        >
          <Plus className="w-4 h-4" /> Agregar al Itinerario
        </button>
      </div>

      {(showAdd || editing) && (
        <div className="rounded-xl p-3 mb-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end" style={{ background: "var(--brand-surface-muted)" }} data-testid="itv-form">
          <div className="md:col-span-2">
            <label className="text-[10px] font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Actividad *</label>
            <input value={tempActividad} onChange={e => setTempActividad(e.target.value)} className="eo-input w-full text-sm" placeholder="Ej: Recepción" data-testid="itv-form-actividad" />
          </div>
          <div>
            <label className="text-[10px] font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Hora *</label>
            <input type="time" value={tempHora} onChange={e => setTempHora(e.target.value)} className="eo-input w-full text-sm" data-testid="itv-form-hora" />
          </div>
          <div>
            <label className="text-[10px] font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Hora fin</label>
            <input type="time" value={tempHoraFin} onChange={e => setTempHoraFin(e.target.value)} className="eo-input w-full text-sm" data-testid="itv-form-hora-fin" />
          </div>
          <div className="md:col-span-4 flex gap-2 justify-end">
            <button onClick={() => { setShowAdd(false); setEditing(null); }} className="eo-btn eo-btn-outline text-xs" data-testid="itv-form-cancelar">Cancelar</button>
            <button onClick={editing ? guardarEdicion : agregarNuevo} className="eo-btn eo-btn-primary text-xs" data-testid="itv-form-guardar">
              <Save className="w-4 h-4" /> {editing ? "Guardar" : "Agregar"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-4 text-center text-sm" style={{ color: "var(--brand-text-muted)" }}>Cargando…</div>
      ) : items.length === 0 ? (
        <p className="text-xs italic p-4 rounded-lg text-center" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text-muted)" }}>
          Sin actividades en el Itinerario aún. Presiona <b>Agregar al Itinerario</b> o desde Orden Maestra usa el menú ⋮ → &quot;Agregar al Itinerario&quot;.
        </p>
      ) : (
        <div className="space-y-2" data-testid="itv-lista">
          {items.map((it, idx) => (
            <div key={it.item_id} className="flex items-start gap-2 p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid={`itv-item-${it.item_id}`}>
              <div className="flex flex-col gap-0.5 items-center shrink-0">
                <button onClick={() => mover(idx, "up")} disabled={idx === 0} className="p-0.5 rounded disabled:opacity-30" style={{ background: "var(--brand-surface)" }} data-testid={`itv-up-${it.item_id}`}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><path d="M6 3l4 5H2z" /></svg>
                </button>
                <button onClick={() => mover(idx, "down")} disabled={idx === items.length - 1} className="p-0.5 rounded disabled:opacity-30" style={{ background: "var(--brand-surface)" }} data-testid={`itv-down-${it.item_id}`}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><path d="M6 9L2 4h8z" /></svg>
                </button>
              </div>
              <div className="w-16 text-center shrink-0">
                <div className="text-sm font-bold" style={{ color: "var(--brand-primary)" }} data-testid={`itv-hora-${it.item_id}`}>{it.hora_itinerario || "--:--"}</div>
                {it.hora_fin_itinerario && <div className="text-[9px]" style={{ color: "var(--brand-text-muted)" }}>→ {it.hora_fin_itinerario}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }} data-testid={`itv-actividad-${it.item_id}`}>{it.actividad}</div>
                <div className="flex gap-2 mt-0.5 text-[10px]" style={{ color: "var(--brand-text-muted)" }}>
                  {it.area && <span>{it.area}</span>}
                  {it.responsable && <span>· {it.responsable}</span>}
                  {it.hora && <span>· op {it.hora}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => { setEditing(it); setTempActividad(it.actividad); setTempHora(it.hora_itinerario || ""); setTempHoraFin(it.hora_fin_itinerario || ""); setShowAdd(false); }}
                  className="text-[10px] px-2 py-1 rounded-lg"
                  style={{ background: "var(--brand-surface)", color: "var(--brand-text)" }}
                  data-testid={`itv-editar-${it.item_id}`}
                >
                  <Edit className="w-3 h-3 inline" /> Editar
                </button>
                <button
                  onClick={() => quitar(it)}
                  className="text-[10px] px-2 py-1 rounded-lg"
                  style={{ background: "#FEE2E2", color: "#991B1B" }}
                  data-testid={`itv-quitar-${it.item_id}`}
                >
                  <X className="w-3 h-3 inline" /> Quitar del Itinerario
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// Iter127 · ÓRDENES DE TRABAJO
// ─────────────────────────────────────────────────────────────────────────
// Colección: `ordenes_trabajo`. Cada orden guarda item_ids con referencia a
// orden_maestra (fuente única de verdad). Regla P0 CRUD respetada.
// Dentro de una orden se DESVINCULA (no elimina la actividad de OM).
// ═══════════════════════════════════════════════════════════════════════════
const OrdenesTrabajoTab = ({ eventoId }) => {
  const [ordenes, setOrdenes] = useState([]);
  const [omItems, setOmItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormOrden, setShowFormOrden] = useState(false);
  const [editingOrden, setEditingOrden] = useState(null);
  const [openOrdenId, setOpenOrdenId] = useState(null);
  const [showVincular, setShowVincular] = useState(null);
  const [openActMenu, setOpenActMenu] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: dOT }, { data: dOM }] = await Promise.all([
        axios.get(`${API}/eventos/${eventoId}/ordenes-trabajo`),
        axios.get(`${API}/eventos/${eventoId}/orden-maestra`),
      ]);
      setOrdenes(dOT.ordenes || []);
      setOmItems(dOM.items || []);
    } catch {
      toast.error("No se pudo cargar Órdenes de Trabajo");
    } finally {
      setLoading(false);
    }
  }, [eventoId]);
  useEffect(() => { cargar(); }, [cargar]);

  const materializarOT = async () => {
    try {
      const { data } = await axios.post(`${API}/eventos/${eventoId}/ordenes-trabajo/materializar`);
      toast.success(`Materializado: ${data.ordenes_creadas} órdenes, ${data.vinculos_creados} vínculos`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo materializar");
    }
  };
  const guardarOrden = async (payload) => {
    try {
      if (editingOrden) {
        await axios.put(`${API}/eventos/${eventoId}/ordenes-trabajo/${editingOrden.orden_id}`, payload);
        toast.success("Orden actualizada");
      } else {
        await axios.post(`${API}/eventos/${eventoId}/ordenes-trabajo`, payload);
        toast.success("Orden creada");
      }
      setShowFormOrden(false);
      setEditingOrden(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };
  const eliminarOrden = async (orden) => {
    const cnt = orden.actividades_count || 0;
    const msg = cnt > 0
      ? `Esta orden contiene ${cnt} actividad(es). Se desvincularán y la orden quedará eliminada. Las actividades permanecerán en Orden Maestra.\n\n¿Continuar?`
      : "¿Eliminar esta orden?";
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/eventos/${eventoId}/ordenes-trabajo/${orden.orden_id}`);
      toast.success(cnt > 0 ? `Orden eliminada · ${cnt} actividades desvinculadas` : "Orden eliminada");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };
  const vincularItems = async (ordenId, itemIds) => {
    if (!itemIds.length) return;
    try {
      await axios.post(`${API}/eventos/${eventoId}/ordenes-trabajo/${ordenId}/vincular`, { item_ids: itemIds });
      toast.success("Vinculado");
      setShowVincular(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
    }
  };
  const desvincular = async (ordenId, itemId) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/ordenes-trabajo/${ordenId}/desvincular`, { item_ids: [itemId] });
      toast.success("Desvinculado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desvincular");
    }
  };
  const actEstado = async (itemId, accion) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${itemId}/estado`, { accion });
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };
  const actDuplicar = async (itemId) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/orden-maestra/${itemId}/duplicar`);
      toast.success("Duplicado en Orden Maestra");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo duplicar");
    }
  };

  return (
    <div className="space-y-4" data-testid="ordenes-trabajo-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold" style={{ color: "var(--brand-text)" }}>Órdenes de Trabajo</h2>
          <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Las actividades viven en Orden Maestra. Aquí se agrupan por rol/área para operar el evento.</p>
        </div>
        <div className="flex gap-2">
          <button className="eo-btn eo-btn-outline text-xs" onClick={materializarOT} data-testid="ot-materializar">
            Materializar por áreas
          </button>
          <button className="eo-btn eo-btn-primary text-xs" onClick={() => { setEditingOrden(null); setShowFormOrden(true); }} data-testid="ot-btn-crear">
            <Plus className="w-4 h-4" /> Crear orden
          </button>
        </div>
      </div>

      {showFormOrden && (
        <OrdenTrabajoForm
          initial={editingOrden}
          onCancel={() => { setShowFormOrden(false); setEditingOrden(null); }}
          onSave={guardarOrden}
        />
      )}

      {loading ? (
        <div className="p-8 text-center text-sm" style={{ color: "var(--brand-text-muted)" }}>Cargando…</div>
      ) : ordenes.length === 0 ? (
        <div className="p-8 rounded-2xl text-center" style={{ background: "var(--brand-surface-muted)", border: "1px dashed var(--brand-border)" }}>
          <ClipboardList className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--brand-text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Aún no hay órdenes. Presiona <b>+ Crear orden</b> o <b>Materializar por áreas</b> para agrupar actividades existentes.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="ot-lista">
          {ordenes.map((o) => {
            const expandida = openOrdenId === o.orden_id;
            const desvincSet = new Set(o.desvinculados_manual || []);
            return (
              <div key={o.orden_id} className="rounded-2xl p-4" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }} data-testid={`ot-card-${o.orden_id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold" style={{ color: "var(--brand-text)" }} data-testid={`ot-nombre-${o.orden_id}`}>{o.nombre}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: o.origen === "manual" ? "#DCFCE7" : "#EDE9FE", color: o.origen === "manual" ? "#166534" : "#5B21B6" }} data-testid={`ot-origen-${o.orden_id}`}>{o.origen === "manual" ? "Personalizada" : "Automática"}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px]" style={{ color: "var(--brand-text-muted)" }}>
                      {o.area && <span><LayoutGrid className="w-3 h-3 inline mr-1" />{o.area}</span>}
                      {o.responsable && <span><UserCog className="w-3 h-3 inline mr-1" />{o.responsable}</span>}
                      <span data-testid={`ot-count-${o.orden_id}`}><ClipboardList className="w-3 h-3 inline mr-1" />{o.actividades_count} actividades</span>
                    </div>
                    {o.descripcion && <p className="text-xs mt-1" style={{ color: "var(--brand-text-muted)" }}>{o.descripcion}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0 flex-wrap">
                    <button className="text-xs px-2 py-1 rounded-lg" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }} onClick={() => setOpenOrdenId(expandida ? null : o.orden_id)} data-testid={`ot-toggle-${o.orden_id}`}>
                      {expandida ? "Ocultar" : "Ver actividades"}
                    </button>
                    <button className="text-xs px-2 py-1 rounded-lg" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }} onClick={() => { setEditingOrden(o); setShowFormOrden(true); }} data-testid={`ot-editar-${o.orden_id}`}>
                      <Edit className="w-3.5 h-3.5 inline" /> Editar
                    </button>
                    <button className="text-xs px-2 py-1 rounded-lg" style={{ background: "#FEE2E2", color: "#991B1B" }} onClick={() => eliminarOrden(o)} data-testid={`ot-eliminar-${o.orden_id}`}>
                      <Trash2 className="w-3.5 h-3.5 inline" /> Eliminar
                    </button>
                  </div>
                </div>
                {expandida && (
                  <div className="mt-3 space-y-2" data-testid={`ot-actividades-${o.orden_id}`}>
                    {o.actividades.length === 0 ? (
                      <p className="text-xs italic p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text-muted)" }}>Sin actividades vinculadas.</p>
                    ) : (
                      o.actividades.map((a) => {
                        const menuKey = `${o.orden_id}:${a.item_id}`;
                        const menuOpen = openActMenu === menuKey;
                        return (
                          <div key={a.item_id} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid={`ot-act-${o.orden_id}-${a.item_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm truncate" style={{ color: "var(--brand-text)", textDecoration: a.estado === "completado" ? "line-through" : "none" }}>{a.actividad}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: (ORIGEN_META[a.origen] || ORIGEN_META.manual).bg, color: (ORIGEN_META[a.origen] || ORIGEN_META.manual).fg }}>{(ORIGEN_META[a.origen] || ORIGEN_META.manual).label}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: (ESTADO_META_OM[a.estado] || ESTADO_META_OM.pendiente).bg, color: (ESTADO_META_OM[a.estado] || ESTADO_META_OM.pendiente).fg }}>{(ESTADO_META_OM[a.estado] || ESTADO_META_OM.pendiente).label}</span>
                              </div>
                              {(a.responsable || a.hora) && (
                                <div className="text-[10px] mt-0.5" style={{ color: "var(--brand-text-muted)" }}>
                                  {a.responsable && <span className="mr-3">{a.responsable}</span>}
                                  {a.hora && <span>{a.hora}</span>}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {a.estado === "completado" ? (
                                <button onClick={() => actEstado(a.item_id, "reabrir")} className="text-[10px] px-2 py-1 rounded-lg" style={{ background: "var(--brand-surface)", color: "var(--brand-text)" }} data-testid={`ot-act-reabrir-${a.item_id}`}>Reabrir</button>
                              ) : (
                                <button onClick={() => actEstado(a.item_id, "completar")} className="text-[10px] px-2 py-1 rounded-lg" style={{ background: "#DCFCE7", color: "#166534" }} data-testid={`ot-act-completar-${a.item_id}`}>Completar</button>
                              )}
                              <div className="relative">
                                <button onClick={() => setOpenActMenu(menuOpen ? null : menuKey)} className="p-1 rounded-md" style={{ background: "var(--brand-surface)" }} data-testid={`ot-act-menu-${a.item_id}`}>
                                  <MoreHorizontal className="w-3.5 h-3.5" />
                                </button>
                                {menuOpen && (
                                  <>
                                    <div className="fixed inset-0 z-40" onClick={() => setOpenActMenu(null)} />
                                    <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-xl shadow-lg py-1" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }}>
                                      <MenuAction icon={X} label="No aplica" onClick={() => { setOpenActMenu(null); actEstado(a.item_id, "no_aplica"); }} testid={`ot-act-noaplica-${a.item_id}`} />
                                      <MenuAction icon={Plus} label="Duplicar en OM" onClick={() => { setOpenActMenu(null); actDuplicar(a.item_id); }} testid={`ot-act-duplicar-${a.item_id}`} />
                                      <MenuAction icon={Trash2} label="Desvincular de esta orden" onClick={() => { setOpenActMenu(null); desvincular(o.orden_id, a.item_id); }} testid={`ot-act-desvincular-${a.item_id}`} />
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <button
                      className="w-full text-xs py-2 rounded-lg border-2 border-dashed"
                      style={{ borderColor: "var(--brand-border)", color: "var(--brand-text-muted)" }}
                      onClick={() => setShowVincular(o.orden_id)}
                      data-testid={`ot-vincular-btn-${o.orden_id}`}
                    >
                      <Plus className="w-3.5 h-3.5 inline" /> Vincular actividades de Orden Maestra
                    </button>
                    {desvincSet.size > 0 && (
                      <p className="text-[10px] italic px-2" style={{ color: "var(--brand-text-muted)" }} data-testid={`ot-desvinc-info-${o.orden_id}`}>
                        {desvincSet.size} actividad(es) desvinculadas manualmente — no serán revinculadas automáticamente.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showVincular && (
        <OTVincularModal
          candidatos={omItems.filter(i => {
            const orden = ordenes.find(o => o.orden_id === showVincular);
            return orden && !(orden.item_ids || []).includes(i.item_id);
          })}
          onCancel={() => setShowVincular(null)}
          onConfirm={(ids) => vincularItems(showVincular, ids)}
        />
      )}
    </div>
  );
};

const OrdenTrabajoForm = ({ initial, onCancel, onSave }) => {
  const [nombre, setNombre] = useState(initial?.nombre || "");
  const [area, setArea] = useState(initial?.area || "");
  const [responsable, setResponsable] = useState(initial?.responsable || "");
  const [descripcion, setDescripcion] = useState(initial?.descripcion || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    await onSave({ nombre, area, responsable, descripcion });
    setSaving(false);
  };
  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-primary)" }} data-testid="ot-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: "var(--brand-text)" }}>{initial ? "Editar orden" : "Nueva orden de trabajo"}</h3>
        <button onClick={onCancel} className="p-1"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Nombre *</label>
          <input value={nombre} onChange={e => setNombre(e.target.value)} className="eo-input w-full text-sm" placeholder="Ej: Seguridad" autoFocus data-testid="ot-form-nombre" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Área</label>
          <input value={area} onChange={e => setArea(e.target.value)} className="eo-input w-full text-sm" list="ot-areas-datalist" data-testid="ot-form-area" />
          <datalist id="ot-areas-datalist">
            {["Cocina","Barra","Montaje","Servicio","Coordinación","Seguridad","Transporte","Niñeras","Valet","Audiovisual","Show","Limpieza","Fotografía","Ceremonia"].map(a => <option key={a} value={a} />)}
          </datalist>
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Responsable</label>
          <input value={responsable} onChange={e => setResponsable(e.target.value)} className="eo-input w-full text-sm" data-testid="ot-form-responsable" />
        </div>
        <div>
          <label className="text-xs font-medium block mb-1" style={{ color: "var(--brand-text-muted)" }}>Descripción</label>
          <input value={descripcion} onChange={e => setDescripcion(e.target.value)} className="eo-input w-full text-sm" data-testid="ot-form-descripcion" />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="eo-btn eo-btn-outline text-sm" data-testid="ot-form-cancelar">Cancelar</button>
        <button onClick={submit} disabled={saving || !nombre.trim()} className="eo-btn eo-btn-primary text-sm" data-testid="ot-form-guardar">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
};

const OTVincularModal = ({ candidatos, onCancel, onConfirm }) => {
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const filtrados = useMemo(() => {
    const qq = q.toLowerCase().trim();
    if (!qq) return candidatos;
    return candidatos.filter(c => (c.actividad || "").toLowerCase().includes(qq) || (c.area || "").toLowerCase().includes(qq));
  }, [candidatos, q]);
  const toggle = (id) => {
    const n = new Set(sel);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSel(n);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="ot-vincular-modal">
      <div className="rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col" style={{ background: "var(--brand-surface)" }}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--brand-border)" }}>
          <h3 className="text-sm font-bold" style={{ color: "var(--brand-text)" }}>Vincular actividades de Orden Maestra</h3>
          <button onClick={onCancel}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar actividad…" className="eo-input w-full text-sm" data-testid="ot-vincular-search" />
        </div>
        <div className="flex-1 overflow-auto px-3 space-y-1">
          {filtrados.length === 0 ? (
            <p className="text-xs italic text-center py-4" style={{ color: "var(--brand-text-muted)" }}>Sin actividades disponibles.</p>
          ) : filtrados.map(c => (
            <label key={c.item_id} className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-black/5">
              <input type="checkbox" checked={sel.has(c.item_id)} onChange={() => toggle(c.item_id)} data-testid={`ot-vincular-check-${c.item_id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate" style={{ color: "var(--brand-text)" }}>{c.actividad}</div>
                <div className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{c.area || "sin área"}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="p-3 border-t flex gap-2 justify-end" style={{ borderColor: "var(--brand-border)" }}>
          <button onClick={onCancel} className="eo-btn eo-btn-outline text-sm">Cancelar</button>
          <button onClick={() => onConfirm(Array.from(sel))} disabled={sel.size === 0} className="eo-btn eo-btn-primary text-sm" data-testid="ot-vincular-confirmar">
            Vincular ({sel.size})
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Iter128 · PRODUCCIÓN + Recetas + Cantidades operativas
// ═══════════════════════════════════════════════════════════════════════════
const PROD_ESTADO_META = {
  pendiente:      { label: "Pendiente",      bg: "#FEF3C7", fg: "#92400E" },
  en_preparacion: { label: "En preparación", bg: "#DBEAFE", fg: "#1E40AF" },
  terminado:      { label: "Terminado",      bg: "#DCFCE7", fg: "#166534" },
  empacado:       { label: "Empacado",       bg: "#E0E7FF", fg: "#3730A3" },
  cargado:        { label: "Cargado",        bg: "#FCE7F3", fg: "#9D174D" },
  entregado:      { label: "Entregado",      bg: "#DCFCE7", fg: "#14532D" },
  no_aplica:      { label: "No aplica",      bg: "#E5E7EB", fg: "#374151" },
  atrasado:       { label: "Atrasado",       bg: "#FEE2E2", fg: "#991B1B" },
};

// Iter143 · Vista unificada Cocina / Chef.
// Reutiliza componentes existentes: Bloque + EmptyBlock + ProduccionTab.
// No crea endpoints ni colecciones nuevas — solo agrupa 3 fuentes ya vinculadas
// al evento actual:
//   · Menú del evento     → evento.menu_asignado.secciones (ya cargado)
//   · Producción/Recetas  → <ProduccionTab eventoId={eventoId} /> (endpoints por evento)
//   · Compras del evento  → GET /api/compras?evento_id={eventoId}
const CocinaChefTab = ({ evento, eventoId, menuAsignado }) => {
  const [comprasEvento, setComprasEvento] = useState([]);
  const [loadingCompras, setLoadingCompras] = useState(true);
  // Iter144 · Fallback a campos planos legacy (mismo criterio que menuSeccionesBackend, líneas 791-802).
  // Si secciones no es un arreglo válido, reconstruye desde botanas/plato_fuerte/bebidas/postres.
  const secciones = useMemo(() => {
    const ma = menuAsignado || evento?.menu_asignado || {};
    const arr = ma.secciones;
    if (Array.isArray(arr) && arr.length > 0) return arr;
    const out = [];
    if (ma.botanas)       out.push({ _rid: "leg_bot",  tipo: "Botanas",      nombre: "Botanas",      descripcion: ma.botanas,      precio_extra: 0 });
    if (ma.plato_fuerte)  out.push({ _rid: "leg_pf",   tipo: "Plato fuerte", nombre: "Plato fuerte", descripcion: ma.plato_fuerte, precio_extra: 0 });
    if (ma.bebidas)       out.push({ _rid: "leg_beb",  tipo: "Bebidas",      nombre: "Bebidas",      descripcion: ma.bebidas,      precio_extra: 0 });
    if (ma.postres)       out.push({ _rid: "leg_post", tipo: "Postres",      nombre: "Postres",      descripcion: ma.postres,      precio_extra: 0 });
    return out;
  }, [menuAsignado, evento?.menu_asignado]);

  useEffect(() => {
    let cancel = false;
    setLoadingCompras(true);
    axios.get(`${API}/compras?evento_id=${eventoId}`)
      .then(({ data }) => { if (!cancel) setComprasEvento(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancel) setComprasEvento([]); })
      .finally(() => { if (!cancel) setLoadingCompras(false); });
    return () => { cancel = true; };
  }, [eventoId]);

  const totalCompras = comprasEvento.reduce((acc, c) => acc + (c.total || c.subtotal || c.importe || 0), 0);

  return (
    <div className="space-y-4" data-testid="cocina-chef-tab">
      {/* MENÚ DEL EVENTO */}
      <Bloque titulo="Menú del evento" testid="cocina-menu">
        {secciones.length === 0 ? (
          <EmptyBlock icon={ChefHat} msg="Sin menú asignado. Configúralo desde la pestaña Comercial → Servicios y menú." />
        ) : (
          <div className="space-y-3">
            {secciones.map((sec, i) => {
              // Iter144 · Compatibilidad con secciones nuevas (items/platillos) y legacy (nombre/descripcion).
              const items = sec.items || sec.platillos || [];
              const tieneItems = Array.isArray(items) && items.length > 0;
              const fallbackTexto = sec.descripcion || sec.nombre || "";
              return (
                <div key={sec._rid || `sec-${i}`} className="border rounded-lg p-3" style={{ borderColor: "var(--brand-border)", background: "var(--brand-surface-muted)" }} data-testid={`cocina-menu-sec-${i}`}>
                  <p className="text-sm font-medium mb-2" style={{ color: "var(--brand-text)" }}>{sec.tipo || sec.titulo || sec.nombre || `Sección ${i + 1}`}</p>
                  {tieneItems ? (
                    <ul className="space-y-1 text-xs" style={{ color: "var(--brand-text)" }}>
                      {items.map((it, j) => (
                        <li key={it._rid || `it-${j}`} className="flex justify-between gap-2">
                          <span className="truncate">{it.nombre || it.titulo || (typeof it === "string" ? it : "—")}</span>
                          {it.cantidad ? <span className="text-[10px] shrink-0" style={{ color: "var(--brand-text-muted)" }}>×{it.cantidad}</span> : null}
                        </li>
                      ))}
                    </ul>
                  ) : fallbackTexto ? (
                    <p className="text-xs whitespace-pre-wrap" style={{ color: "var(--brand-text)" }}>{fallbackTexto}</p>
                  ) : (
                    <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sección sin platillos.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Mismo menú de Comercial · Servicios y menú. Los cambios se hacen ahí.</p>
      </Bloque>

      {/* PRODUCCIÓN / RECETAS · reutiliza componente existente */}
      <ProduccionTab eventoId={eventoId} />

      {/* COMPRAS DEL EVENTO */}
      <Bloque
        titulo={`Compras del evento${comprasEvento.length ? ` (${comprasEvento.length})` : ""}`}
        testid="cocina-compras"
      >
        {loadingCompras ? (
          <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Cargando compras…</p>
        ) : comprasEvento.length === 0 ? (
          <EmptyBlock icon={Package} msg="Sin compras registradas para este evento. Se gestionan desde Finanzas o desde Producción → Generar compras." />
        ) : (
          <>
            <div className="overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
              <table className="w-full text-xs">
                <thead style={{ background: "var(--brand-surface-muted)" }}>
                  <tr>
                    <th className="text-left p-2">Concepto</th>
                    <th className="text-left p-2">Proveedor</th>
                    <th className="text-left p-2">Estatus</th>
                    <th className="text-right p-2 w-24">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {comprasEvento.map((c, i) => (
                    <tr key={c.compra_id || `c-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`cocina-compra-${c.compra_id || i}`}>
                      <td className="p-2" style={{ color: "var(--brand-text)" }}>{c.concepto || c.descripcion || c.nombre || "—"}</td>
                      <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{c.proveedor_nombre || c.proveedor || "—"}</td>
                      <td className="p-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }}>{c.estatus || c.estado || "—"}</span>
                      </td>
                      <td className="p-2 text-right font-medium" style={{ color: "var(--brand-text)" }}>${(c.total || c.subtotal || c.importe || 0).toLocaleString("es-MX")}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "var(--brand-surface-muted)" }}>
                    <td colSpan="3" className="p-2 text-right text-[10px] uppercase tracking-wider" style={{ color: "var(--brand-text-muted)" }}>Total compras</td>
                    <td className="p-2 text-right font-display text-sm" style={{ color: "var(--brand-text)" }}>${totalCompras.toLocaleString("es-MX")}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>Mismas compras que aparecen en Finanzas, filtradas por este evento.</p>
          </>
        )}
      </Bloque>
    </div>
  );
};

const ProduccionTab = ({ eventoId }) => {
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState({});
  const [faltantes, setFaltantes] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [recetas, setRecetas] = useState([]);
  // Iter125 · P1 Fase 4c · Faltantes reales vs Inventario
  const [faltInv, setFaltInv] = useState([]);
  const [faltInvResumen, setFaltInvResumen] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [subvista, setSubvista] = useState("items");
  const [fEstado, setFEstado] = useState("");
  const [fMomento, setFMomento] = useState("");
  const [fResp, setFResp] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d1 }, { data: d2 }, { data: d3 }, r4] = await Promise.all([
        axios.get(`${API}/eventos/${eventoId}/produccion-items`),
        axios.get(`${API}/eventos/${eventoId}/produccion-items/ingredientes`),
        axios.get(`${API}/recetas`),
        axios.get(`${API}/eventos/${eventoId}/faltantes`).catch(() => ({ data: { faltantes: [], resumen: {} } })),
      ]);
      setItems(d1.items || []);
      setResumen(d1.resumen || {});
      setFaltantes(d1.requieren_configuracion || []);
      setIngredientes(d2.ingredientes || []);
      setRecetas(d3 || []);
      setFaltInv(r4.data?.faltantes || []);
      setFaltInvResumen(r4.data?.resumen || {});
    } catch {
      toast.error("No se pudo cargar Producción");
    } finally {
      setLoading(false);
    }
  }, [eventoId]);
  useEffect(() => { cargar(); }, [cargar]);

  const materializar = async () => {
    try {
      const { data } = await axios.post(`${API}/eventos/${eventoId}/produccion-items/materializar`);
      toast.success(`Materializado: ${data.creados} nuevas, ${data.actualizados} actualizadas, ${data.faltantes.length} sin config`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo materializar");
    }
  };

  // Iter125 · P1 Fase 4c
  const generarCompras = async () => {
    try {
      const { data } = await axios.post(`${API}/eventos/${eventoId}/faltantes/generar-compras`);
      toast.success(`Compras: ${data.creados} nuevas, ${data.actualizados} actualizadas, ${data.omitidos.length} omitidas`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron generar compras");
    }
  };
  const guardar = async (payload) => {
    try {
      if (editing) {
        await axios.put(`${API}/eventos/${eventoId}/produccion-items/${editing.produccion_item_id}`, payload);
        toast.success("Actualizado");
      } else {
        await axios.post(`${API}/eventos/${eventoId}/produccion-items`, payload);
        toast.success("Creado");
      }
      setShowForm(false);
      setEditing(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };
  const cambiarEstado = async (pid, accion) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/produccion-items/${pid}/estado`, { accion });
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };
  const excluir = async (pid) => {
    if (!window.confirm("¿Excluir este item automático de este evento? No reaparecerá al re-materializar.")) return;
    try {
      await axios.post(`${API}/eventos/${eventoId}/produccion-items/${pid}/excluir`);
      toast.success("Excluido");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo excluir");
    }
  };
  const eliminar = async (pid) => {
    if (!window.confirm("¿Eliminar este item de Producción? Se desvinculará su actividad de Orden Maestra.")) return;
    try {
      await axios.delete(`${API}/eventos/${eventoId}/produccion-items/${pid}`);
      toast.success("Eliminado");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const hoy = new Date().toISOString().slice(0, 10);
  const filtrados = useMemo(() => items.filter(i => {
    if (fEstado === "atrasado") {
      if (!(i.estado === "pendiente" && i.fecha_limite && i.fecha_limite < hoy)) return false;
    } else if (fEstado && i.estado !== fEstado) return false;
    if (fMomento && i.momento !== fMomento) return false;
    if (fResp && i.responsable !== fResp) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      if (!(`${i.preparacion} ${i.momento} ${i.responsable} ${i.observaciones||""}`.toLowerCase().includes(q))) return false;
    }
    return true;
  }), [items, fEstado, fMomento, fResp, busqueda, hoy]);

  const responsablesUnicos = useMemo(() => Array.from(new Set(items.map(i => i.responsable).filter(Boolean))).sort(), [items]);
  const momentosUnicos = useMemo(() => Array.from(new Set(items.map(i => i.momento).filter(Boolean))).sort(), [items]);

  return (
    <div className="space-y-4" data-testid="produccion-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold" style={{ color: "var(--brand-text)" }}>Producción del evento</h2>
          <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Calculado desde menú + recetas. Vinculado a Orden Maestra.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="eo-btn eo-btn-outline text-xs" onClick={materializar} data-testid="prod-materializar">Materializar desde menú</button>
          <button className="eo-btn eo-btn-outline text-xs" onClick={generarCompras} data-testid="prod-generar-compras" title="Genera compras 'pendiente' para los ingredientes faltantes contra inventario. Idempotente.">Generar compras de faltantes</button>
          <button className="eo-btn eo-btn-primary text-xs" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="prod-btn-crear">
            <Plus className="w-4 h-4" /> Agregar producción
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        <Contador testid="prod-cnt-pendientes" label="Pendientes" value={resumen.pendientes} color="#F59E0B" />
        <Contador testid="prod-cnt-en-prep" label="En preparación" value={resumen.en_preparacion} color="#2563EB" />
        <Contador testid="prod-cnt-terminadas" label="Terminadas" value={resumen.terminadas} color="#16A34A" />
        <Contador testid="prod-cnt-atrasadas" label="Atrasadas" value={resumen.atrasadas} color="#DC2626" />
        <Contador testid="prod-cnt-sinresp" label="Sin resp." value={resumen.sin_responsable} color="#7C3AED" />
        <Contador testid="prod-cnt-config" label="Sin config" value={faltantes.length} color="#EA580C" />
      </div>

      <div className="inline-flex rounded-xl p-1 flex-wrap" style={{ background: "var(--brand-surface-muted)", border: "1px solid var(--brand-border)" }}>
        <BtnVista active={subvista === "items"} onClick={() => setSubvista("items")} testid="prod-sub-items">Preparaciones ({items.length})</BtnVista>
        <BtnVista active={subvista === "ingredientes"} onClick={() => setSubvista("ingredientes")} testid="prod-sub-ingredientes">Ingredientes ({ingredientes.length})</BtnVista>
        <BtnVista active={subvista === "faltantes"} onClick={() => setSubvista("faltantes")} testid="prod-sub-faltantes">Faltantes vs Inventario ({faltInv.length})</BtnVista>
        <BtnVista active={subvista === "requieren_config"} onClick={() => setSubvista("requieren_config")} testid="prod-sub-config">Requieren configuración {faltantes.length > 0 && <span className="ml-1 text-[10px] px-1.5 rounded-full" style={{ background: "#EA580C", color: "#fff" }}>{faltantes.length}</span>}</BtnVista>
      </div>

      {showForm && (
        <ProdForm initial={editing} recetas={recetas} onCancel={() => { setShowForm(false); setEditing(null); }} onSave={guardar} />
      )}

      {subvista === "items" && (
        <>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar preparación…" className="eo-input w-full text-sm" data-testid="prod-search" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select value={fEstado} onChange={e => setFEstado(e.target.value)} className="eo-input text-xs" data-testid="prod-filter-estado">
              <option value="">Estado: Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="en_preparacion">En preparación</option>
              <option value="terminado">Terminado</option>
              <option value="atrasado">Atrasado</option>
              <option value="no_aplica">No aplica</option>
            </select>
            <select value={fMomento} onChange={e => setFMomento(e.target.value)} className="eo-input text-xs" data-testid="prod-filter-momento">
              <option value="">Momento: Todos</option>
              {momentosUnicos.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={fResp} onChange={e => setFResp(e.target.value)} className="eo-input text-xs" data-testid="prod-filter-resp">
              <option value="">Responsable: Todos</option>
              {responsablesUnicos.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm" style={{ color: "var(--brand-text-muted)" }}>Cargando…</div>
          ) : filtrados.length === 0 ? (
            <div className="p-8 rounded-2xl text-center" style={{ background: "var(--brand-surface-muted)", border: "1px dashed var(--brand-border)" }}>
              <ClipboardList className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--brand-text-muted)" }} />
              <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Sin preparaciones. Presiona <b>Agregar producción</b> o <b>Materializar desde menú</b>.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="prod-lista">
              {filtrados.map((it) => (
                <ProdCard
                  key={it.produccion_item_id}
                  item={it}
                  hoy={hoy}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  onEditar={() => { setEditing(it); setShowForm(true); }}
                  onEstado={(a) => cambiarEstado(it.produccion_item_id, a)}
                  onExcluir={() => excluir(it.produccion_item_id)}
                  onEliminar={() => eliminar(it.produccion_item_id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {subvista === "ingredientes" && (
        <div className="rounded-2xl overflow-hidden" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }} data-testid="prod-ingredientes-tabla">
          {ingredientes.length === 0 ? (
            <p className="p-6 text-sm text-center" style={{ color: "var(--brand-text-muted)" }}>Aún no hay ingredientes consolidados. Materializa producción para calcular.</p>
          ) : (
            <table className="w-full text-sm">
              <thead style={{ background: "var(--brand-surface-muted)" }}>
                <tr>
                  <th className="text-left p-2" style={{ color: "var(--brand-text-muted)" }}>Ingrediente</th>
                  <th className="text-right p-2" style={{ color: "var(--brand-text-muted)" }}>Total requerido</th>
                  <th className="text-left p-2" style={{ color: "var(--brand-text-muted)" }}>Preparaciones</th>
                </tr>
              </thead>
              <tbody>
                {ingredientes.map((ing, idx) => (
                  <tr key={`${ing.producto}-${idx}`} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`prod-ing-${idx}`}>
                    <td className="p-2 font-medium" style={{ color: "var(--brand-text)" }}>{ing.producto}</td>
                    <td className="p-2 text-right font-bold" style={{ color: "var(--brand-primary)" }}>{ing.cantidad_total} {ing.unidad}</td>
                    <td className="p-2 text-xs" style={{ color: "var(--brand-text-muted)" }}>
                      {(ing.origenes || []).map(o => `${o.preparacion} (${o.cantidad} ${ing.unidad})`).join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {subvista === "faltantes" && (
        <div className="rounded-2xl p-4" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }} data-testid="prod-faltantes-inv">
          <div className="grid grid-cols-4 gap-2 mb-3 text-xs">
            <div className="p-2 rounded" style={{ background: "#FEF3C7" }} data-testid="fi-cnt-faltante">
              <p style={{ color: "#92400E" }}>Con faltante</p>
              <p className="font-bold text-base" style={{ color: "#92400E" }}>{faltInvResumen.con_faltante || 0}</p>
            </div>
            <div className="p-2 rounded" style={{ background: "#DCFCE7" }} data-testid="fi-cnt-cubierto">
              <p style={{ color: "#166534" }}>Cubierto</p>
              <p className="font-bold text-base" style={{ color: "#166534" }}>{faltInvResumen.cubierto || 0}</p>
            </div>
            <div className="p-2 rounded" style={{ background: "#FEE2E2" }} data-testid="fi-cnt-no-registrado">
              <p style={{ color: "#991B1B" }}>No en Inventario</p>
              <p className="font-bold text-base" style={{ color: "#991B1B" }}>{faltInvResumen.no_registrado || 0}</p>
            </div>
            <div className="p-2 rounded" style={{ background: "#E0E7FF" }} data-testid="fi-cnt-conversion">
              <p style={{ color: "#3730A3" }}>Req. conversión</p>
              <p className="font-bold text-base" style={{ color: "#3730A3" }}>{faltInvResumen.requiere_conversion || 0}</p>
            </div>
          </div>
          {faltInv.length === 0 ? (
            <p className="text-sm text-center" style={{ color: "var(--brand-text-muted)" }}>Sin ingredientes materializados aún. Presiona <b>Materializar desde menú</b> primero.</p>
          ) : (
            <div className="overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
              <table className="w-full text-xs">
                <thead style={{ background: "var(--brand-surface-muted)" }}>
                  <tr>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-right p-2">Requerido</th>
                    <th className="text-right p-2">Disponible</th>
                    <th className="text-right p-2">Faltante</th>
                    <th className="text-left p-2">Unidad</th>
                    <th className="text-left p-2">Estado</th>
                    <th className="text-left p-2">Compra</th>
                  </tr>
                </thead>
                <tbody>
                  {faltInv.map((f, i) => {
                    const estMeta = {
                      ok: { bg: "#FEF3C7", color: "#92400E", label: "Falta comprar" },
                      cubierto: { bg: "#DCFCE7", color: "#166534", label: "Cubierto" },
                      no_registrado_inventario: { bg: "#FEE2E2", color: "#991B1B", label: "No en Inventario" },
                      requiere_conversion: { bg: "#E0E7FF", color: "#3730A3", label: "Req. conversión" },
                    }[f.estado] || { bg: "#F3F4F6", color: "#374151", label: f.estado };
                    return (
                      <tr key={`fi-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }} data-testid={`fi-row-${i}`}>
                        <td className="p-2" style={{ color: "var(--brand-text)" }}>
                          {f.producto}
                          {f.detalle && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{f.detalle}</p>}
                        </td>
                        <td className="p-2 text-right">{f.requerido}</td>
                        <td className="p-2 text-right">{f.disponible ?? "—"}</td>
                        <td className="p-2 text-right font-bold" style={{ color: (f.faltante || 0) > 0 ? "#92400E" : "#166534" }}>{f.faltante ?? "—"}</td>
                        <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{f.unidad || "—"}</td>
                        <td className="p-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: estMeta.bg, color: estMeta.color }}>{estMeta.label}</span></td>
                        <td className="p-2">
                          {f.compra ? (
                            <span className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{f.compra.estatus} · {f.compra.cantidad} {f.compra.unidad}</span>
                          ) : (
                            <span className="text-[10px] italic" style={{ color: "var(--brand-text-muted)" }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] mt-2" style={{ color: "var(--brand-text-muted)" }}>
            Fórmula: <b>faltante = max(0, requerido − disponible)</b>. Solo faltantes con estado &quot;Falta comprar&quot; generan compras al presionar <b>Generar compras de faltantes</b>. Idempotente por ref_key evento+producto+unidad. Compras manuales quedan intactas.
          </p>
        </div>
      )}

      {subvista === "requieren_config" && (
        <div className="rounded-2xl p-4" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }} data-testid="prod-requieren-config">
          {faltantes.length === 0 ? (
            <p className="text-sm text-center" style={{ color: "var(--brand-text-muted)" }}>Todo configurado. No hay elementos del menú sin receta o regla.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs mb-2" style={{ color: "var(--brand-text-muted)" }}>Elementos del menú que requieren configuración:</p>
              {faltantes.map((f, idx) => (
                <div key={idx} className="flex items-center justify-between gap-2 p-3 rounded-lg" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }} data-testid={`prod-falta-${idx}`}>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "#9A3412" }}>{f.nombre}</div>
                    <div className="text-xs" style={{ color: "#C2410C" }}>{f.detalle}</div>
                  </div>
                  <span className="text-[10px] px-2 py-1 rounded-full font-bold" style={{ background: "#FED7AA", color: "#9A3412" }}>{f.tipo === "sin_receta" ? "Sin receta" : f.tipo === "sin_regla" ? "Sin regla" : "Config faltante"}</span>
                </div>
              ))}
              <p className="text-[11px] italic mt-3" style={{ color: "var(--brand-text-muted)" }}>Para resolver: editar el menú del evento y asignar receta/regla, o crear manualmente la producción con &quot;Agregar producción&quot;.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProdCard = ({ item, hoy, openMenu, setOpenMenu, onEditar, onEstado, onExcluir, onEliminar }) => {
  const esAtrasado = item.estado === "pendiente" && item.fecha_limite && item.fecha_limite < hoy;
  const estKey = esAtrasado ? "atrasado" : item.estado;
  const est = PROD_ESTADO_META[estKey] || PROD_ESTADO_META.pendiente;
  const ori = ORIGEN_META[item.origen] || ORIGEN_META.manual;
  const menuOpen = openMenu === item.produccion_item_id;
  return (
    <div className="rounded-2xl p-3 md:p-4" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)", opacity: item.estado === "no_aplica" ? 0.6 : 1 }} data-testid={`prod-card-${item.produccion_item_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm md:text-base font-semibold" style={{ color: "var(--brand-text)", textDecoration: (item.estado === "terminado" || item.estado === "entregado") ? "line-through" : "none" }} data-testid={`prod-nombre-${item.produccion_item_id}`}>{item.preparacion}</h4>
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: ori.bg, color: ori.fg }} data-testid={`prod-origen-${item.produccion_item_id}`}>{ori.label}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: est.bg, color: est.fg }} data-testid={`prod-estado-${item.produccion_item_id}`}>{est.label}</span>
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold" style={{ color: "var(--brand-primary)" }} data-testid={`prod-cantidad-${item.produccion_item_id}`}>{item.cantidad_vigente}</span>
            <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{item.unidad}</span>
            {item.snapshot_receta && (
              <span className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>· receta: {item.snapshot_receta.nombre} (base {item.snapshot_receta.porciones_base})</span>
            )}
          </div>
          {item.base_calculo && (
            <p className="text-[10px] mt-0.5" style={{ color: "var(--brand-text-muted)" }} data-testid={`prod-base-calculo-${item.produccion_item_id}`}>
              Cálculo: {item.base_calculo}
            </p>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px]" style={{ color: "var(--brand-text-muted)" }}>
            {item.momento && <span className="inline-flex items-center px-2 py-0.5 rounded-full" style={{ background: "#EEF2FF", color: "#3730A3" }}>{item.momento}</span>}
            {item.responsable ? <span><UserCog className="w-3 h-3 inline mr-1" />{item.responsable}</span> : <span style={{ color: "#7C3AED" }}><UserCog className="w-3 h-3 inline mr-1" />Sin responsable</span>}
            {item.fecha_limite && <span><Calendar className="w-3 h-3 inline mr-1" />{item.fecha_limite}</span>}
            {item.hora_limite && <span><Clock className="w-3 h-3 inline mr-1" />{item.hora_limite}</span>}
          </div>
          {/* Iter125 · P1 Fase 4b · Ingredientes requeridos (snapshot congelado por evento) */}
          {item.snapshot_ingredientes && item.snapshot_ingredientes.length > 0 ? (
            <details className="mt-2" open data-testid={`prod-ingredientes-${item.produccion_item_id}`}>
              <summary className="text-[11px] cursor-pointer font-medium" style={{ color: "var(--brand-primary)" }}>
                Ingredientes requeridos · {item.snapshot_ingredientes.length}
              </summary>
              <div className="max-h-52 overflow-y-auto border rounded mt-1" style={{ borderColor: "var(--brand-border)" }}>
                <table className="w-full text-[11px]">
                  <thead style={{ background: "var(--brand-surface-muted)" }}>
                    <tr>
                      <th className="text-left p-1.5">Ingrediente</th>
                      <th className="text-right p-1.5">Cantidad</th>
                      <th className="text-left p-1.5">Unidad</th>
                      <th className="text-left p-1.5">Origen / Receta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.snapshot_ingredientes.map((ing, i) => (
                      <tr key={`${item.produccion_item_id}-ing-${i}`} className="border-t" style={{ borderColor: "var(--brand-border)" }}>
                        <td className="p-1.5" style={{ color: "var(--brand-text)" }}>{ing.producto}</td>
                        <td className="p-1.5 text-right font-medium">{ing.cantidad}{ing.merma_pct ? <span className="text-[10px] text-amber-700"> (+{ing.merma_pct}% merma)</span> : null}</td>
                        <td className="p-1.5" style={{ color: "var(--brand-text-muted)" }}>{ing.unidad}</td>
                        <td className="p-1.5" style={{ color: "var(--brand-text-muted)" }}>{item.snapshot_receta?.nombre || item.receta_id || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : (
            item.receta_id ? null : (
              <p className="text-[11px] mt-2 px-2 py-1 rounded" style={{ background: "var(--brand-warning-soft)", color: "var(--brand-warning)" }} data-testid={`prod-requiere-receta-${item.produccion_item_id}`}>
                Requiere configurar receta
              </p>
            )
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {item.estado === "pendiente" && (
            <button onClick={() => onEstado("iniciar")} className="text-[10px] px-2 py-1 rounded-lg" style={{ background: "#DBEAFE", color: "#1E40AF" }} data-testid={`prod-inline-iniciar-${item.produccion_item_id}`}>Iniciar</button>
          )}
          {item.estado === "en_preparacion" && (
            <button onClick={() => onEstado("terminar")} className="text-[10px] px-2 py-1 rounded-lg" style={{ background: "#DCFCE7", color: "#166534" }} data-testid={`prod-inline-terminar-${item.produccion_item_id}`}>Terminar</button>
          )}
          {(item.estado === "terminado" || item.estado === "empacado" || item.estado === "cargado" || item.estado === "entregado") && (
            <button onClick={() => onEstado("reabrir")} className="text-[10px] px-2 py-1 rounded-lg" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }} data-testid={`prod-inline-reabrir-${item.produccion_item_id}`}>Reabrir</button>
          )}
          <button onClick={onEditar} className="text-[10px] px-2 py-1 rounded-lg" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }} data-testid={`prod-inline-editar-${item.produccion_item_id}`}>
            <Edit className="w-3 h-3 inline" /> Editar
          </button>
          <div className="relative">
            <button onClick={() => setOpenMenu(menuOpen ? null : item.produccion_item_id)} className="p-1.5 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid={`prod-menu-btn-${item.produccion_item_id}`}><MoreHorizontal className="w-4 h-4" /></button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-xl shadow-lg py-1" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-border)" }}>
                  {item.estado !== "no_aplica" && <MenuAction icon={X} label="No aplica" onClick={() => { setOpenMenu(null); onEstado("no_aplica"); }} testid={`prod-act-noaplica-${item.produccion_item_id}`} />}
                  {item.estado === "no_aplica" && <MenuAction icon={Circle} label="Volver a pendiente" onClick={() => { setOpenMenu(null); onEstado("restaurar"); }} testid={`prod-act-restaurar-${item.produccion_item_id}`} />}
                  {item.estado === "terminado" && <MenuAction icon={CheckCircle2} label="Empacar" onClick={() => { setOpenMenu(null); onEstado("empacar"); }} testid={`prod-act-empacar-${item.produccion_item_id}`} />}
                  {item.estado === "empacado" && <MenuAction icon={CheckCircle2} label="Cargar" onClick={() => { setOpenMenu(null); onEstado("cargar"); }} testid={`prod-act-cargar-${item.produccion_item_id}`} />}
                  {item.estado === "cargado" && <MenuAction icon={CheckCircle2} label="Entregar" onClick={() => { setOpenMenu(null); onEstado("entregar"); }} testid={`prod-act-entregar-${item.produccion_item_id}`} />}
                  {item.origen === "manual" ? (
                    <MenuAction icon={Trash2} label="Eliminar" danger onClick={() => { setOpenMenu(null); onEliminar(); }} testid={`prod-act-eliminar-${item.produccion_item_id}`} />
                  ) : (
                    <MenuAction icon={Trash2} label="Excluir de este evento" danger onClick={() => { setOpenMenu(null); onExcluir(); }} testid={`prod-act-excluir-${item.produccion_item_id}`} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProdForm = ({ initial, recetas, onCancel, onSave }) => {
  const [preparacion, setPreparacion] = useState(initial?.preparacion || "");
  const [cantidad, setCantidad] = useState(initial?.cantidad_vigente || 0);
  const [unidad, setUnidad] = useState(initial?.unidad || "porciones");
  const [recetaId, setRecetaId] = useState(initial?.receta_id || "");
  const [responsable, setResponsable] = useState(initial?.responsable || "");
  const [fechaLimite, setFechaLimite] = useState(initial?.fecha_limite || "");
  const [horaLimite, setHoraLimite] = useState(initial?.hora_limite || "");
  const [momento, setMomento] = useState(initial?.momento || "");
  const [instrucciones, setInstrucciones] = useState(initial?.instrucciones || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!preparacion.trim()) { toast.error("La preparación es obligatoria"); return; }
    setSaving(true);
    await onSave({
      preparacion, cantidad_vigente: Number(cantidad), unidad,
      receta_id: recetaId || null,
      responsable, fecha_limite: fechaLimite, hora_limite: horaLimite,
      momento, instrucciones,
    });
    setSaving(false);
  };
  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ background: "var(--brand-surface)", border: "1px solid var(--brand-primary)" }} data-testid="prod-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: "var(--brand-text)" }}>{initial ? "Editar producción" : "Nueva producción"}</h3>
        <button onClick={onCancel} className="p-1"><X className="w-4 h-4" /></button>
      </div>
      <div>
        <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Preparación *</label>
        <input value={preparacion} onChange={e => setPreparacion(e.target.value)} className="eo-input w-full text-sm" placeholder="Ej: Preparar salsa habanera" autoFocus data-testid="prod-form-preparacion" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Cantidad</label>
          <input type="number" step="any" value={cantidad} onChange={e => setCantidad(e.target.value)} className="eo-input w-full text-sm" data-testid="prod-form-cantidad" />
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Unidad</label>
          <input value={unidad} onChange={e => setUnidad(e.target.value)} className="eo-input w-full text-sm" data-testid="prod-form-unidad" />
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Receta (opcional)</label>
          <select value={recetaId} onChange={e => setRecetaId(e.target.value)} className="eo-input w-full text-sm" data-testid="prod-form-receta">
            <option value="">— sin receta —</option>
            {(recetas || []).map(r => <option key={r.receta_id} value={r.receta_id}>{r.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Responsable</label>
          <input value={responsable} onChange={e => setResponsable(e.target.value)} className="eo-input w-full text-sm" data-testid="prod-form-resp" />
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Fecha límite</label>
          <input type="date" value={fechaLimite} onChange={e => setFechaLimite(e.target.value)} className="eo-input w-full text-sm" data-testid="prod-form-fecha" />
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Hora límite</label>
          <input type="time" value={horaLimite} onChange={e => setHoraLimite(e.target.value)} className="eo-input w-full text-sm" data-testid="prod-form-hora" />
        </div>
        <div className="md:col-span-3">
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Momento</label>
          <input value={momento} onChange={e => setMomento(e.target.value)} className="eo-input w-full text-sm" placeholder="Ej: Días antes / Montaje / Producción" data-testid="prod-form-momento" />
        </div>
        <div className="md:col-span-3">
          <label className="text-xs block mb-1" style={{ color: "var(--brand-text-muted)" }}>Instrucciones</label>
          <textarea value={instrucciones} onChange={e => setInstrucciones(e.target.value)} rows={2} className="eo-input w-full text-sm" data-testid="prod-form-instrucciones" />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="eo-btn eo-btn-outline text-sm" data-testid="prod-form-cancelar">Cancelar</button>
        <button onClick={submit} disabled={saving || !preparacion.trim()} className="eo-btn eo-btn-primary text-sm" data-testid="prod-form-guardar">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
};

// Iter125 · P1 Fase 5 · Modales del Equipo del Evento (relación, no maestro)

const EquipoEditPersonalModal = ({ abierto, draft, onClose, onSave }) => {
  const [d, setD] = useState({});
  useEffect(() => { setD(draft || {}); }, [draft]);
  if (!abierto) return null;
  // Iter132 · Sugerencia de tarifa maestra (solo lectura). Editar el costo del evento
  // NO modifica la ficha del Personal maestro; se guarda únicamente en la relación.
  const maestro = d._maestro || null;
  const ESQ_LABELS = {
    por_hora: "Por hora", por_evento: "Por evento", por_jornada: "Por jornada",
    por_servicio: "Por servicio", fijo: "Fijo", personalizado: "Personalizado",
    no_definido: "Sin definir",
  };
  const sugMonto = maestro
    ? ((maestro.pago_monto != null && maestro.pago_monto !== "")
        ? Number(maestro.pago_monto)
        : (maestro.pago_hora != null && maestro.pago_hora !== "" ? Number(maestro.pago_hora) : null))
    : null;
  const sugEsquema = maestro?.esquema_pago || (maestro?.pago_hora != null ? "por_hora" : null);
  const sugConcepto = maestro?.pago_concepto || "";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="modal-editar-personal-relacion">
      <div className="eo-card p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg" style={{ color: "var(--brand-text)" }}>Editar asignación</h3>
          <button onClick={onClose} className="p-1"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: "var(--brand-text-muted)" }}>Datos de <b>{d.nombre}</b> específicos a este evento. NO se modifica el registro maestro.</p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Rol en este evento</label>
            <input value={d.rol || ""} onChange={e => setD({ ...d, rol: e.target.value })} className="eo-input w-full text-sm" data-testid="edit-personal-rol" />
          </div>
          <div>
            <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Horario (ej. 16:00 - 23:00)</label>
            <input value={d.horario || ""} onChange={e => setD({ ...d, horario: e.target.value })} className="eo-input w-full text-sm" data-testid="edit-personal-horario" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Costo para este evento</label>
              {sugMonto != null && (
                <button
                  type="button"
                  onClick={() => setD({ ...d, costo: sugMonto })}
                  className="text-[10px] underline"
                  style={{ color: "var(--brand-primary)" }}
                  data-testid="edit-personal-usar-sugerencia"
                  title="Copia la tarifa maestra al costo del evento (no modifica la ficha maestra)"
                >Usar sugerida</button>
              )}
            </div>
            <input type="number" min="0" value={d.costo ?? ""} onChange={e => setD({ ...d, costo: e.target.value })} className="eo-input w-full text-sm" data-testid="edit-personal-costo" />
            {maestro ? (
              <p className="text-[10px] mt-1" style={{ color: "var(--brand-text-muted)" }} data-testid="edit-personal-sugerencia">
                {sugMonto != null || sugEsquema ? (
                  <>Tarifa maestra: {sugMonto != null ? <b>${Number(sugMonto).toLocaleString("es-MX")}</b> : <b>—</b>}
                    {sugEsquema ? <> · {ESQ_LABELS[sugEsquema] || sugEsquema}</> : null}
                    {sugEsquema === "personalizado" && sugConcepto ? <> · {sugConcepto}</> : null}
                  </>
                ) : "Sin tarifa maestra registrada"}. El costo del evento es independiente.
              </p>
            ) : (
              <p className="text-[10px] mt-1" style={{ color: "var(--brand-text-muted)" }}>El costo se guarda solo en este evento.</p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Estado</label>
            <select value={d.estatus || "asignado"} onChange={e => setD({ ...d, estatus: e.target.value })} className="eo-input w-full text-sm" data-testid="edit-personal-estatus">
              <option value="asignado">Asignado</option>
              <option value="confirmado">Confirmado</option>
              <option value="presente">Presente</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="eo-btn eo-btn-outline text-xs">Cancelar</button>
          <button onClick={() => onSave({ rol: d.rol, horario: d.horario, costo: Number(d.costo) || 0, estatus: d.estatus })} className="eo-btn eo-btn-primary text-xs" data-testid="edit-personal-guardar">Guardar</button>
        </div>
      </div>
    </div>
  );
};

const EquipoEditProveedorModal = ({ abierto, draft, onClose, onSave }) => {
  const [d, setD] = useState({});
  useEffect(() => { setD(draft || {}); }, [draft]);
  if (!abierto) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="modal-editar-proveedor-relacion">
      <div className="eo-card p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg" style={{ color: "var(--brand-text)" }}>Editar asignación</h3>
          <button onClick={onClose} className="p-1"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: "var(--brand-text-muted)" }}>Datos del proveedor <b>{d.nombre}</b> específicos a este evento. NO se modifica el registro maestro.</p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Servicio contratado</label>
            <input value={d.servicio || ""} onChange={e => setD({ ...d, servicio: e.target.value })} className="eo-input w-full text-sm" placeholder="Ej. DJ 4h, Fotografía, Rental" data-testid="edit-prov-servicio" />
          </div>
          <div>
            <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Llegada / Entrega</label>
            <input value={d.llegada || ""} onChange={e => setD({ ...d, llegada: e.target.value })} className="eo-input w-full text-sm" placeholder="Ej. 17:00 / entrega 15:30" data-testid="edit-prov-llegada" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Costo total</label>
              <input type="number" value={d.costo ?? ""} onChange={e => setD({ ...d, costo: e.target.value })} className="eo-input w-full text-sm" data-testid="edit-prov-costo" />
            </div>
            <div>
              <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Anticipo</label>
              <input type="number" value={d.anticipo ?? ""} onChange={e => setD({ ...d, anticipo: e.target.value })} className="eo-input w-full text-sm" data-testid="edit-prov-anticipo" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Estado</label>
            <select value={d.estatus || "asignado"} onChange={e => setD({ ...d, estatus: e.target.value })} className="eo-input w-full text-sm" data-testid="edit-prov-estatus">
              <option value="asignado">Asignado</option>
              <option value="confirmado">Confirmado</option>
              <option value="anticipo_pagado">Anticipo pagado</option>
              <option value="liquidado">Liquidado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="eo-btn eo-btn-outline text-xs">Cancelar</button>
          <button onClick={() => onSave({ servicio: d.servicio, llegada: d.llegada, costo: Number(d.costo) || 0, anticipo: Number(d.anticipo) || 0, estatus: d.estatus })} className="eo-btn eo-btn-primary text-xs" data-testid="edit-prov-guardar">Guardar</button>
        </div>
      </div>
    </div>
  );
};

const EquipoTareasModal = ({ abierto, data, onClose }) => {
  if (!abierto || !data) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="modal-tareas-personal">
      <div className="eo-card p-5 w-full max-w-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg" style={{ color: "var(--brand-text)" }}>Tareas asignadas · {data.nombre}</h3>
          <button onClick={onClose} className="p-1"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] mb-2" style={{ color: "var(--brand-text-muted)" }}>Actividades reales de Orden Maestra cuyo responsable coincide con este nombre. Editar en Orden Maestra.</p>
        <div className="max-h-72 overflow-y-auto overflow-x-auto border rounded" style={{ borderColor: "var(--brand-border)" }}>
          <table className="w-full text-xs">
            <thead style={{ background: "var(--brand-surface-muted)" }}>
              <tr>
                <th className="text-left p-2">Actividad</th>
                <th className="text-left p-2">Área</th>
                <th className="text-left p-2">Hora</th>
                <th className="text-left p-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map(it => (
                <tr key={it.item_id} className="border-t" style={{ borderColor: "var(--brand-border)" }}>
                  <td className="p-2" style={{ color: "var(--brand-text)" }}>{it.actividad}</td>
                  <td className="p-2" style={{ color: "var(--brand-text-muted)" }}>{it.area || "—"}</td>
                  <td className="p-2">{it.hora || "—"}</td>
                  <td className="p-2">{it.estado || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpedienteEvento;

// ============================================================================
// Iter157 · HojaGeneralTrabajo
// Convierte la pestaña "Resumen" en una Hoja General imprimible.
// Reutiliza data/helpers ya existentes en el expediente:
//   · datos: evento, cliente
//   · comercial: cotizacion, contrato, itemsContratados
//   · operación: serviciosAsignados, menuAsignado, personalAsignados,
//     proveedoresAsignados, itinerario, ordenMaestra, checklist, pagos
//   · CRUD: guardarCampo (mismo patrón que servicios_asignados / itinerario /
//     checklist). NO abre backend nuevo.
// Persistencia añadida SIN backend nuevo (arrays dentro del propio evento,
// como ya se hace con `servicios_asignados`, `itinerario`, `checklist`):
//   · evento.tareas_produccion (libreta simple; NO confundir con db.produccion,
//     que es workflow avanzado con recetas/aprobación).
//   · evento.lista_compras (manual, para TODO el evento; NO ligada a menú/
//     recetas/inventario; db.compras se mantiene como módulo avanzado aparte).
// Impresión: window.print() + <style> con @media print embebido.
// ============================================================================
const HojaGeneralTrabajo = ({
  evento, cliente, personalAsignados, proveedoresAsignados, serviciosAsignados,
  menuAsignado, pagos, totalPagado, saldo, itemsContratados, cotizacion, contrato,
  itinerario, ordenMaestra, checklist, toggleChecklist, guardarCampo, navigate,
  setTab, onRegistrarPago, onEditar,
}) => {
  const tareasProduccion = evento?.tareas_produccion || [];
  const listaCompras = evento?.lista_compras || [];

  // -- Producción (libreta simple)
  const [nvaTarea, setNvaTarea] = useState({ tarea: "", cantidad: "", responsable: "", hora: "", instrucciones: "" });
  const [addTareaOpen, setAddTareaOpen] = useState(false);
  const agregarTarea = async () => {
    if (!nvaTarea.tarea.trim()) { toast.error("Escribe una tarea"); return; }
    const item = { id: `tp_${Date.now()}`, ...nvaTarea, estado: "Pendiente" };
    await guardarCampo("tareas_produccion", [...tareasProduccion, item]);
    setNvaTarea({ tarea: "", cantidad: "", responsable: "", hora: "", instrucciones: "" });
    setAddTareaOpen(false);
  };
  const editarTarea = async (id, patch) => {
    await guardarCampo("tareas_produccion", tareasProduccion.map(t => t.id === id ? { ...t, ...patch } : t));
  };
  const eliminarTarea = async (id) => {
    if (!window.confirm("¿Eliminar esta tarea?")) return;
    await guardarCampo("tareas_produccion", tareasProduccion.filter(t => t.id !== id));
  };

  // -- Lista de compras (manual)
  const CAT_COMPRAS = ["Decoración", "Snacks", "Bebidas", "Cocina", "Montaje", "Otros"];
  const [nvaCompra, setNvaCompra] = useState({ producto: "", categoria: "Otros", cantidad: "", unidad: "", proveedor: "", notas: "" });
  const [addCompraOpen, setAddCompraOpen] = useState(false);
  const agregarCompra = async () => {
    if (!nvaCompra.producto.trim()) { toast.error("Escribe el producto"); return; }
    const item = { id: `lc_${Date.now()}`, ...nvaCompra, estado: "Pendiente" };
    await guardarCampo("lista_compras", [...listaCompras, item]);
    setNvaCompra({ producto: "", categoria: "Otros", cantidad: "", unidad: "", proveedor: "", notas: "" });
    setAddCompraOpen(false);
  };
  const editarCompra = async (id, patch) => {
    await guardarCampo("lista_compras", listaCompras.map(c => c.id === id ? { ...c, ...patch } : c));
  };
  const eliminarCompra = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await guardarCampo("lista_compras", listaCompras.filter(c => c.id !== id));
  };

  // -- Cronograma combinado (mismo helper que ItinerarioEditor.line 913)
  const filasCronograma = useMemo(() => {
    const filasOM = (ordenMaestra || [])
      .filter(o => o.hora)
      .map(o => ({ hora: o.hora, actividad: o.actividad, responsable: o.responsable || "" }));
    const filasIti = (itinerario || [])
      .filter(i => i.hora || i.actividad)
      .map(i => ({ hora: i.hora, actividad: i.actividad, responsable: i.responsable || "" }));
    return [...filasOM, ...filasIti].sort((a, b) => (a.hora || "").localeCompare(b.hora || ""));
  }, [itinerario, ordenMaestra]);

  const invitados = (evento?.adultos || 0) + (evento?.ninos || 0) + (evento?.jovenes || 0) + (evento?.bebes || 0);
  const totalContratado = evento?.total || 0;
  const horario = (evento?.hora_inicio && evento?.hora_fin) ? `${evento.hora_inicio} - ${evento.hora_fin}` : (evento?.horario || "");
  const pendientes = checklist || [];

  const ESTADOS_TAREA = ["Pendiente", "En preparación", "Listo"];
  const ESTADOS_COMPRA = ["Pendiente", "Comprado"];

  const colorEstadoTarea = (e) => e === "Listo" ? "var(--brand-success)" : e === "En preparación" ? "var(--brand-warning)" : "var(--brand-text-muted)";
  const colorEstadoCompra = (e) => e === "Comprado" ? "var(--brand-success)" : "var(--brand-warning)";

  return (
    <div className="hoja-general" data-testid="hoja-general-trabajo">
      <style>{`
        @media print {
          @page { margin: 12mm; }
          body * { visibility: hidden; }
          .hoja-general, .hoja-general * { visibility: visible; }
          .hoja-general { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .no-print { display: none !important; }
          .hoja-general .hg-block { break-inside: avoid; page-break-inside: avoid; border: 1px solid #999 !important; box-shadow: none !important; background: #fff !important; margin-bottom: 8px !important; }
          .hoja-general .hg-title { background: #222 !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 4px 8px !important; font-size: 11px !important; }
          .hoja-general table { border-collapse: collapse; width: 100%; }
          .hoja-general table th, .hoja-general table td { border-bottom: 1px solid #ccc; padding: 3px 4px; font-size: 10px; }
          .hoja-general .hg-grid { display: grid !important; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
          .hoja-general input, .hoja-general select, .hoja-general textarea { border: none !important; background: transparent !important; padding: 0 !important; }
        }
        .hoja-general .hg-block { border: 1px solid var(--brand-border); border-radius: 8px; background: var(--brand-surface); padding: 12px; margin-bottom: 12px; }
        .hoja-general .hg-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--brand-text-muted); margin-bottom: 8px; font-weight: 600; display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .hoja-general .hg-input { width: 100%; padding: 4px 6px; font-size: 12px; border: 1px solid var(--brand-border); border-radius: 4px; background: var(--brand-surface); }
        .hoja-general .hg-tbl { width: 100%; font-size: 12px; }
        .hoja-general .hg-tbl th { text-align: left; padding: 4px; background: var(--brand-surface-muted); color: var(--brand-text-muted); font-weight: 500; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
        .hoja-general .hg-tbl td { padding: 4px; border-top: 1px solid var(--brand-border); vertical-align: top; }
        @media (min-width: 1024px) { .hoja-general .hg-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; } }
        @media (max-width: 1023px) { .hoja-general .hg-grid { display: block; } }
      `}</style>

      {/* Encabezado + botón imprimir */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2 no-print">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Hoja General de Trabajo</p>
          <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>{evento?.nombre || "Evento"}</p>
        </div>
        <button
          onClick={() => window.print()}
          className="eo-btn eo-btn-primary"
          data-testid="btn-imprimir-hoja"
        >
          <FileText className="w-4 h-4" /> Imprimir / Guardar PDF
        </button>
      </div>

      <div className="hg-grid">
        {/* 1 · Datos del evento */}
        <div className="hg-block" data-testid="hg-datos">
          <div className="hg-title"><span>1 · Datos del evento</span>
            <button onClick={() => onEditar?.("datos")} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>✎ Editar</button>
          </div>
          <div className="space-y-1 text-xs">
            <p><span style={{ color: "var(--brand-text-muted)" }}>Cliente:</span> {cliente?.nombre || "—"}</p>
            {cliente?.telefono && <p><span style={{ color: "var(--brand-text-muted)" }}>Teléfono:</span> {cliente.telefono}</p>}
            {cliente?.email && <p><span style={{ color: "var(--brand-text-muted)" }}>Correo:</span> {cliente.email}</p>}
            <p><span style={{ color: "var(--brand-text-muted)" }}>Tipo:</span> {evento?.tipo || evento?.tipo_evento || "—"}</p>
            <p><span style={{ color: "var(--brand-text-muted)" }}>Fecha:</span> {evento?.fecha || "—"}</p>
            {horario && <p><span style={{ color: "var(--brand-text-muted)" }}>Horario:</span> {horario}</p>}
            <p><span style={{ color: "var(--brand-text-muted)" }}>Lugar:</span> {evento?.ubicacion || evento?.direccion || "—"}</p>
            {invitados > 0 && <p><span style={{ color: "var(--brand-text-muted)" }}>Invitados:</span> {invitados} {(evento?.adultos || evento?.ninos) ? `(${evento?.adultos || 0}A · ${evento?.ninos || 0}N)` : ""}</p>}
          </div>
        </div>

        {/* 2 · Cotización / Lo contratado */}
        <div className="hg-block" data-testid="hg-contratado">
          <div className="hg-title">
            <span>2 · Cotización / Lo contratado</span>
            {cotizacion && <button onClick={() => navigate(`/panel/cotizar/${cotizacion.cotizacion_id}`)} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>Ver cotización</button>}
          </div>
          {(itemsContratados || []).length > 0 ? (
            <>
              <table className="hg-tbl">
                <thead>
                  <tr><th>Concepto</th><th className="text-right">Cant.</th><th className="text-right">P.Unit</th><th className="text-right">Importe</th></tr>
                </thead>
                <tbody>
                  {itemsContratados.map((it, i) => (
                    <tr key={it.item_id || `hg-c-${i}`}>
                      <td>
                        <p style={{ color: "var(--brand-text)" }}>{it.nombre}</p>
                        {it.descripcion && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{it.descripcion}</p>}
                      </td>
                      <td className="text-right">{it.cantidad || 1}</td>
                      <td className="text-right">${(it.precio_unitario || 0).toLocaleString("es-MX")}</td>
                      <td className="text-right font-medium">${(it.importe || 0).toLocaleString("es-MX")}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan="3" className="text-right text-[10px] uppercase" style={{ color: "var(--brand-text-muted)" }}>Total contratado</td><td className="text-right font-display" style={{ color: "var(--brand-text)" }}>${totalContratado.toLocaleString("es-MX")}</td></tr>
                </tfoot>
              </table>
            </>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin conceptos contratados en la cotización.</p>
          )}
        </div>

        {/* 3 · Servicios y extras */}
        <div className="hg-block" data-testid="hg-servicios">
          <div className="hg-title">
            <span>3 · Servicios y extras</span>
            <button onClick={() => setTab("servicios_menu")} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>+ Agregar</button>
          </div>
          {serviciosAsignados.length > 0 ? (
            <ul className="text-xs space-y-1">
              {serviciosAsignados.map((s, i) => (
                <li key={s.servicio_id || `hg-s-${i}`} className="flex justify-between gap-2">
                  <span className="truncate" style={{ color: "var(--brand-text)" }}>{s.nombre}{s.cantidad > 1 ? ` × ${s.cantidad}` : ""}</span>
                  <span className="shrink-0" style={{ color: "var(--brand-text-muted)" }}>${((s.precio || 0) * (s.cantidad || 1)).toLocaleString("es-MX")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin extras del evento.</p>
          )}
        </div>

        {/* 4 · Menú */}
        <div className="hg-block" data-testid="hg-menu">
          <div className="hg-title">
            <span>4 · Menú</span>
            <button onClick={() => setTab("servicios_menu")} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>✎ Editar menú</button>
          </div>
          <div className="space-y-1 text-xs">
            {menuAsignado?.adultos && <p><span style={{ color: "var(--brand-text-muted)" }}>Adultos:</span> {menuAsignado.adultos}</p>}
            {menuAsignado?.infantil && <p><span style={{ color: "var(--brand-text-muted)" }}>Infantil:</span> {menuAsignado.infantil}</p>}
            {menuAsignado?.bebidas && <p><span style={{ color: "var(--brand-text-muted)" }}>Bebidas:</span> {menuAsignado.bebidas}</p>}
            {menuAsignado?.barra && <p><span style={{ color: "var(--brand-text-muted)" }}>Barra:</span> {menuAsignado.barra}</p>}
            {Array.isArray(menuAsignado?.secciones) && menuAsignado.secciones.map((s, i) => (
              <p key={s._rid || `hg-m-${i}`}><span style={{ color: "var(--brand-text-muted)" }}>{s.tipo || s.nombre}:</span> {s.descripcion || s.nombre}</p>
            ))}
            {!menuAsignado?.adultos && !menuAsignado?.infantil && !(menuAsignado?.secciones || []).length && (
              <p className="italic" style={{ color: "var(--brand-text-muted)" }}>Menú aún no definido.</p>
            )}
          </div>
        </div>

        {/* 5 · Proveedores */}
        <div className="hg-block" data-testid="hg-proveedores">
          <div className="hg-title">
            <span>5 · Proveedores ({proveedoresAsignados.length})</span>
            <button onClick={() => setTab("proveedores")} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>+ Agregar</button>
          </div>
          {proveedoresAsignados.length > 0 ? (
            <table className="hg-tbl">
              <thead><tr><th>Proveedor</th><th>Servicio</th><th>Llegada</th><th>Estado</th></tr></thead>
              <tbody>
                {proveedoresAsignados.map((p, i) => (
                  <tr key={p.proveedor_id || `hg-p-${i}`}>
                    <td>{p.nombre || "—"}</td>
                    <td>{p.servicio || p.categoria || "—"}</td>
                    <td>{p.hora_llegada || p.hora || "—"}</td>
                    <td>{p.estado_pago || p.estado || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin proveedores asignados.</p>
          )}
        </div>

        {/* 6 · Personal */}
        <div className="hg-block" data-testid="hg-personal">
          <div className="hg-title">
            <span>6 · Personal ({personalAsignados.length})</span>
            <button onClick={() => setTab("personal")} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>+ Agregar</button>
          </div>
          {personalAsignados.length > 0 ? (
            <table className="hg-tbl">
              <thead><tr><th>Nombre</th><th>Puesto</th><th>Horario</th></tr></thead>
              <tbody>
                {personalAsignados.map((p, i) => (
                  <tr key={p.personal_id || `hg-per-${i}`}>
                    <td>{p.nombre || "—"}</td>
                    <td>{p.rol || p.puesto || "—"}</td>
                    <td>{p.hora_llegada || p.horario || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin personal asignado.</p>
          )}
        </div>

        {/* 7 · Producción (tareas) */}
        <div className="hg-block" data-testid="hg-produccion">
          <div className="hg-title">
            <span>7 · Producción · tareas ({tareasProduccion.length})</span>
            <button onClick={() => setAddTareaOpen(v => !v)} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }} data-testid="btn-agregar-tarea">+ Crear tarea</button>
          </div>
          {addTareaOpen && (
            <div className="no-print mb-2 p-2 rounded" style={{ background: "var(--brand-surface-muted)" }}>
              <input className="hg-input mb-1" placeholder="Tarea" value={nvaTarea.tarea} onChange={e => setNvaTarea({ ...nvaTarea, tarea: e.target.value })} data-testid="input-nva-tarea" />
              <div className="grid grid-cols-2 gap-1 mb-1">
                <input className="hg-input" placeholder="Cantidad" value={nvaTarea.cantidad} onChange={e => setNvaTarea({ ...nvaTarea, cantidad: e.target.value })} />
                <input className="hg-input" placeholder="Responsable" value={nvaTarea.responsable} onChange={e => setNvaTarea({ ...nvaTarea, responsable: e.target.value })} />
              </div>
              <input className="hg-input mb-1" placeholder="Hora" value={nvaTarea.hora} onChange={e => setNvaTarea({ ...nvaTarea, hora: e.target.value })} />
              <textarea className="hg-input mb-1" rows={2} placeholder="Instrucciones" value={nvaTarea.instrucciones} onChange={e => setNvaTarea({ ...nvaTarea, instrucciones: e.target.value })} />
              <div className="flex gap-1 justify-end">
                <button onClick={() => setAddTareaOpen(false)} className="eo-btn eo-btn-outline text-xs">Cancelar</button>
                <button onClick={agregarTarea} className="eo-btn eo-btn-primary text-xs" data-testid="btn-guardar-tarea">Guardar</button>
              </div>
            </div>
          )}
          {tareasProduccion.length > 0 ? (
            <ul className="space-y-1 text-xs">
              {tareasProduccion.map(t => (
                <li key={t.id} className="flex items-start gap-2 py-1 border-b" style={{ borderColor: "var(--brand-border)" }} data-testid={`tarea-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <p style={{ color: "var(--brand-text)" }}>{t.tarea} {t.cantidad ? <span style={{ color: "var(--brand-text-muted)" }}>· {t.cantidad}</span> : null}</p>
                    <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>
                      {t.responsable ? `${t.responsable}` : ""}{t.hora ? ` · ${t.hora}` : ""}
                    </p>
                    {t.instrucciones && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{t.instrucciones}</p>}
                  </div>
                  <select value={t.estado || "Pendiente"} onChange={e => editarTarea(t.id, { estado: e.target.value })} className="hg-input text-[10px]" style={{ width: "auto", color: colorEstadoTarea(t.estado) }} data-testid={`select-estado-tarea-${t.id}`}>
                    {ESTADOS_TAREA.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => eliminarTarea(t.id)} className="text-xs no-print" style={{ color: "var(--brand-danger)" }} data-testid={`btn-eliminar-tarea-${t.id}`}>×</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin tareas de producción.</p>
          )}
        </div>

        {/* 8 · Lista de compras */}
        <div className="hg-block" data-testid="hg-compras">
          <div className="hg-title">
            <span>8 · Lista de compras ({listaCompras.length})</span>
            <button onClick={() => setAddCompraOpen(v => !v)} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }} data-testid="btn-agregar-compra">+ Agregar compra</button>
          </div>
          {addCompraOpen && (
            <div className="no-print mb-2 p-2 rounded" style={{ background: "var(--brand-surface-muted)" }}>
              <input className="hg-input mb-1" placeholder="Producto (ej. globos, hielo, refrescos)" value={nvaCompra.producto} onChange={e => setNvaCompra({ ...nvaCompra, producto: e.target.value })} data-testid="input-nva-compra" />
              <div className="grid grid-cols-2 gap-1 mb-1">
                <select className="hg-input" value={nvaCompra.categoria} onChange={e => setNvaCompra({ ...nvaCompra, categoria: e.target.value })}>
                  {CAT_COMPRAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input className="hg-input" placeholder="Proveedor (opc.)" value={nvaCompra.proveedor} onChange={e => setNvaCompra({ ...nvaCompra, proveedor: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-1 mb-1">
                <input className="hg-input" placeholder="Cantidad" value={nvaCompra.cantidad} onChange={e => setNvaCompra({ ...nvaCompra, cantidad: e.target.value })} />
                <input className="hg-input" placeholder="Unidad (pz, kg, l...)" value={nvaCompra.unidad} onChange={e => setNvaCompra({ ...nvaCompra, unidad: e.target.value })} />
              </div>
              <textarea className="hg-input mb-1" rows={2} placeholder="Notas" value={nvaCompra.notas} onChange={e => setNvaCompra({ ...nvaCompra, notas: e.target.value })} />
              <div className="flex gap-1 justify-end">
                <button onClick={() => setAddCompraOpen(false)} className="eo-btn eo-btn-outline text-xs">Cancelar</button>
                <button onClick={agregarCompra} className="eo-btn eo-btn-primary text-xs" data-testid="btn-guardar-compra">Guardar</button>
              </div>
            </div>
          )}
          {listaCompras.length > 0 ? (
            <table className="hg-tbl">
              <thead><tr><th>Producto</th><th>Cat.</th><th>Cant.</th><th>Estado</th><th className="no-print"></th></tr></thead>
              <tbody>
                {listaCompras.map(c => (
                  <tr key={c.id} data-testid={`compra-${c.id}`}>
                    <td>
                      <p>{c.producto}</p>
                      {(c.proveedor || c.notas) && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{c.proveedor}{c.proveedor && c.notas ? " · " : ""}{c.notas}</p>}
                    </td>
                    <td className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{c.categoria}</td>
                    <td>{c.cantidad}{c.unidad ? ` ${c.unidad}` : ""}</td>
                    <td>
                      <select value={c.estado || "Pendiente"} onChange={e => editarCompra(c.id, { estado: e.target.value })} className="hg-input text-[10px]" style={{ color: colorEstadoCompra(c.estado) }} data-testid={`select-estado-compra-${c.id}`}>
                        {ESTADOS_COMPRA.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="no-print"><button onClick={() => eliminarCompra(c.id)} style={{ color: "var(--brand-danger)" }} data-testid={`btn-eliminar-compra-${c.id}`}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin compras registradas.</p>
          )}
        </div>

        {/* 9 · Montaje / Decoración */}
        <div className="hg-block" data-testid="hg-montaje">
          <div className="hg-title">
            <span>9 · Montaje / Decoración</span>
            <button onClick={() => setTab("montaje")} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>✎ Editar</button>
          </div>
          <div className="space-y-1 text-xs">
            {evento?.tema && <p><span style={{ color: "var(--brand-text-muted)" }}>Tema:</span> {evento.tema}</p>}
            {evento?.colorimetria && <p><span style={{ color: "var(--brand-text-muted)" }}>Colorimetría:</span> {evento.colorimetria}</p>}
            {evento?.mesas && <p><span style={{ color: "var(--brand-text-muted)" }}>Mesas:</span> {evento.mesas}</p>}
            {evento?.vajilla && <p><span style={{ color: "var(--brand-text-muted)" }}>Vajilla/Mobiliario:</span> {evento.vajilla}</p>}
            {evento?.decoracion && <p><span style={{ color: "var(--brand-text-muted)" }}>Decoración:</span> {evento.decoracion}</p>}
            {!evento?.tema && !evento?.colorimetria && !evento?.decoracion && !evento?.mesas && !evento?.vajilla && (
              <p className="italic" style={{ color: "var(--brand-text-muted)" }}>Sin definir.</p>
            )}
          </div>
        </div>

        {/* 10 · Cronograma */}
        <div className="hg-block" data-testid="hg-cronograma">
          <div className="hg-title">
            <span>10 · Cronograma</span>
            <button onClick={() => setTab("orden_maestra")} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>Ver cronograma</button>
          </div>
          {filasCronograma.length > 0 ? (
            <table className="hg-tbl">
              <thead><tr><th>Hora</th><th>Actividad</th><th>Resp.</th></tr></thead>
              <tbody>
                {filasCronograma.map((f, i) => (
                  <tr key={`hg-cr-${i}`}>
                    <td className="whitespace-nowrap">{f.hora || "—"}</td>
                    <td>{f.actividad}</td>
                    <td className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{f.responsable}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin actividades programadas.</p>
          )}
        </div>

        {/* 11 · Pendientes */}
        <div className="hg-block" data-testid="hg-pendientes">
          <div className="hg-title">
            <span>11 · Pendientes ({pendientes.length})</span>
          </div>
          {pendientes.length > 0 ? (
            <ul className="text-xs space-y-1">
              {pendientes.map(p => (
                <li key={p.id} className="flex items-center gap-2" data-testid={`pendiente-${p.id}`}>
                  <input type="checkbox" checked={!!p.hecho} onChange={() => toggleChecklist?.(p.id)} className="no-print" />
                  <span style={{ color: "var(--brand-text)", textDecoration: p.hecho ? "line-through" : "none" }}>{p.label}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin pendientes.</p>
          )}
        </div>

        {/* 12 · Pagos */}
        <div className="hg-block" data-testid="hg-pagos">
          <div className="hg-title">
            <span>12 · Pagos</span>
            <button onClick={onRegistrarPago} className="text-[11px] no-print" style={{ color: "var(--brand-primary)" }}>+ Registrar pago</button>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span style={{ color: "var(--brand-text-muted)" }}>Total contratado:</span> <span style={{ color: "var(--brand-text)" }}>${totalContratado.toLocaleString("es-MX")}</span></div>
            <div className="flex justify-between"><span style={{ color: "var(--brand-success)" }}>Pagado:</span> <span style={{ color: "var(--brand-success)" }}>${totalPagado.toLocaleString("es-MX")}</span></div>
            <div className="flex justify-between font-medium border-t pt-1" style={{ borderColor: "var(--brand-border)" }}><span style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>Saldo:</span> <span style={{ color: saldo > 0 ? "var(--brand-warning)" : "var(--brand-success)" }}>${saldo.toLocaleString("es-MX")}</span></div>
            {pagos.length > 0 && (
              <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--brand-border)" }}>
                <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-text-muted)" }}>Historial ({pagos.filter(p => !p.cancelado).length})</p>
                <ul className="text-[11px] space-y-0.5 max-h-32 overflow-y-auto">
                  {pagos.filter(p => !p.cancelado).map(p => (
                    <li key={p.pago_id} className="flex justify-between gap-2">
                      <span style={{ color: "var(--brand-text)" }}>{p.fecha_pago || (p.created_at || "").substring(0, 10)} · {p.metodo || "—"}</span>
                      <span className="shrink-0" style={{ color: "var(--brand-text)" }}>${(p.monto || 0).toLocaleString("es-MX")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};




